// Dashboard.jsx - Optimized
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import RendersPanel from '../components/RendersPanel.jsx';
import AssetLibraryPanel from '../components/AssetLibraryPanel.jsx';
import DevStudio from '../components/DevStudio.jsx';
import { getRemainingCredits, refreshRemainingCounts, IMAGE_MARKET_RATE, VIDEO_MARKET_RATE_HIGH } from '../services/usageService.js';
import {
  prefetchProjectForEditor,
  buildFrameComposite,
  computeCompositeCacheKey,
  getCachedComposite,
  setCachedComposite,
} from '../services/compositeService.js';
import { SkeletonShimmer, SkeletonHorizontalCards } from '../components/SkeletonComponents.jsx';
import { FiSettings, FiCamera, FiPlus, FiHelpCircle, FiUser, FiX, FiImage, FiArrowLeft,
  FiEdit2, FiHome, FiChevronLeft, FiChevronRight, FiSearch, FiGrid, FiTrash2, FiUsers,
  FiCode, FiArrowRight } from 'react-icons/fi';

import { FaChevronDown } from 'react-icons/fa6';
import { IoSettingsOutline } from 'react-icons/io5';
import { TiCogOutline } from 'react-icons/ti';
import { FaCog } from 'react-icons/fa';
import { MdOutlinePhotoCamera } from 'react-icons/md';
import { LuPencilLine, LuShrink } from 'react-icons/lu';
import { MdFlashOn, MdFlashOff, MdFlashAuto } from 'react-icons/md';
import { BiFileBlank } from 'react-icons/bi';
import { GiPowerButton } from 'react-icons/gi';
import { TbCube3dSphere, TbDeviceIpadMinus } from 'react-icons/tb';
import { HiOutlineSwatch } from 'react-icons/hi2';
import { RiFilmAiLine, RiImageCircleAiLine, RiVideoOnAiLine } from 'react-icons/ri';
import { IoExpand } from 'react-icons/io5';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { GrDocumentConfig, GrGallery } from 'react-icons/gr';
import { LiaToolsSolid } from 'react-icons/lia';
import { saveAs } from 'file-saver';
import LogoWhite from '../assets/LOGO_WHITE.png';
import DefaultAvatarIcon from '../assets/avataricon.jpg';
import { gsap } from 'gsap';
import { useNavigate } from 'react-router-dom';
import { useNotification, FULLSCREEN_TRIGGERS, TOAST_IDS, ComingSoonOverlay } from '../components/NotificationSystem';
import axios from 'axios';
import {
  getAuth, onAuthStateChanged, updateProfile, updateEmail, updatePassword,
  EmailAuthProvider, reauthenticateWithCredential, signOut
} from 'firebase/auth';
import {
  doc, getDoc, setDoc, updateDoc, serverTimestamp, onSnapshot,
  collection, addDoc, query, where, orderBy, getDocs, writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { getStorage, ref, uploadString, getDownloadURL, deleteObject, listAll } from 'firebase/storage';

// ============================================
// STATIC ASSETS & API CONFIG
// ============================================
const TutorialPreview1 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/videos%2Ftutorialpreview.mp4?alt=media&token=2903024c-6f84-460a-8f5b-8ef8458afcc7";
const TutorialPreview2 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/videos%2Ftutorialpreview2.mp4?alt=media&token=db1314f4-018e-44f8-b533-1295a020782d";
const SaveFilePreview = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/project_gallery%2Fsave01.jpg?alt=media&token=73f54703-9673-48bb-8f81-e08b38331712";
const UNSPLASH_ACCESS_KEY = import.meta.env.VITE_UNSPLASH_ACCESS_KEY;
const UNSPLASH_API_URL = 'https://api.unsplash.com';

// ============================================
// CATALOG IMAGE ARRAYS
// ============================================
const catalogImages = [
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600210492493-0946911123ea?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600607687644-c7f34b5d8b7b?w=800&auto=format&fit=crop",
];

const feedCatalogImages = [
  "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1615529182904-14819c35db37?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1598928506311-c55ded91a20c?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600607686527-6fb886090705?w=800&auto=format&fit=crop",
];

// ============================================
// STYLE GUIDES DATA
// ============================================
const styleGuides = [
  {
    id: 'style-1', title: 'Modern Minimalist', description: 'Clean lines and open spaces',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&auto=format&fit=crop',
    category: 'Interior Design', author: 'Design Team', date: '2025-01-15',
  },
  {
    id: 'style-2', title: 'Mid Century Comfort', description: 'Warm textures and natural light',
    image: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&auto=format&fit=crop',
    category: 'Interior Design', author: 'Design Team', date: '2025-02-20',
  },
  {
    id: 'style-3', title: 'Industrial Chic', description: 'Raw materials meet refined design',
    image: 'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=1200&auto=format&fit=crop',
    category: 'Interior Design', author: 'Design Team', date: '2025-03-10',
  },
];

// ============================================
// POPUP STYLE CONSTANTS
// ============================================
const popupOverlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
  display: 'flex', alignItems: 'center', justifyContent: 'center'
};
const popupContentStyle = {
  backgroundColor: '#FAFAFA', borderRadius: '16px', padding: '25px',
  maxWidth: '400px', width: '90%'
};
const popupInputStyle = {
  width: '100%', padding: '12px 16px', marginBottom: '15px',
  backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px',
  fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', boxSizing: 'border-box'
};
const popupCancelStyle = {
  padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #ccc',
  borderRadius: '30px', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
};
const popupConfirmStyle = {
  padding: '10px 20px', backgroundColor: '#88B7FF', border: 'none',
  borderRadius: '30px', color: 'white', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
};

// ============================================
// NAVIGATION ITEMS - DESKTOP/TABLET
// ============================================
const navItems = [
  { id: 'new-project', label: 'New Project', icon: 'file' },
  { id: 'renders', label: 'Renders', icon: 'film' },
  { id: 'asset-library', label: 'Asset Library', icon: 'cube' },
  { id: 'creative-catalog', label: 'Creative Catalog', icon: 'catalog' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
  { id: 'developer', label: 'Developer', icon: 'developer', requiresAuth: true },
];

// ============================================
// LAZY IMAGE COMPONENT
// ============================================
const LazyImage = ({ src, alt, style, className, ...props }) => {
  const imgRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsInView(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} style={{ position: 'relative', overflow: 'hidden', width: '100%', height: '100%' }} className={className}>
      {!isLoaded && isInView && <SkeletonShimmer width="100%" height="100%" borderRadius="0" />}
      {isInView && !hasError && (
        <img src={src} alt={alt} onLoad={() => setIsLoaded(true)} onError={() => { setHasError(true); setIsLoaded(true); }}
          style={{ width: '100%', height: '100%', objectFit: style?.objectFit || 'cover', opacity: isLoaded ? 1 : 0, transition: 'opacity 0.3s ease', ...style }} {...props} />
      )}
      {hasError && (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e0e0', color: '#999', fontSize: '0.8rem', fontFamily: 'Avenir, sans-serif' }}>
          <FiImage size={24} style={{ opacity: 0.5 }} />
        </div>
      )}
    </div>
  );
};

// ============================================
// PROJECT SERVICE - FIREBASE CRUD
// ============================================
const projectService = {
  async createProject(userId, projectData) {
    let processedPhoto = projectData.photo || null;
    if (processedPhoto && processedPhoto.startsWith('data:') && processedPhoto.length > 500000) {
      processedPhoto = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxDim = 1200;
          let w = img.width, h = img.height;
          if (w > maxDim || h > maxDim) {
            const ratio = Math.min(maxDim / w, maxDim / h);
            w = Math.floor(w * ratio);
            h = Math.floor(h * ratio);
          }
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.src = processedPhoto;
      });
    }
    const docRef = await addDoc(collection(db, 'projects'), {
      userId, name: projectData.name || 'Untitled Project',
      details: projectData.details || '', photoURL: processedPhoto,
      originalPhotoURL: processedPhoto,
      mode: projectData.mode || 'new-project', status: 'active',
      schemaVersion: 4,
      layerCount: 0,
      frameCount: 0,
      noteCount: 0,
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    return { id: docRef.id };
  },

  async updateProject(projectId, updates) {
    await updateDoc(doc(db, 'projects', projectId), { ...updates, updatedAt: serverTimestamp() });
  },

  async autoSaveProject(userId, projectData) {
    const autoSavesRef = collection(db, 'project_autosaves');
    const q = query(autoSavesRef, where('userId', '==', userId), where('projectId', '==', projectData.projectId || 'new'));
    const snapshot = await getDocs(q);
    const saveData = {
      userId, projectId: projectData.projectId || 'new',
      name: projectData.name || '', details: projectData.details || '',
      photoURL: projectData.photo || null, canvasState: projectData.canvasState || null,
      savedAt: serverTimestamp(),
    };
    snapshot.empty ? await addDoc(autoSavesRef, saveData) : await updateDoc(snapshot.docs[0].ref, saveData);
  },

  async getAutoSave(userId, projectId) {
    const q = query(collection(db, 'project_autosaves'), where('userId', '==', userId), where('projectId', '==', projectId));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  },

  async getUserProjects(userId) {
    const q = query(collection(db, 'projects'), where('userId', '==', userId), where('status', '==', 'active'), orderBy('updatedAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
  },

  async getProject(projectId) {
    const docSnap = await getDoc(doc(db, 'projects', projectId));
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() };
  },

  async deleteProject(projectId) {
  await updateDoc(doc(db, 'projects', projectId), { status: 'deleted', deletedAt: serverTimestamp(), updatedAt: serverTimestamp() });
},
async restoreProject(projectId) {
  await updateDoc(doc(db, 'projects', projectId), { status: 'active', deletedAt: null, updatedAt: serverTimestamp() });
},

  subscribeToUserProjects(userId, callback) {
    const q = query(collection(db, 'projects'), where('userId', '==', userId), where('status', '==', 'active'), orderBy('updatedAt', 'desc'));
    return onSnapshot(q, (snapshot) => callback(snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))));
  },
};

// ============================================
// PROJECT STORAGE SWEEP (Schema v3)
// ----------------------------------------------------------------------------
// Firestore does not cascade-delete subcollections or Storage objects.
// Called from handleDeleteProject to clean up frames / layers / notes /
// drawings / moodboard subcollections plus every Storage file under the
// project prefix.
// ============================================
const sweepProjectStorage = async (uid, pid) => {
  if (!uid || !pid) return;
  const storage = getStorage();
  const projectPrefix = `users/${uid}/projects/${pid}`;

  const clearCollection = async (collectionName) => {
    try {
      const collRef = collection(db, 'projects', pid, collectionName);
      const snap = await getDocs(collRef);
      if (snap.empty) return;
      const docs = snap.docs;
      for (let i = 0; i < docs.length; i += 400) {
        const batch = writeBatch(db);
        docs.slice(i, i + 400).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    } catch (err) {
      console.error(`sweepProjectStorage clearCollection(${collectionName}) failed:`, err);
      // Re-throw so the caller can decide whether to surface a warning.
      // (Individual project deletion should still succeed even if the sweep
      //  partially fails — the project header is soft-deleted regardless.)
      throw err;
    }
  };

  await clearCollection('frames');
  await clearCollection('layers');
  await clearCollection('notes');
  await clearCollection('drawings');
  await clearCollection('moodboard');

  try {
    const rootRef = ref(storage, projectPrefix);
    const listRecursive = async (r) => {
      const listing = await listAll(r);
      for (const item of listing.items) {
        try { await deleteObject(item); } catch (e) { /* ignore missing */ }
      }
      for (const prefix of listing.prefixes) {
        await listRecursive(prefix);
      }
    };
    await listRecursive(rootRef);
  } catch (err) {
    console.error('sweepProjectStorage Storage sweep error:', err);
  }
};

// ============================================
// DASHBOARD COMPONENT
// ============================================
const Dashboard = () => {
  const navigate = useNavigate();
  const { showFullScreenModal, showToast } = useNotification();

  // ============================================
  // STATE - CORE
  // ============================================
  const [hasReloaded, setHasReloaded] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isDashboardAvailable, setIsDashboardAvailable] = useState(false);

  // ============================================
  // STATE - PROFILE & AVATAR
  // ============================================
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const [userName, setUserName] = useState('User');
  const [userAvatar, setUserAvatar] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempUserName, setTempUserName] = useState('');
  const [showAvatarDropdown, setShowAvatarDropdown] = useState(false);
  const [showProfileOverlay, setShowProfileOverlay] = useState(false);
  const [showDesktopProfileView, setShowDesktopProfileView] = useState(false);
  const [showDesktopEmailDropdown, setShowDesktopEmailDropdown] = useState(false);
  const [showMobileEmailDropdown, setShowMobileEmailDropdown] = useState(false);

  // ============================================
  // STATE - EMAIL & PASSWORD POPUPS
  // ============================================
  const [showEmailChangePopup, setShowEmailChangePopup] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [showPasswordChangePopup, setShowPasswordChangePopup] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showDeleteDisclaimer, setShowDeleteDisclaimer] = useState(false);
const [hasPhotoPermission, setHasPhotoPermission] = useState(false);
  const [hasShownLowCreditWarning, setHasShownLowCreditWarning] = useState(false);
  const [hasShownZeroCreditModal, setHasShownZeroCreditModal] = useState(false);

  // ============================================
  // STATE - CREATIVE CATALOG
  // ============================================
  const [unsplashPhotos, setUnsplashPhotos] = useState([]);
  const [unsplashLoading, setUnsplashLoading] = useState(false);
  const [unsplashPage, setUnsplashPage] = useState(1);
  const [catalogExpanded, setCatalogExpanded] = useState(false);
  const [catalogActive, setCatalogActive] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showPhotoDetails, setShowPhotoDetails] = useState(false);
  const [showStyleGuidesFull, setShowStyleGuidesFull] = useState(false);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [activeStyleGuide, setActiveStyleGuide] = useState(0);
  const [showInteriorDesignComingSoon, setShowInteriorDesignComingSoon] = useState(true);

  // ============================================
  // STATE - SETTINGS & PANELS
  // ============================================
  const [showDesktopSettingsView, setShowDesktopSettingsView] = useState(false);
  const [showMobileSettingsView, setShowMobileSettingsView] = useState(false);
  const [showDesktopDeveloperView, setShowDesktopDeveloperView] = useState(false);
  const [showDevToolsPopup, setShowDevToolsPopup] = useState(false);
  const [showMobileDeveloperView, setShowMobileDeveloperView] = useState(false);
  const [showDevStudio, setShowDevStudio] = useState(false);
  const [activeDeveloperTab, setActiveDeveloperTab] = useState('account');
  const [selectedDeveloperModule, setSelectedDeveloperModule] = useState(null);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [isMarketer, setIsMarketer] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserPlan, setEditUserPlan] = useState({ show: false, userId: null, currentPlan: '', newPlan: '', billingCycle: '' });
  const [searchUsersQuery, setSearchUsersQuery] = useState('');
  const [rendersActive, setRendersActive] = useState(false);
  const [userRenders, setUserRenders] = useState([]);
  const [assetLibraryActive, setAssetLibraryActive] = useState(false);
  const [userAssets, setUserAssets] = useState([]);
const [isAssetsLoading, setIsAssetsLoading] = useState(false);

// Add this function after your other fetch functions (around line 400-420 area)
const fetchUserAssets = useCallback(async () => {
  if (!authUser) return;
  setIsAssetsLoading(true);
  try {
    const assetsQuery = query(
      collection(db, 'assets'),
      where('userId', '==', authUser.uid),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(assetsQuery);
    const assets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setUserAssets(assets);
  } catch (error) {
    console.error('Error fetching assets:', error);
  } finally {
    setIsAssetsLoading(false);
  }
}, [authUser]);

// Check if user is Developer or Marketer
const checkDeveloperStatus = useCallback(() => {
  if (!userData) return;
  const isDev = userData.role === 'developer' || userData.isDeveloper === true || userData.accountType === 'developer';
  const isMkt = userData.role === 'marketer' || userData.isMarketer === true || userData.accountType === 'marketer';
  setIsDeveloper(isDev);
  setIsMarketer(isMkt);
}, [userData]);

// Add usage service import at top of file (after other imports):
// import { getRemainingCredits, refreshRemainingCounts } from '../services/usageService';

 

// Fetch all users (Developer only)
const fetchAllUsers = useCallback(async () => {
  if (!authUser || !isDeveloper) return;
  setUsersLoading(true);
  try {
    const usersRef = collection(db, 'users');
    // Try ordered query first – if a composite index is missing, fall back to unordered
    let snapshot;
    try {
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      snapshot = await getDocs(q);
    } catch (indexErr) {
      console.warn(
        'Ordering users by createdAt requires a Firestore composite index. ' +
        'Falling back to unsorted list. Create the index for production.',
        indexErr
      );
      snapshot = await getDocs(usersRef);
    }
    const users = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
    setUsersList(users);
  } catch (error) {
    console.error('Error fetching users:', error);
  } finally {
    setUsersLoading(false);
  }
}, [authUser, isDeveloper]);

// Update user subscription plan (Developer only)
const handleUpdateUserPlan = useCallback(async () => {
  if (!editUserPlan.userId || !editUserPlan.newPlan) return;
  try {
    const userRef = doc(db, 'users', editUserPlan.userId);
    const updateData = {
      subscriptionPlan: editUserPlan.newPlan,
      subscriptionStatus: 'active',
      updatedAt: serverTimestamp(),
    };
    if (editUserPlan.billingCycle) {
      updateData.billingCycle = editUserPlan.billingCycle;
      updateData.subscription = {
        planName: editUserPlan.newPlan,
        billingCycle: editUserPlan.billingCycle,
        status: 'active',
        updatedAt: new Date().toISOString(),
      };
    }
    await updateDoc(userRef, updateData);
    setEditUserPlan({ show: false, userId: null, currentPlan: '', newPlan: '', billingCycle: '' });
    await fetchAllUsers();
    alert(`User plan updated to ${editUserPlan.newPlan}`);
  } catch (error) {
    console.error('Error updating user plan:', error);
    alert('Failed to update user plan');
  }
}, [editUserPlan, fetchAllUsers]);

// Reset user usage (Developer only)
const handleResetUserUsage = useCallback(async (userId, userEmail) => {
  if (!window.confirm(`Reset usage for ${userEmail}? This will set imageEquivalents to 0.`)) return;
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'usage.imageEquivalents': 0,
      'usage.videosGenerated': 0,
      updatedAt: serverTimestamp(),
    });
    await fetchAllUsers();
    alert(`Usage reset for ${userEmail}`);
  } catch (error) {
    console.error('Error resetting usage:', error);
    alert('Failed to reset usage');
  }
}, [fetchAllUsers]);

// Sync user account with correct plan caps (Developer only)
const handleSyncUserAccount = useCallback(async (userId, userEmail) => {
  if (!window.confirm(`Sync account for ${userEmail}? This will reset caps and budget according to their current plan.`)) return;
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      alert('User not found');
      return;
    }
    const userData = userSnap.data();
    const planName = userData.subscription?.planName || userData.pendingPlan?.name || 'Starter';
    const billingCycle = userData.subscription?.billingCycle || userData.pendingPlan?.billingCycle || 'monthly';
    // Pull caps & budget from internal doc
    let genBudget = 8; // Starter funded $8
    let imageCap = 100;
    let videoCap = 3;
    if (planName === 'Creator') {
      genBudget = billingCycle === 'annual' ? 234 : 23.40;
      imageCap = 360;
      videoCap = 13;
    } else if (planName === 'Pro') {
      genBudget = billingCycle === 'annual' ? 594 : 59.40;
      imageCap = 914;
      videoCap = 33;
    }
    await updateDoc(userRef, {
      'usage.imageEquivalents': 0,
      'usage.videosGenerated': 0,
      'planCaps.imageCap': imageCap,
      'planCaps.videoCap': videoCap,
      'planCaps.genBudget': genBudget,
      subscription: {
        ...userData.subscription,
        planName: planName,
        billingCycle: billingCycle,
        status: 'active',
        updatedAt: new Date().toISOString(),
      },
      updatedAt: serverTimestamp(),
    });
    await fetchAllUsers();
    alert(`Account synced for ${userEmail}. Caps updated to ${planName} (${imageCap} images, ${videoCap} videos).`);
  } catch (error) {
    console.error('Error syncing account:', error);
    alert('Failed to sync account');
  }
}, [fetchAllUsers]);

// Grant developer role (Developer only)
const handleGrantDeveloperRole = useCallback(async (userId, userEmail) => {
  if (!window.confirm(`Grant DEVELOPER role to ${userEmail}? This gives admin privileges.`)) return;
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: 'developer',
      isDeveloper: true,
      accountType: 'developer',
      updatedAt: serverTimestamp(),
    });
    await fetchAllUsers();
    alert(`${userEmail} is now a Developer`);
  } catch (error) {
    console.error('Error granting developer role:', error);
    alert('Failed to grant developer role');
  }
}, [fetchAllUsers]);

// Grant marketer role (Developer only)
const handleGrantMarketerRole = useCallback(async (userId, userEmail) => {
  if (!window.confirm(`Grant MARKETER role to ${userEmail}?`)) return;
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: 'marketer',
      isMarketer: true,
      accountType: 'marketer',
      updatedAt: serverTimestamp(),
    });
    await fetchAllUsers();
    alert(`${userEmail} is now a Marketer`);
  } catch (error) {
    console.error('Error granting marketer role:', error);
    alert('Failed to grant marketer role');
  }
}, [fetchAllUsers]);

// Revoke special role (set to regular user)
const handleRevokeRole = useCallback(async (userId, userEmail) => {
  if (!window.confirm(`Revoke special role from ${userEmail}? They will become a regular user.`)) return;
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: 'user',
      isDeveloper: false,
      isMarketer: false,
      accountType: 'user',
      updatedAt: serverTimestamp(),
    });
    await fetchAllUsers();
    alert(`${userEmail} is now a regular user`);
  } catch (error) {
    console.error('Error revoking role:', error);
    alert('Failed to revoke role');
  }
}, [fetchAllUsers]);



// Add real-time subscription for assets
useEffect(() => {
  if (!authUser) return;
  
  const assetsQuery = query(
    collection(db, 'assets'),
    where('userId', '==', authUser.uid),
    orderBy('createdAt', 'desc')
  );
  
  const unsubscribe = onSnapshot(assetsQuery, (snapshot) => {
    const assets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setUserAssets(assets);
  }, (error) => {
    console.error('Error fetching assets:', error);
  });
  
  return () => unsubscribe();
}, [authUser]);

  // ============================================
  // STATE - USAGE TRACKING
  // ============================================
  const [imagesRemaining, setImagesRemaining] = useState(0);
  const [videosRemaining, setVideosRemaining] = useState(0);
  const [totalImageEquivalents, setTotalImageEquivalents] = useState(0);
  const [usedImageEquivalents, setUsedImageEquivalents] = useState(0);

  // Dollar‑value animation
  const [showDollarValue, setShowDollarValue] = useState(false);


  useEffect(() => {
    if (!authUser) return;
    const interval = setInterval(() => {
      setShowDollarValue(prev => !prev);
    }, 6000);
    return () => clearInterval(interval);
  }, [authUser]);

  // ============================================
  // STATE - PROJECT MANAGEMENT
  // ============================================
  const [userProjects, setUserProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [deleteProjectPopup, setDeleteProjectPopup] = useState(null);
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  const [projectSettingsPopup, setProjectSettingsPopup] = useState(null);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(null);
  const [showRenameProjectPopup, setShowRenameProjectPopup] = useState(false);
  const [projectToRename, setProjectToRename] = useState(null);
  const [newProjectName, setNewProjectName] = useState('');

  // ============================================
  // STATE - TAB SELECTION & SCROLL
  // ============================================
  const [selectedTab, setSelectedTab] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const galleryRef = useRef(null);
  const trackRef = useRef(null);
  const [activeGrid, setActiveGrid] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(0.7);

  // ============================================
  // STATE - PROMPT & PROJECT CREATION
  // ============================================
  const [promptValue, setPromptValue] = useState('');
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [projectName, setProjectName] = useState('');
  const [projectDetails, setProjectDetails] = useState('');
  const [projectPhoto, setProjectPhoto] = useState(null);
  const [projectType, setProjectType] = useState('Personal');
  const [loadingMode, setLoadingMode] = useState('');
  const [loadedProjectData, setLoadedProjectData] = useState(null);
  const [isLoadingActive, setIsLoadingActive] = useState(false);
  const [showPhotoStep, setShowPhotoStep] = useState(false);
  const [tempPhoto, setTempPhoto] = useState(null);
  const [loadingDots, setLoadingDots] = useState('');
  const [loadingFileName, setLoadingFileName] = useState('');
  const [projectCreationStep, setProjectCreationStep] = useState('name');
  const [loadStatusText, setLoadStatusText] = useState('');

  // ============================================
  // STATE - CAMERA
  // ============================================
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState('camera');
  const [photoSource, setPhotoSource] = useState('camera');
  const [flashMode, setFlashMode] = useState('off');
  const [flashSupported, setFlashSupported] = useState(true);
  const [showShutterEffect, setShowShutterEffect] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isZooming, setIsZooming] = useState(false);
  const [maxZoom, setMaxZoom] = useState(1);
  const [zoomStep, setZoomStep] = useState(0.1);
  const [cameraModeStep, setCameraModeStep] = useState('photo');

  // ============================================
  // STATE - DESKTOP/TABLET SPECIFIC
  // ============================================
  const [activeNavItem, setActiveNavItem] = useState('dashboard');
  const [catalogIndex, setCatalogIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [feedIndex, setFeedIndex] = useState(0);
  const [videoLoadingStates, setVideoLoadingStates] = useState({});

  // ============================================
  // REFS
  // ============================================
  const avatarDropdownRef = useRef(null);
  const avatarButtonRef = useRef(null);
  const fileInputRef = useRef(null);
  const profileFileInputRef = useRef(null);
  const gridContainerRef = useRef(null);
  const gridScrollRef = useRef({ x: 0, y: 0 });
  const lastGridChangeTime = useRef(0);
  const rafRef = useRef(null);
  const videoRefs = useRef([]);

  // ============================================
  // USAGE TRACKING - UPDATE REMAINING COUNTS
  // ============================================
  const updateRemainingCounts = useCallback(() => {
    if (!userData) return;
    const remaining = getRemainingCredits(userData);
    setImagesRemaining(remaining.iterations || 0);
    setVideosRemaining(remaining.videos || 0);
    setTotalImageEquivalents(remaining.iterationCap || 0);
    setUsedImageEquivalents(remaining.usedImageEquivalents || 0);
  }, [userData]);

  // ============================================
  // PROFILE HELPERS
  // ============================================
  const getDisplayName = useCallback(() => {
    if (!authUser) return 'Guest';
    if (userData?.displayName) return userData.displayName;
    if (userData?.firstName) return userData.firstName;
    return authUser.email?.split('@')[0] || 'User';
  }, [authUser, userData]);

  const getLetterAvatar = useCallback(() => {
    const name = getDisplayName();
    if (!name || name === 'Guest') return DefaultAvatarIcon;
    const nameParts = name.split(' ');
    let initials = userData?.displayName ? name.charAt(0).toUpperCase() : nameParts.length >= 2 ? (nameParts[0][0] + nameParts[1][0]).toUpperCase() : name.charAt(0).toUpperCase();
    const canvas = document.createElement('canvas');
    canvas.width = 200; canvas.height = 200;
    const ctx = canvas.getContext('2d');
    ctx.beginPath(); ctx.arc(100, 100, 100, 0, Math.PI * 2); ctx.clip();
    ctx.fillStyle = '#538ff0ff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'; ctx.lineWidth = 1;
    for (let x = 0; x <= canvas.width; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke(); }
    for (let y = 0; y <= canvas.height; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke(); }
    ctx.fillStyle = '#ffffff'; ctx.font = 'bold 40px avenir-lt-pro, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(initials, canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL();
  }, [getDisplayName, userData]);

  const getAvatarUrl = useCallback(() => {
    if (avatarPreview) return avatarPreview;
    if (userData?.photoURL) return userData.photoURL;
    if (authUser?.photoURL) return authUser.photoURL;
    if (authUser) return getLetterAvatar();
    return DefaultAvatarIcon;
  }, [avatarPreview, userData, authUser, getLetterAvatar]);

  const getCurrentPlanName = useCallback(() => userData?.subscription?.planName || userData?.pendingPlan?.name || 'Starter', [userData]);
  const getCurrentBillingCycle = useCallback(() => userData?.subscription?.billingCycle || userData?.pendingPlan?.billingCycle, [userData]);

  // ============================================
  // UNSLASH API - FETCH PHOTOS
  // ============================================
  const fetchUnsplashPhotos = useCallback(async (page = 1, query = '') => {
    setUnsplashLoading(true);
    try {
      const params = { client_id: UNSPLASH_ACCESS_KEY, per_page: 12, page };
      let endpoint = `${UNSPLASH_API_URL}/photos`;
      if (query.trim()) { endpoint = `${UNSPLASH_API_URL}/search/photos`; params.query = query; }
      else params.order_by = 'popular';
      const response = await axios.get(endpoint, { params });
      const photos = query ? response.data.results : response.data;
      setUnsplashPhotos(page === 1 ? photos : prev => [...prev, ...photos]);
    } catch (error) {
      console.error('Error fetching Unsplash photos:', error);
      if (page === 1) {
        setUnsplashPhotos(feedCatalogImages.map((url, index) => ({
          id: `fallback-${index}`, urls: { regular: url, small: url, full: url },
          user: { name: 'Unsplash Photographer', username: 'unsplash', links: { html: 'https://unsplash.com' } },
          alt_description: 'Interior design inspiration', description: 'Beautiful interior design inspiration',
          width: 800, height: 600, created_at: new Date().toISOString(), links: { download_location: '', html: 'https://unsplash.com' },
        })));
      }
    } finally { setUnsplashLoading(false); }
  }, []);

  const triggerUnsplashDownload = useCallback(async (photo) => {
    if (photo.links?.download_location) {
      try { await axios.get(photo.links.download_location, { params: { client_id: UNSPLASH_ACCESS_KEY } }); }
      catch (error) { console.error('Error triggering download:', error); }
    }
  }, []);

  // ============================================
  // CAMERA - GET STREAM
  // ============================================
  const getCameraStream = () => {
    return new Promise((resolve, reject) => {
      const constraints = {
        audio: false,
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 }, aspectRatio: { ideal: 16/9 } }
      };
      if (navigator.mediaDevices?.getUserMedia) {
        navigator.mediaDevices.getUserMedia(constraints)
          .then(stream => {
            const videoTrack = stream.getVideoTracks()[0];
            const capabilities = videoTrack.getCapabilities ? videoTrack.getCapabilities() : {};
            if (capabilities.zoom) { setMaxZoom(capabilities.zoom.max || 1); setZoomStep(capabilities.zoom.step || 0.1); }
            else setMaxZoom(1);
            resolve(stream);
          })
          .catch(() => {
            navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: 'environment' } } })
              .then(resolve).catch(reject);
          });
      } else reject(new Error('Camera not supported'));
    });
  };

  // ============================================
  // CAMERA - ZOOM TOGGLE (SMOOTH)
  // ============================================
  const toggleZoomSmooth = async () => {
    if (isZooming || !cameraStream || capturedPhoto) return;
    const targetZoomLevel = zoomLevel === 1 ? 0.5 : 1;
    setIsZooming(true);
    try {
      const videoTrack = cameraStream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities ? videoTrack.getCapabilities() : {};
      if (capabilities.zoom) {
        const steps = 10;
        for (let i = 1; i <= steps; i++) {
          const currentZoom = zoomLevel + (targetZoomLevel - zoomLevel) * (i / steps);
          await new Promise(resolve => setTimeout(resolve, 30));
          await videoTrack.applyConstraints({ advanced: [{ zoom: currentZoom }] });
        }
      } else if (videoRef.current) {
        await new Promise(resolve => {
          gsap.to(videoRef.current, { scale: targetZoomLevel === 0.5 ? 0.7 : 1, duration: 0.3, ease: "power2.inOut", onComplete: resolve });
        });
      }
      setZoomLevel(targetZoomLevel);
    } catch (error) {
      try { await cameraStream.getVideoTracks()[0].applyConstraints({ advanced: [{ zoom: targetZoomLevel }] }); }
      catch (fallbackError) { console.error('Fallback zoom failed:', fallbackError); }
      setZoomLevel(targetZoomLevel);
    } finally { setIsZooming(false); }
  };

  // ============================================
  // FLASH TOGGLE
  // ============================================
  const toggleFlash = () => {
    const modes = ['off', 'on', 'auto'];
    setFlashMode(modes[(modes.indexOf(flashMode) + 1) % modes.length]);
  };

  // ============================================
  // NAVIGATION - GO HOME
  // ============================================
  const handleGoHome = () => navigate('/');

  // ============================================
  // AVATAR - FILE CHANGE
  // ============================================
  const handleAvatarChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be less than 5MB'); return; }
    if (!file.type.startsWith('image/')) { alert('Please select an image file'); return; }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  }, []);

  // ============================================
  // AVATAR - SAVE TO FIREBASE
  // ============================================
  const handleSaveAvatar = useCallback(async () => {
    if (!avatarFile || !authUser) return;
    setIsSavingAvatar(true);
    try {
      const compressImage = (dataUrl, maxWidth, maxHeight, quality) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
          if (h > maxHeight) { w = (maxHeight / h) * w; h = maxHeight; }
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = dataUrl;
      });
      const compressedPhoto = await compressImage(avatarPreview, 400, 400, 0.85);
      const storageRef = ref(getStorage(), `users/${authUser.uid}/avatars/profile.jpg`);
      await uploadString(storageRef, compressedPhoto, 'data_url');
      const downloadURL = await getDownloadURL(storageRef);
      await updateProfile(authUser, { photoURL: downloadURL });
      await updateDoc(doc(db, 'users', authUser.uid), { photoURL: downloadURL, updatedAt: serverTimestamp() });
      setUserData(prev => ({ ...prev, photoURL: downloadURL }));
      setUserAvatar(downloadURL);
      setAvatarFile(null); setAvatarPreview(null);
    } catch (error) {
      console.error("Avatar update error:", error);
      alert(error.code === 'storage/unauthorized' ? "Permission denied. Check Firebase Storage rules." : "Failed to update avatar. Please try again.");
    } finally { setIsSavingAvatar(false); }
  }, [avatarFile, avatarPreview, authUser]);

  const handleCancelAvatar = useCallback(() => { setAvatarFile(null); setAvatarPreview(null); }, []);

    // ============================================
  // EMAIL CHANGE (CONTINUED)
  // ============================================
  const handleChangeEmail = useCallback(async (e) => {
    e.preventDefault();
    setEmailError(''); setEmailSuccess('');
    if (!newEmail || !emailPassword) { setEmailError('All fields are required.'); return; }
    try {
      const credential = EmailAuthProvider.credential(authUser.email, emailPassword);
      await reauthenticateWithCredential(authUser, credential);
      await updateEmail(authUser, newEmail);
      await updateDoc(doc(db, 'users', authUser.uid), { email: newEmail, updatedAt: serverTimestamp() });
      setUserData(prev => ({ ...prev, email: newEmail }));
      setEmailSuccess('Email updated successfully!');
      setTimeout(() => { setShowEmailChangePopup(false); setNewEmail(''); setEmailPassword(''); setEmailSuccess(''); }, 1500);
    } catch (error) {
      console.error("Email change error:", error);
      if (error.code === 'auth/wrong-password') setEmailError('Incorrect password.');
      else if (error.code === 'auth/email-already-in-use') setEmailError('This email is already in use.');
      else if (error.code === 'auth/invalid-email') setEmailError('Please enter a valid email address.');
      else if (error.code === 'auth/requires-recent-login') setEmailError('Please log out and log in again to change your email.');
      else setEmailError(error.message);
    }
  }, [authUser, newEmail, emailPassword]);

  // ============================================
  // PASSWORD CHANGE
  // ============================================
  const handleChangePassword = useCallback(async (e) => {
    e.preventDefault();
    setPasswordError(''); setPasswordSuccess('');
    if (!oldPassword || !newPassword || !confirmNewPassword) { setPasswordError('All fields are required.'); return; }
    if (newPassword !== confirmNewPassword) { setPasswordError('New passwords do not match.'); return; }
    if (newPassword.length < 6) { setPasswordError('Password must be at least 6 characters.'); return; }
    try {
      const credential = EmailAuthProvider.credential(authUser.email, oldPassword);
      await reauthenticateWithCredential(authUser, credential);
      await updatePassword(authUser, newPassword);
      setPasswordSuccess('Password updated successfully!');
      setTimeout(() => { setShowPasswordChangePopup(false); setOldPassword(''); setNewPassword(''); setConfirmNewPassword(''); setPasswordSuccess(''); }, 1500);
    } catch (error) {
      console.error("Password change error:", error);
      if (error.code === 'auth/wrong-password') setPasswordError('Current password is incorrect.');
      else if (error.code === 'auth/weak-password') setPasswordError('Password should be at least 6 characters.');
      else if (error.code === 'auth/requires-recent-login') setPasswordError('Please log out and log in again to change your password.');
      else setPasswordError(error.message);
    }
  }, [authUser, oldPassword, newPassword, confirmNewPassword]);

  // ============================================
  // SIGN OUT
  // ============================================
  const handleSignOut = useCallback(async () => {
    try {
      await signOut(getAuth());
      setShowAvatarDropdown(false); setShowDesktopProfileView(false); setShowProfileOverlay(false);
    } catch (error) {
      console.error("Sign out error:", error);
      setShowAvatarDropdown(false); setShowDesktopProfileView(false); setShowProfileOverlay(false);
    }
  }, []);

  // ============================================
  // SAVE PROFILE TO FIREBASE
  // ============================================
  const handleSaveProfileToFirebase = useCallback(async () => {
    if (!authUser) return;
    try {
      const userRef = doc(db, 'users', authUser.uid);
      await updateDoc(userRef, { displayName: tempUserName, updatedAt: serverTimestamp() });
      await updateProfile(authUser, { displayName: tempUserName });
      setUserData(prev => ({ ...prev, displayName: tempUserName }));
      setUserName(tempUserName);
      setIsEditingName(false);
      setShowProfileOverlay(false);
    } catch (error) {
      console.error("Profile update error:", error);
      alert("Failed to update profile. Please try again.");
    }
  }, [authUser, tempUserName]);

  // ============================================
  // PROJECT CRUD HANDLERS
  // ============================================
  const fetchUserProjects = useCallback(async () => {
    if (!authUser) return;
    setProjectsLoading(true);
    try { setUserProjects(await projectService.getUserProjects(authUser.uid)); }
    catch (error) { console.error('Error fetching projects:', error); }
    finally { setProjectsLoading(false); }
  }, [authUser]);

   const handleDeleteProject = useCallback(async () => {
    if (!deleteProjectPopup || !authUser) return;
    setIsDeletingProject(true);
    try {
      // 1) Soft-delete the project header first.
      await projectService.deleteProject(deleteProjectPopup.id);

      // 2) Sweep subcollections + Storage objects. Firestore does not
      //    cascade-delete. Best-effort — don't block the UI on failures.
      //    Orphaned subcollection docs will be overwritten on next open
      //    (writeFrames uses setDoc with merge:true), so this is safe.
      try {
        await sweepProjectStorage(authUser.uid, deleteProjectPopup.id);
      } catch (sweepErr) {
        console.warn('Project storage sweep failed (orphaned files may remain):', sweepErr);
        showToast('Project deleted, but some files could not be cleaned up.', 'warning', 4000);
      }

      setUserProjects(prev => prev.filter(p => p.id !== deleteProjectPopup.id));
      setDeleteProjectPopup(null);
      showToast('Project deleted successfully', 'success', 3000);
    } catch (error) {
      console.error('Error deleting project:', error);
      showToast('Failed to delete project. Please try again.', 'error', 4000);
    } finally {
      setIsDeletingProject(false);
    }
  }, [deleteProjectPopup, authUser]);

  const handleRenameProject = useCallback(async () => {
    if (!projectToRename || !authUser || !newProjectName.trim()) return;
    try {
      await projectService.updateProject(projectToRename.id, { name: newProjectName.trim() });
      setUserProjects(prev => prev.map(p => p.id === projectToRename.id ? { ...p, name: newProjectName.trim() } : p));
      await fetchUserProjects();
      setShowRenameProjectPopup(false); setProjectToRename(null); setNewProjectName('');
    } catch (error) {
      console.error('Error renaming project:', error);
      alert('Failed to rename project. Please try again.');
    }
  }, [projectToRename, authUser, newProjectName, fetchUserProjects]);

  // ============================================
  // PROJECT LOADING & NAVIGATION
  // ============================================
  const navigateToEditor = useCallback((projectData) => {
    const existingId = projectData.projectId || projectData.id;
    if (authUser && !existingId) {
      projectService.createProject(authUser.uid, projectData)
        .then((result) => navigate('/editor', { state: { projectData: { ...projectData, projectId: result.id, id: result.id } } }))
        .catch(() => navigate('/editor', { state: { projectData } }));
    } else {
      navigate('/editor', { state: { projectData: { ...projectData, projectId: existingId } } });
    }
  }, [authUser, navigate]);

  const handleLoadSavedFile = async (projectId, fileName, fileImage) => {
    // ────────────────────────────────────────────────────────────────
    // The Dashboard now owns the ENTIRE load pipeline for saved projects:
    // project doc → canvas state → frames → bitmaps → working base →
    // composite. The Editor will receive the fully built payload via
    // location.state.projectData.__preloaded and render on first commit —
    // no spinner in the editor.
    // ────────────────────────────────────────────────────────────────
    setLoadingMode('load-file');
    setLoadingFileName(fileName);
    setProjectPhoto(fileImage);
    setShowLoadingOverlay(true);
    setLoadingProgress(0);
    setIsLoadingActive(true);
    setLoadStatusText('Fetching project…');

    if (projectId) sessionStorage.setItem('currentLoadingProjectId', projectId);

    // If we don't have an auth user or project id, fall back to the old
    // synthetic progress bar so the UI still feels alive.
    if (!authUser || !projectId) {
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) { clearInterval(interval); return 100; }
          return prev + 2;
        });
      }, 30);
      setLoadStatusText('Loading project…');
      return;
    }

    try {
      const { projectData: loadedDoc, payload } = await prefetchProjectForEditor({
        authUser,
        projectId,
        onProgress: (pct, label) => {
          setLoadingProgress(pct);
          setLoadStatusText(label);
        },
      });

      setLoadingFileName(loadedDoc.name || fileName);
      setProjectPhoto(loadedDoc.photoURL || fileImage);

      // ────────────────────────────────────────────────────────────────
      // ROOMS HYDRATION — prefetchProjectForEditor does not currently
      // read `roomsStateURL` off the project doc, so the payload arrives
      // here with an empty rooms array even when the project has stored
      // rooms. Because the Editor's prefetch fast path sets
      // baseCanonicalRef.current = true on the first commit and thereby
      // DISABLES the Firestore load effect that would normally read
      // roomsStateURL, the rooms never get a second chance to load. The
      // user sees only a freshly seeded "Original" room and every
      // uploaded room appears lost.
      //
      // Fix: read `roomsStateURL` off the freshly fetched project doc,
      // fetch the JSON, and hand a normalised room list to the Editor
      // inside `__preloaded.rooms`. The shape matches exactly what the
      // Editor's fast-path restore block consumes: {id, name,
      // canvasImage, originalImage, thumbnail, naturalWidth,
      // naturalHeight, layers, selectedLayerId, frameImages, createdAt}.
      //
      // Long-term this fetch belongs inside prefetchProjectForEditor in
      // compositeService.js so every caller benefits; until then, this
      // covers the saved-project load path.
      // ────────────────────────────────────────────────────────────────
      let roomsForPayload = Array.isArray(payload?.rooms) ? payload.rooms : [];
      let activeRoomIdForPayload = payload?.activeRoomId || null;

      if (roomsForPayload.length === 0 && loadedDoc?.roomsStateURL) {
        try {
          const roomsRes = await fetch(loadedDoc.roomsStateURL);
          if (roomsRes.ok) {
            const roomsJson = await roomsRes.json();
            if (Array.isArray(roomsJson) && roomsJson.length > 0) {
              roomsForPayload = roomsJson.map(r => ({
                id: r.id,
                name: r.name || 'Room',
                canvasImage: r.canvasImage || null,
                originalImage: r.originalImage || r.canvasImage || null,
                thumbnail: r.thumbnail || r.canvasImage || null,
                naturalWidth: r.naturalWidth || 0,
                naturalHeight: r.naturalHeight || 0,
                layers: Array.isArray(r.layers) ? r.layers : [],
                selectedLayerId: r.selectedLayerId || null,
                frameImages: Array.isArray(r.frameImages) ? r.frameImages : [],
                createdAt: r.createdAt || Date.now(),
              }));
              activeRoomIdForPayload = loadedDoc.activeRoomId
                || (roomsForPayload[0] && roomsForPayload[0].id)
                || null;
            }
          }
        } catch (roomsFetchErr) {
          console.warn('[rooms] Failed to hydrate rooms into prefetch payload:', roomsFetchErr);
        }
      }

      // ────────────────────────────────────────────────────────────────
      // FULL PROJECT PRELOAD — everything the Editor needs is fetched,
      // decoded, measured, and composited HERE, behind the Dashboard
      // progress bar. When the Editor mounts, its <img> tags hit the
      // already-warm browser image cache and its composite effect hits
      // the already-built IndexedDB cache for every room. The Editor
      // does almost zero loading work of its own.
      //
      // Three things happen per room, sequentially, with live progress:
      //
      //   1. DECODE every URL the Editor will render as a display <img>
      //      — room canvas, room thumbnail, and every frame's artwork.
      //      A plain `new Image()` with NO crossOrigin matches the
      //      consumption pattern of the Editor's <img> tags, so the
      //      browser caches a single entry under the exact key the
      //      Editor will look up. (A crossOrigin load would warm a
      //      different cache entry than the display <img> reads from.)
      //
      //   2. BUILD each room's composite (base + placed frames) and
      //      stash it in IndexedDB under the same fingerprint the
      //      Editor's composite effect uses. When the user switches to
      //      this room in the Editor, the composite effect hits the
      //      cache and does ZERO pixel work — no frozen UI on switch.
      //
      //   3. MEASURE each room's canvas natural dimensions if they are
      //      not already on the room record, so the Editor never has
      //      to instantiate a throwaway Image() just to size the
      //      canvas on first paint.
      //
      // Cost note: building composites for every room with frames
      // adds real time to the load (roughly 100-300 ms per room at
      // 2048px with a handful of frames). That is the trade — the
      // time moves from a per-switch stall in the Editor to a single
      // predictable wait behind this progress bar. If a project ever
      // has so many rooms that this becomes excessive, cap the loop
      // with `roomsForPayload.slice(0, 4)` and let the remaining
      // rooms fall back to lazy build on switch.
      // ────────────────────────────────────────────────────────────────
      const decodeImage = (url) => new Promise((resolve) => {
        if (typeof url !== 'string' || !url) {
          resolve(null);
          return;
        }
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });

      const topLevelFrames = Array.isArray(payload?.frames) ? payload.frames : [];
      const totalRoomsForPreload = roomsForPayload.length;
      const preloadStartPct = 55;
      const preloadEndPct = 95;
      const pctPerRoom = totalRoomsForPreload > 0
        ? Math.max(1, Math.floor((preloadEndPct - preloadStartPct) / totalRoomsForPreload))
        : 0;

      for (let i = 0; i < roomsForPayload.length; i++) {
        const r = roomsForPayload[i];
        if (!r) continue;

        setLoadingProgress(preloadStartPct + (i * pctPerRoom));
        setLoadStatusText(`Preloading room ${i + 1} of ${totalRoomsForPreload}…`);

        // ── 1. Decode every display URL for this room ────────────────
        const urlsToWarm = [];
        if (r.canvasImage) urlsToWarm.push(r.canvasImage);
        if (r.originalImage) urlsToWarm.push(r.originalImage);
        if (r.thumbnail) urlsToWarm.push(r.thumbnail);

        const roomFrames = Array.isArray(r.frameImages) ? r.frameImages : [];
        for (const f of roomFrames) {
          if (!f) continue;
          if (f.originalImage) urlsToWarm.push(f.originalImage);
          if (f.imageData) urlsToWarm.push(f.imageData);
          if (f.originalUncroppedData) urlsToWarm.push(f.originalUncroppedData);
        }

        const decoded = await Promise.all(urlsToWarm.map(decodeImage));

        // ── 3. Measure natural dimensions if missing on the record ───
        if ((!r.naturalWidth || !r.naturalHeight) && r.canvasImage) {
          const canvasIdx = urlsToWarm.indexOf(r.canvasImage);
          const canvasImg = canvasIdx >= 0 ? decoded[canvasIdx] : null;
          if (canvasImg) {
            r.naturalWidth = canvasImg.naturalWidth;
            r.naturalHeight = canvasImg.naturalHeight;
          }
        }

        // ── 2. Build + cache this room's composite ───────────────────
        // Rooms with no placed frames render the raw base, so the
        // Editor's "no placed frames → show raw base" short-circuit
        // handles them for free. Only prewarm when there is real
        // compositing work to do.
        const placedFrames = roomFrames.filter(
          f => f && f.corners && Array.isArray(f.corners) && f.corners.length === 4 && f.isPlaced !== false
        );

        if (placedFrames.length > 0 && r.canvasImage) {
          try {
            const cacheKey = computeCompositeCacheKey(r.canvasImage, placedFrames);
            const existing = await getCachedComposite(cacheKey);
            if (!existing) {
              setLoadStatusText(`Compositing room ${i + 1} of ${totalRoomsForPreload}…`);
              const composite = await buildFrameComposite(r.canvasImage, roomFrames);
              if (composite) {
                await setCachedComposite(cacheKey, composite);
              }
            }
          } catch (compositeErr) {
            console.warn('[rooms] prewarm composite failed for room', r.id, compositeErr);
          }
        }
      }

      // Also warm the top-level frame list (project legacy mirror) in
      // case the Editor falls back to it on a project with no rooms.
      for (const f of topLevelFrames) {
        if (!f) continue;
        await Promise.all([
          decodeImage(f.originalImage),
          decodeImage(f.imageData),
          decodeImage(f.originalUncroppedData),
        ]);
      }

      setLoadingProgress(preloadEndPct);
      setLoadStatusText('Finalizing…');

      // The Editor reads location.state.projectData.__preloaded and
      // populates its state in a single commit. The navigation effect
      // below (isLoadingActive && loadingProgress === 100) picks this up
      // and routes to /editor automatically.
      setLoadedProjectData({
        ...loadedDoc,
        __preloaded: {
          ...payload,
          rooms: roomsForPayload,
          activeRoomId: activeRoomIdForPayload,
        },
      });

      // Guarantee the loading bar finishes even if a phase was skipped.
      setLoadingProgress(100);
      setLoadStatusText('Opening editor…');
    } catch (error) {
      console.error('Error prefetching project:', error);
      showToast('Failed to load project. Please try again.', 'error', 4000);
      setShowLoadingOverlay(false);
      setIsLoadingActive(false);
      setLoadingProgress(0);
      setLoadStatusText('');
    }
  };

  // ============================================
  // PROJECT CREATION HANDLERS
  // ============================================
  const handleCreateProject = () => {
    if (!projectName.trim()) return;
    const finalPhoto = projectPhoto || tempPhoto;
    if (!finalPhoto) return;
    setIsLoadingActive(true);
    const interval = setInterval(() => {
      setLoadingProgress(prev => { if (prev >= 100) { clearInterval(interval); return 100; } return prev + 2; });
    }, 30);
  };

  const handlePromptSubmit = () => {
    if (promptValue.trim() && !isLoadingActive) {
      setProjectDetails(promptValue); setLoadingMode('prompt'); setProjectCreationStep('name');
      setShowPhotoStep(false); setTempPhoto(null); setProjectPhoto(null); setProjectName('');
      setShowLoadingOverlay(true); setLoadingProgress(0); setIsLoadingActive(false);
    }
  };

  const handleNewProjectClick = () => {
    setLoadingMode('new-project'); setProjectCreationStep('name'); setShowPhotoStep(false);
    setTempPhoto(null); setProjectPhoto(null); setProjectDetails(''); setProjectName('');
    setShowLoadingOverlay(true);
  };

  const handleCancelProject = () => {
    setShowLoadingOverlay(false); setProjectName(''); setProjectDetails(''); setPromptValue('');
    setProjectPhoto(null); setTempPhoto(null); setIsLoadingActive(false);
    setShowPhotoStep(false); setProjectCreationStep('name'); setCameraModeStep('photo');
    setProjectType('Personal');
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !showLoadingOverlay) handlePromptSubmit(); };

  const handleProjectNameKeyPress = (e) => {
    if (e.key === 'Enter' && projectName.trim() && !isLoadingActive && projectCreationStep === 'name') {
      setProjectCreationStep('photo'); setShowPhotoStep(true);
    }
  };

  // ============================================
  // PROJECT STEP NAVIGATION
  // ============================================
  const handleBackToNameStep = () => {
    setProjectCreationStep('name');
    setShowPhotoStep(false);
    setTempPhoto(null);
    setProjectPhoto(null);
    setHasPhotoPermission(false);
  };
  const handleBackToPhotoStep = () => {
    setProjectCreationStep('photo');
    setShowPhotoStep(true);
    setHasPhotoPermission(false);
  };
  const handleContinueToPhotoStep = () => { if (projectName.trim()) { setProjectCreationStep('photo'); setShowPhotoStep(true); } };
  const handleContinueToConfirmStep = () => {
    if (tempPhoto || projectPhoto) { setProjectPhoto(tempPhoto || projectPhoto); setProjectCreationStep('confirm'); setShowPhotoStep(false); }
  };

  // ============================================
  // CAMERA HANDLERS
  // ============================================
  const handleCameraClick = async () => {
    setShowCamera(true); setCameraMode('camera'); setZoomLevel(1);
    setCameraModeStep('photo'); setCapturedPhoto(null); setProjectPhoto(null);
    setTempPhoto(null); setProjectName('');
    window.dispatchEvent(new CustomEvent('cameraStateChange', { detail: { cameraOpen: true } }));
    const dashboard = document.querySelector('.mobile-dashboard');
    const topBanner = document.querySelector('.top-banner-dashboard-div');
    if (dashboard) { dashboard.style.transition = 'background-color 0.5s ease'; dashboard.style.backgroundColor = '#333'; }
    if (topBanner) { topBanner.style.transition = 'background-color 0.5s ease'; topBanner.style.backgroundColor = '#333'; }
    gsap.delayedCall(0.001, () => {
      gsap.to('.mobile-dashboard', { x: '-100%', duration: 0.5, ease: 'power2.inOut' });
      gsap.to('.camera-overlay', { x: '0%', duration: 0.5, ease: 'power2.inOut', onComplete: async () => {
        try {
          const stream = await getCameraStream();
          setCameraStream(stream);
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.setAttribute('playsinline', 'true');
            videoRef.current.muted = true;
            videoRef.current.style.transform = 'scale(1)';
            videoRef.current.onloadedmetadata = () => videoRef.current.play();
          }
        } catch (error) { alert('Camera access failed. Please allow camera permissions.'); }
      }});
    });
  };

  const handleCameraClickWithPhotoStep = () => {
    setCameraModeStep('photo'); setTempPhoto(null); setProjectPhoto(null); setProjectName('');
    handleCameraClick();
  };

  const handleCameraClose = () => {
    if (cameraStream) { cameraStream.getTracks().forEach(track => track.stop()); setCameraStream(null); }
    setCapturedPhoto(null);
    window.dispatchEvent(new CustomEvent('cameraStateChange', { detail: { cameraOpen: false } }));
    const dashboard = document.querySelector('.mobile-dashboard');
    const topBanner = document.querySelector('.top-banner-dashboard-div');
    if (dashboard) { dashboard.style.backgroundColor = '#2D2D2D'; }
    if (topBanner) { topBanner.style.backgroundColor = '#2D2D2D'; }
    gsap.to('.mobile-dashboard', { x: '0%', duration: 0.5, ease: 'power2.inOut' });
    gsap.to('.camera-overlay', { x: '100%', duration: 0.5, ease: 'power2.inOut', onComplete: () => setShowCamera(false) });
  };

  const handleTakePicture = () => {
    if (videoRef.current && cameraStream) {
      setShowShutterEffect(true);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const photoDataUrl = canvas.toDataURL('image/png');
      setTimeout(() => { setShowShutterEffect(false); setCapturedPhoto(photoDataUrl); setPhotoSource('camera'); }, 300);
    }
  };

  const handleConfirmPhoto = () => {
    if (!capturedPhoto) return;
    setTempPhoto(capturedPhoto); setProjectPhoto(capturedPhoto);
    handleCameraClose();
    setTimeout(() => {
      // Always go to photo step for review and permission
      setProjectCreationStep('photo');
      setShowPhotoStep(true);
      setShowLoadingOverlay(true);
      setLoadingProgress(0);
      setIsLoadingActive(false);
    }, 500);
  };

  const handleDeclinePhoto = async () => {
    setCapturedPhoto(null); setPhotoSource('camera');
    if (zoomLevel === 0.5) {
      setZoomLevel(1);
      if (videoRef.current) videoRef.current.style.transform = 'scale(1)';
      if (cameraStream) {
        try { await cameraStream.getVideoTracks()[0].applyConstraints({ advanced: [{ zoom: 1 }] }); }
        catch (error) { console.error('Failed to reset hardware zoom:', error); }
      }
    }
    try {
      const stream = await getCameraStream();
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.muted = true;
        videoRef.current.style.transform = 'scale(1)';
        videoRef.current.onloadedmetadata = () => videoRef.current.play();
      }
    } catch (error) { console.error('Failed to restart camera:', error); }
  };

  const handleCameraModeBack = () => {
    setShowLoadingOverlay(false); setProjectName(''); setProjectPhoto(null);
    setTempPhoto(null); setCapturedPhoto(null); setLoadingMode('');
    setShowPhotoStep(false); setIsLoadingActive(false);
    setProjectCreationStep('name'); setCameraModeStep('photo');
    if (cameraStream) { cameraStream.getTracks().forEach(track => track.stop()); setCameraStream(null); }
    setShowCamera(false);
    const dashboard = document.querySelector('.mobile-dashboard');
    const topBanner = document.querySelector('.top-banner-dashboard-div');
    if (dashboard) dashboard.style.backgroundColor = '#2D2D2D';
    if (topBanner) topBanner.style.backgroundColor = '#2D2D2D';
    gsap.to('.mobile-dashboard', { x: '0%', duration: 0.5, ease: 'power2.inOut' });
  };

  const handleCameraModeContinue = () => { if (tempPhoto || projectPhoto) { setShowPhotoStep(false); setCameraModeStep('name'); } };

  // ============================================
  // CREATIVE CATALOG HANDLERS
  // ============================================
  const handleCatalogContainerClick = () => { if (!showPhotoDetails) setCatalogActive(true); };

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo); setShowPhotoDetails(true); setCatalogActive(true);
    triggerUnsplashDownload(photo);
  };

  const handleStyleGuideClick = (guide) => {
    const photoObj = {
      id: guide.id, urls: { regular: guide.image, small: guide.image, full: guide.image },
      user: { name: guide.author, username: 'outlne-design', links: { html: '#' } },
      alt_description: guide.title, description: guide.description, width: 1200, height: 800,
      created_at: guide.date, links: { download_location: '', html: '#' },
    };
    setSelectedPhoto(photoObj); setShowPhotoDetails(true); setCatalogActive(true);
  };

  const handleBackToGallery = () => { setShowPhotoDetails(false); setSelectedPhoto(null); setActiveNavItem(null); };
  const handlePhotoDetailClose = () => { setSelectedPhoto(null); setShowPhotoDetails(false); };

  const handleAddToMoodBoard = (photo) => {
    alert(`Photo "${photo.alt_description || 'Untitled'}" added to Mood Board! (Integration with Editor's Asset Library coming soon)`);
  };

  const handleOpenStyleGuidesFull = (e) => { e.stopPropagation(); setShowStyleGuidesFull(true); setCatalogActive(true); };
  const handleCloseStyleGuidesFull = (e) => { e.stopPropagation(); setShowStyleGuidesFull(false); setActiveNavItem(null); };

  const handleCatalogSearch = (e) => {
    e.preventDefault();
    fetchUnsplashPhotos(1, catalogSearchQuery || 'interior design');
  };

  const handleLoadMorePhotos = () => {
    const nextPage = unsplashPage + 1;
    setUnsplashPage(nextPage);
    fetchUnsplashPhotos(nextPage, catalogSearchQuery || 'interior design');
  };

  // ============================================
  // AVATAR & PROFILE UI HANDLERS
  // ============================================
  const handleAvatarClick = () => setShowAvatarDropdown(!showAvatarDropdown);
  const handleDesktopAvatarClick = () => {
    if (!authUser) { alert('Please log in to view your profile'); return; }
    setShowDesktopProfileView(!showDesktopProfileView);
  };

  const handleProfileClick = () => { setShowAvatarDropdown(false); setShowProfileOverlay(true); setTempUserName(userName); };
  const handleSettingsClick = () => { setShowAvatarDropdown(false); setShowMobileSettingsView(true); };
  const handleSaveProfile = () => handleSaveProfileToFirebase();

  const handleCancelProfile = () => {
    setTempUserName(userName); setIsEditingName(false); setShowProfileOverlay(false);
  };

  const handleEditName = () => setIsEditingName(true);

  // ============================================
  // FILE INPUT HANDLERS
  // ============================================
  const handleChooseFromGallery = () => { if (fileInputRef.current) fileInputRef.current.click(); };
  const handleChooseProfileAvatar = () => { if (profileFileInputRef.current) profileFileInputRef.current.click(); };

  const handleFileChange = (e, forProfile = false) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    if (forProfile) {
      if (file.size > 5 * 1024 * 1024) { alert('Image must be less than 5MB'); e.target.value = ''; return; }
      if (!file.type.startsWith('image/')) { alert('Please select an image file'); e.target.value = ''; return; }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        const photoDataUrl = event.target.result;
        if (showCamera) { setCapturedPhoto(photoDataUrl); setPhotoSource('gallery'); }
        else if (showPhotoStep || projectCreationStep === 'photo') { setTempPhoto(photoDataUrl); }
        else { setCapturedPhoto(photoDataUrl); setPhotoSource('gallery'); }
      };
      reader.onerror = () => alert('Failed to load the selected image. Please try again.');
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // ============================================
  // TAB CLICK HANDLER
  // ============================================
  const handleSavedProjectClick = (tab) => {
    if (tab.type === 'saved-project' && tab.fileName) {
      setSelectedTab(tab.id);
      handleLoadSavedFile(tab.projectId, tab.fileName, tab.image);
    } else if (tab.type === 'tutorial') {
      setSelectedTab(tab.id);
      setTimeout(() => setSelectedTab(null), 500);
    } else if (tab.type === 'new-project') {
      setSelectedTab(tab.id);
      handleNewProjectClick();
    }
  };

  // ============================================
  // SCROLL HANDLER
  // ============================================
  const handleScroll = () => {
    if (projectDropdownOpen) setProjectDropdownOpen(null);
    const gallery = galleryRef.current;
    const track = trackRef.current;
    if (!gallery || !track) return;
    const scrollLeft = gallery.scrollLeft;
    const maxScrollLeft = gallery.scrollWidth - gallery.clientWidth;
    if (maxScrollLeft > 0) {
      const scrollPercentage = scrollLeft / maxScrollLeft;
      const indicatorWidth = 60;
      const maxIndicatorPosition = track.offsetWidth - indicatorWidth;
      const indicatorPosition = scrollPercentage * maxIndicatorPosition;
      const roundedPosition = Math.round(indicatorPosition * 100) / 100;
      const indicatorBar = track.querySelector('.scroll-indicator-bar');
      if (indicatorBar) indicatorBar.style.transform = `translate3d(${roundedPosition}px, 0, 0)`;
      if (Math.abs(scrollPosition - roundedPosition) > 0.5) setScrollPosition(roundedPosition);
    } else if (scrollPosition !== 0) setScrollPosition(0);
  };

  // ============================================
  // MEMOIZED TABS
  // ============================================
  const tabs = useMemo(() => {
    const dynamicTabs = [{ id: 'new-project', type: 'new-project', label: 'New Project', icon: 'plus' }];
    userProjects.forEach((project) => {
      dynamicTabs.push({
        id: project.id, type: 'saved-project', label: project.name || 'Untitled', icon: 'file',
        image: project.thumbnailURL || project.photoURL || SaveFilePreview, fileName: project.name || 'Untitled',
        projectId: project.id, updatedAt: project.updatedAt,
      });
    });
    dynamicTabs.push(
      { id: 'tutorial-1', type: 'tutorial', label: 'Starter Tutorial', icon: 'question', video: TutorialPreview1 },
      { id: 'tutorial-2', type: 'tutorial', label: 'Tutorial', icon: 'question', video: TutorialPreview2 },
    );
    return dynamicTabs;
  }, [userProjects]);

  // ============================================
  // GRID STYLES
  // ============================================
  const gridStyles = [
    { backgroundSize: '40px 40px', display: activeGrid === 0 ? 'block' : 'none', opacity: gridOpacity, transition: 'opacity 1s ease' },
    { backgroundSize: '50px 40px', backgroundPositionX: '20px', display: activeGrid === 1 ? 'block' : 'none', opacity: gridOpacity, transition: 'opacity 1s ease' },
    { backgroundSize: '40px 30px', backgroundPositionY: '10px', display: activeGrid === 2 ? 'block' : 'none', opacity: gridOpacity, transition: 'opacity 1s ease' },
  ];

  // ============================================
  // USE EFFECTS - PWA DETECTION
  // ============================================
  useEffect(() => {
    const checkPWA = () => {
      setIsPWA(window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || document.referrer.includes('android-app://'));
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkPWA);
    return () => mediaQuery.removeEventListener('change', checkPWA);
  }, []);

  // ============================================
  // USE EFFECTS - RELOAD TRACKING
  // ============================================
  useEffect(() => {
    const needsReload = sessionStorage.getItem('needsThemeReload');
    if (needsReload === 'true' && !hasReloaded) {
      sessionStorage.removeItem('needsThemeReload');
      setHasReloaded(true);
      setTimeout(() => { window.location.href = window.location.href.split('?')[0] + '?t=' + Date.now(); }, 100);
    }
  }, [hasReloaded]);

  // ============================================
  // USE EFFECTS - DEBUG LOGS (REMOVED IN PRODUCTION - KEPT MINIMAL)
  // ============================================
  useEffect(() => {
    console.log('TutorialPreview1 URL:', TutorialPreview1);
    console.log('TutorialPreview2 URL:', TutorialPreview2);
    console.log('SaveFilePreview URL:', SaveFilePreview);
  }, []);

  // ============================================
  // USE EFFECTS - AUTH STATE LISTENER
  // ============================================
  const isInitialAuthCheck = useRef(true);
  
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user);
      if (user) {
        setIsDashboardAvailable(true);
        try {
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
           if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            if (data.displayName) { setUserName(data.displayName); setTempUserName(data.displayName); }
            else { const defaultName = user.email?.split('@')[0] || 'User'; setUserName(defaultName); setTempUserName(defaultName); }
            if (data.photoURL) setUserAvatar(data.photoURL);
            // Only call checkDeveloperStatus on initial load
            if (isInitialAuthCheck.current) {
              checkDeveloperStatus();
              isInitialAuthCheck.current = false;
            }
          } else {
            const defaultName = user.email?.split('@')[0] || 'User';
            const basicData = { uid: user.uid, email: user.email, displayName: user.displayName || defaultName, photoURL: user.photoURL || null, createdAt: serverTimestamp(), profileComplete: false };
            await setDoc(userRef, basicData);
            setUserData(basicData); setUserName(defaultName); setTempUserName(defaultName);
            if (user.photoURL) setUserAvatar(user.photoURL);
            if (isInitialAuthCheck.current) {
              checkDeveloperStatus();
              isInitialAuthCheck.current = false;
            }
          }
        } catch (error) { console.error("Error fetching user data:", error); }
      } else {
        setUserData(null); setIsDashboardAvailable(false);
        setUserName('Guest'); setTempUserName('Guest'); setUserAvatar(null);
      }
      setIsProfileLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Watch userData changes and update developer status only when userData actually changes
  useEffect(() => {
    if (userData) {
      checkDeveloperStatus();
    }
  }, [userData, checkDeveloperStatus]);

  // ============================================
  // USE EFFECTS - THEME COLOR & VIEWPORT FIXES
  // ============================================

  // ============================================
  // USE EFFECTS - THEME COLOR & VIEWPORT FIXES
  // ============================================
  useEffect(() => {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) { metaThemeColor = document.createElement('meta'); metaThemeColor.name = 'theme-color'; document.head.appendChild(metaThemeColor); }
    metaThemeColor.content = '#2D2D2D';
    document.documentElement.style.transition = 'background-color 0.5s ease';
    document.body.style.transition = 'background-color 0.5s ease';
    document.documentElement.style.backgroundColor = '#2D2D2D';
    document.body.style.backgroundColor = '#2D2D2D';
    document.documentElement.style.minHeight = '100vh';
    document.documentElement.style.minHeight = '-webkit-fill-available';
    window.dispatchEvent(new CustomEvent('cameraStateChange', { detail: { cameraOpen: false } }));

    const setVH = () => { document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`); };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', () => setTimeout(setVH, 100));
    window.addEventListener('focus', setVH);
    window.addEventListener('blur', setVH);

    const preventZoom = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        const viewport = document.querySelector('meta[name=viewport]');
        if (viewport) viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
    };
    const resetZoom = () => {
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
    };
    document.addEventListener('focusin', preventZoom);
    document.addEventListener('focusout', resetZoom);

    if (!isPWA) { document.body.classList.add('browser-body'); document.documentElement.style.overflow = 'hidden'; document.body.style.overflow = 'hidden'; }

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
      window.removeEventListener('focus', setVH);
      window.removeEventListener('blur', setVH);
      document.removeEventListener('focusin', preventZoom);
      document.removeEventListener('focusout', resetZoom);
      document.body.classList.remove('browser-body');
      document.documentElement.style.backgroundColor = 'transparent';
      document.body.style.backgroundColor = 'transparent';
      document.documentElement.style.minHeight = '';
      if (metaThemeColor) metaThemeColor.content = '#ffffff';
    };
  }, [isPWA]);

  // ============================================
  // USE EFFECTS - CLICK OUTSIDE CATALOG
  // ============================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (catalogActive && !showPhotoDetails) {
        const catalogElement = document.querySelector('.feed-section');
        const mobileCatalogElement = document.querySelector('[data-mobile-catalog]');
        const clickedInsideDesktop = catalogElement?.contains(event.target);
        const clickedInsideMobile = mobileCatalogElement?.contains(event.target);
        if (!clickedInsideDesktop && !clickedInsideMobile) setCatalogActive(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [catalogActive, showPhotoDetails]);

  // ============================================
  // USE EFFECTS - CLICK OUTSIDE AVATAR DROPDOWN
  // ============================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAvatarDropdown && avatarDropdownRef.current && !avatarDropdownRef.current.contains(event.target) && avatarButtonRef.current && !avatarButtonRef.current.contains(event.target)) {
        setShowAvatarDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); document.removeEventListener('touchstart', handleClickOutside); };
  }, [showAvatarDropdown]);

  // ============================================
  // USE EFFECTS - CLOSE PROJECT DROPDOWN ON SCROLL
  // ============================================
  useEffect(() => {
    const handleScroll = () => {
      if (projectDropdownOpen) setProjectDropdownOpen(null);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [projectDropdownOpen]);

    // ============================================
  // USE EFFECTS - CLOSE DROPDOWN ON CLICK OUTSIDE (except gear area & menu)
  // ============================================
  useEffect(() => {
    if (!projectDropdownOpen) return;
    const handleClickOutside = (e) => {
      const menu = document.querySelector('.project-dropdown-menu');
      const gearArea = document.querySelector('.gear-click-area');
      // If click is inside the menu or on the gear area, do nothing
      if (menu?.contains(e.target) || gearArea?.contains(e.target)) return;
      setProjectDropdownOpen(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [projectDropdownOpen]);

  // ============================================
  // USE EFFECTS - ANIMATED DOTS DURING LOADING
  // ============================================
  useEffect(() => {
    let dotsInterval;
    if (isLoadingActive) {
      dotsInterval = setInterval(() => {
        setLoadingDots(prev => prev === '...' ? '' : prev === '..' ? '...' : prev === '.' ? '..' : '.');
      }, 500);
    } else setLoadingDots('');
    return () => { if (dotsInterval) clearInterval(dotsInterval); };
  }, [isLoadingActive]);

  // ============================================
  // USE EFFECTS - AUTO-ROTATE CAROUSELS (DESKTOP)
  // ============================================
  useEffect(() => { const interval = setInterval(() => setCatalogIndex(prev => (prev + 1) % catalogImages.length), 5000); return () => clearInterval(interval); }, []);
  useEffect(() => { const interval = setInterval(() => setFeedIndex(prev => (prev + 1) % feedCatalogImages.length), 6000); return () => clearInterval(interval); }, []);
  useEffect(() => { const interval = setInterval(() => setActiveStyleGuide(prev => (prev + 1) % styleGuides.length), 6000); return () => clearInterval(interval); }, []);

  // ============================================
  // USE EFFECTS - CAMERA CLEANUP
  // ============================================
  useEffect(() => { return () => { if (cameraStream) cameraStream.getTracks().forEach(track => track.stop()); }; }, [cameraStream]);

  // ============================================
  // RENDERS PANEL OPEN/CLOSE — MOBILE ONLY (Desktop uses instant modal)
  // ============================================
  const openRendersPanel = useCallback(() => {
    if (!authUser) return;
    setRendersActive(true);
    setAssetLibraryActive(false);
    setCatalogActive(false);
    setShowDesktopSettingsView(false);
    setShowDesktopProfileView(false);
    
    // Mirror camera open pattern: wait for DOM paint, then animate
        gsap.delayedCall(0.001, () => {
      const dashboard = document.querySelector('.mobile-dashboard');
      const panel = document.querySelector('.renders-panel-slide');
      if (!dashboard || !panel || window.innerWidth > 767) return;
      
      // Disable CSS transition on dashboard during animation
      const originalTransition = dashboard.style.transition;
      dashboard.style.transition = 'none';
      
      gsap.killTweensOf([dashboard, panel]);
      gsap.set(panel, { x: '100vw' });
      gsap.to(dashboard, { x: '-100vw', duration: 0.5, ease: 'power2.inOut', force3D: true });
      gsap.to(panel, { x: '0vw', duration: 0.5, ease: 'power2.inOut', force3D: true, onComplete: () => {
        dashboard.style.transition = originalTransition;
      }});
    });
  }, [authUser]);
  
  const closeRendersPanel = useCallback(() => {
    const dashboard = document.querySelector('.mobile-dashboard');
    const panel = document.querySelector('.renders-panel-slide');
    
    if (dashboard && panel && window.innerWidth <= 767) {
      // Disable the CSS transition on the dashboard to avoid interference
      const originalTransition = dashboard.style.transition;
      dashboard.style.transition = 'none';
      
      gsap.killTweensOf([dashboard, panel]);
      // Simple, perfectly synced slide: dashboard to 0, panel to 100vw
      gsap.to(dashboard, { x: '0vw', duration: 0.5, ease: 'power2.inOut', force3D: true });
      gsap.to(panel, { x: '100vw', duration: 0.5, ease: 'power2.inOut', force3D: true, onComplete: () => {
        // Restore the CSS transition
        dashboard.style.transition = originalTransition;
        gsap.set(dashboard, { clearProps: 'transform' });
        gsap.set(panel, { clearProps: 'all' });
        setRendersActive(false);
        setActiveNavItem(null);
        setCatalogActive(false);
        setAssetLibraryActive(false);
        setShowDesktopSettingsView(false);
        setShowDesktopProfileView(false);
      }});
    } else {
      // Desktop/tablet instant close
      setRendersActive(false);
      setActiveNavItem(null);
      setCatalogActive(false);
      setShowDesktopSettingsView(false);
      setShowDesktopProfileView(false);
    }
  }, []);

  // ============================================
  // CLEANUP GSAP ANIMATIONS ON UNMOUNT
  // ============================================
  useEffect(() => {
    return () => {
      const dashboard = document.querySelector('.mobile-dashboard');
      const panel = document.querySelector('.renders-panel-slide');
      if (dashboard) {
        gsap.killTweensOf(dashboard);
        gsap.set(dashboard, { clearProps: 'transform' });
      }
      if (panel) {
        gsap.killTweensOf(panel);
        gsap.set(panel, { clearProps: 'all' });
      }
    };
  }, []);

  // ============================================
  // USE EFFECTS - CAMERA THEME COLOR
  // ============================================
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.content = showCamera ? '#333' : '#2D2D2D';
      document.documentElement.style.backgroundColor = showCamera ? '#333' : '#2D2D2D';
      document.body.style.backgroundColor = showCamera ? '#333' : '#2D2D2D';
    }
  }, [showCamera]);

  // ============================================
  // USE EFFECTS - UNSLASH INITIAL FETCH
  // ============================================
  useEffect(() => { fetchUnsplashPhotos(1, 'interior design'); }, [fetchUnsplashPhotos]);

  // ============================================
  // USE EFFECTS - LOADING COMPLETION & NAVIGATION
  // ============================================
  useEffect(() => {
    let navigationTimeout;
    if (isLoadingActive && loadingProgress === 100 && loadingMode === 'load-file') {
      navigationTimeout = setTimeout(() => {
        const currentProjectId = sessionStorage.getItem('currentLoadingProjectId');
        // Use the full project data if we fetched it; otherwise fall back to the minimal object
        const projectData = loadedProjectData || { name: loadingFileName, details: '', photo: projectPhoto, mode: 'load-file', projectId: currentProjectId, timestamp: Date.now() };
        navigateToEditor(projectData);
        sessionStorage.removeItem('currentLoadingProjectId');
        setTimeout(() => { 
          setShowLoadingOverlay(false); 
          setProjectName(''); 
          setProjectDetails(''); 
          setPromptValue(''); 
          setProjectPhoto(null); 
          setTempPhoto(null); 
          setIsLoadingActive(false); 
          setLoadingProgress(0); 
          setLoadingFileName(''); 
          setLoadingMode('');
          setLoadedProjectData(null); // reset for next load
        }, 100);
      }, 200);
    }
    return () => { if (navigationTimeout) clearTimeout(navigationTimeout); };
  }, [isLoadingActive, loadingProgress, loadingMode, loadingFileName, projectPhoto, navigateToEditor, loadedProjectData]);

  useEffect(() => {
    let navigationTimeout;
    if (isLoadingActive && loadingProgress === 100 && loadingMode !== 'load-file') {
      navigationTimeout = setTimeout(() => {
        const finalPhoto = projectPhoto || tempPhoto;
        const projectData = { name: projectName, details: projectDetails || '', photo: finalPhoto, mode: loadingMode, timestamp: Date.now(), type: projectType };
        navigateToEditor(projectData);
        setTimeout(() => { setShowLoadingOverlay(false); setProjectName(''); setProjectDetails(''); setPromptValue(''); setProjectPhoto(null); setTempPhoto(null); setIsLoadingActive(false); setLoadingProgress(0); setShowPhotoStep(false); setProjectCreationStep('name'); setCameraModeStep('photo'); setProjectType('Personal'); }, 100);
      }, 1500);
    }
    return () => { if (navigationTimeout) clearTimeout(navigationTimeout); };
  }, [isLoadingActive, loadingProgress, projectName, projectDetails, projectPhoto, tempPhoto, loadingMode, navigateToEditor, projectType]);

  // ============================================
  // USE EFFECTS - VIDEO AUTOPLAY
  // ============================================
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video && tabs[index]?.video) {
        video.loop = true;
        video.muted = true;
        video.play().catch(error => console.log('Video autoplay failed:', error));
      }
    });
  }, [tabs]);

  // ============================================
  // USE EFFECTS - USAGE SYNC FROM FIRESTORE
  // ============================================
  useEffect(() => { if (authUser && userData) updateRemainingCounts(); }, [authUser, userData, updateRemainingCounts]);
  
  // Monitor credit usage – show low‑credit warning only (no “no credits” toast)
  useEffect(() => {
    if (!authUser || !userData) return;

    const lowThreshold = 10; // show warning when less than 10 iterations left

    // Low credit warning (toast)
    if (imagesRemaining <= lowThreshold && imagesRemaining > 0 && !hasShownLowCreditWarning) {
      showToast(`You have ${imagesRemaining} iterations left. Consider adding balance.`, 'warning', 5000);
      setHasShownLowCreditWarning(true);
    }

    // Reset flags when credits go above thresholds
    if (imagesRemaining > lowThreshold) {
      setHasShownLowCreditWarning(false);
    }
  }, [imagesRemaining, authUser, userData, showToast, hasShownLowCreditWarning]);

  useEffect(() => {
    if (!showLoadingOverlay) {
      setHasPhotoPermission(false);
    }
  }, [showLoadingOverlay]);


  useEffect(() => {
    if (!authUser) return;
    const unsubscribe = onSnapshot(doc(db, 'users', authUser.uid), (docSnap) => {
      if (docSnap.exists()) setUserData(docSnap.data());
    });
    return () => unsubscribe();
  }, [authUser]);

  // ============================================
  // USE EFFECTS - FETCH PROJECTS ON AUTH CHANGE
  // ============================================
  useEffect(() => {
    if (authUser) fetchUserProjects();
    else { setUserProjects([]); setProjectsLoading(false); }
  }, [authUser, fetchUserProjects]);

  // ============================================
  // USE EFFECTS - REAL-TIME PROJECTS SUBSCRIPTION
  // ============================================
  useEffect(() => {
    if (!authUser) return;
    const unsubscribe = projectService.subscribeToUserProjects(authUser.uid, (projects) => {
      setUserProjects(projects);
      setProjectsLoading(false);
    });
    return () => unsubscribe();
  }, [authUser]);

  // ============================================
  // USE EFFECTS - GRID ANIMATION (OPTIMIZED DOM)
  // ============================================
  useEffect(() => {
    let lastTime = 0;
    const scrollSpeed = 8;
    const gridSize = 80;
    const baseTransforms = ['', 'skewX(-15deg)', 'perspective(500px) rotateX(15deg)'];

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = Math.min((time - lastTime) / 1000, 0.1);
      lastTime = time;
      gridScrollRef.current = {
        x: (gridScrollRef.current.x + scrollSpeed * delta) % gridSize,
        y: (gridScrollRef.current.y + scrollSpeed * delta) % gridSize
      };
      const allGrids = document.querySelectorAll('.grid-overlay-container > div, .desktop-grid-overlay > div, .top-bar-grid-overlay > div');
      allGrids.forEach((grid, i) => {
        const styleIndex = i % 3;
        const tx = -gridScrollRef.current.x;
        const ty = -gridScrollRef.current.y;
        const base = baseTransforms[styleIndex];
        grid.style.transform = base ? `${base} translate(${tx}px, ${ty}px)` : `translate(${tx}px, ${ty}px)`;
      });
      rafRef.current = requestAnimationFrame(animateScroll);
    };
    rafRef.current = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      gridScrollRef.current = { x: 0, y: 0 };
      setActiveGrid((prev) => (prev + 1) % 3);
    }, 4000);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearInterval(interval);
    };
  }, []);

  // ============================================
  // USE EFFECTS - SCROLL EVENT LISTENERS
  // ============================================
  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;
    let ticking = false;
    let lastPosition = 0;

    const updateIndicator = () => {
      const scrollLeft = gallery.scrollLeft;
      lastPosition = scrollLeft;
      handleScroll();
      ticking = false;
    };

    const smoothScrollHandler = () => {
      if (!ticking) { requestAnimationFrame(updateIndicator); ticking = true; }
    };

    const handleScrollEnd = () => setTimeout(handleScroll, 150);

    gallery.addEventListener('scroll', smoothScrollHandler, { passive: true, capture: true });
    gallery.addEventListener('touchmove', smoothScrollHandler, { passive: true, capture: true });
    gallery.addEventListener('touchend', handleScrollEnd, { passive: true });
    gallery.addEventListener('scrollend', handleScrollEnd, { passive: true });
    handleScroll();

    return () => {
      gallery.removeEventListener('scroll', smoothScrollHandler, { capture: true });
      gallery.removeEventListener('touchmove', smoothScrollHandler, { capture: true });
      gallery.removeEventListener('touchend', handleScrollEnd);
      gallery.removeEventListener('scrollend', handleScrollEnd);
    };
  }, []);

  // ============================================
  // RENDER JSX
  // ============================================
  return (
    <>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body {
          background: #2D2D2D; overscroll-behavior: none; overscroll-behavior-y: none; overscroll-behavior-x: none;
          transition: background-color 0.5s ease; height: 100%; width: 100%; margin: 0; padding: 0;
          position: fixed; overflow: hidden; touch-action: pan-x pan-y;
        }
        @supports (-webkit-touch-callout: none) { body { -webkit-overflow-scrolling: touch; } }
        input, textarea, [contenteditable="true"] { -webkit-user-select: text !important; user-select: text !important; }
        input:focus { outline: none; }
        @supports (-webkit-touch-callout: none) {
          input { font-size: 16px !important; }
          .project-name-container input, .project-name-input { background-color: transparent !important; -webkit-background-clip: text !important; background-clip: text !important; }
        }
        input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #f5f5f5 inset !important; -webkit-text-fill-color: #333 !important; transition: background-color 50000s ease-in-out 0s;
        }
        .dashboard-container {
          width: 100%; min-height: 100vh; min-height: 100dvh; font-family: 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          overflow: hidden; overflow-y: hidden; background: #2D2D2D; position: relative; margin: 0; padding: 0;
          touch-action: pan-x pan-y; overscroll-behavior: none;
        }
        .dashboard-container.browser-mode {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0; overflow: hidden; width: 100%;
          height: -webkit-fill-available; touch-action: pan-x pan-y; overscroll-behavior: none;
        }
        .dashboard-container.catalog-scrollable { overflow: visible; }
        .desktop-tablet-layout { width: 100vw; height: 100vh; display: flex; background: #2D2D2D; position: relative; overflow: hidden; }
        .desktop-grid-overlay {
          position: absolute; top: 0; left: 0; right: 0; bottom: 0; width: 200%; height: 130%;
          mask-image: linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%);
          -webkit-mask-image: linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%);
          pointer-events: none; z-index: 1;
        }
        .desktop-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 5; padding: 25px; }
        .desktop-dashboard-card {
          border: 1.5px solid #626262ff; border-radius: 24px; flex: 1; display: flex; flex-direction: column; overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08); position: relative; background: #fafafa;
        }
        .desktop-top-bar {
          padding: 15px 20px 35px 20px; display: flex; align-items: center; justify-content: flex-start;
          background: #2D2D2D; position: relative; overflow: visible; border-radius: 24px 24px 0 0;
          flex-wrap: nowrap; gap: 10px; min-height: 95px;
        }
        .desktop-top-bar .top-bar-grid-overlay {
          position: absolute; top: 0; left: 0; right: 0; bottom: 0; width: 200%; height: 103%;
          mask-image: linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%);
          -webkit-mask-image: linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%);
          pointer-events: none; z-index: 1; overflow: visible;
        }
        .desktop-top-bar > * { position: relative; z-index: 2; }
        .top-bar-left { display: flex; align-items: center; gap: 12px; flex-wrap: nowrap; flex: 0 0 auto; position: relative; }
        .desktop-logo { display: flex; align-items: center; gap: 10px; }
        .desktop-logo-img { width: clamp(24px, 3vw, 35px); height: clamp(24px, 3vw, 35px); object-fit: contain; flex-shrink: 0; }
        .greeting-section { display: flex; flex-direction: column; flex-shrink: 0; width: auto; max-width: 160px; position: relative; }
        .greeting-section h1 { font-family: 'Avenir', sans-serif; font-weight: 500; font-size: clamp(0.85rem, 1.5vw, 1.15rem); color: white; margin-bottom: 2px; white-space: nowrap; }
        .greeting-section p { font-family: 'Avenir', sans-serif; font-weight: 500; font-size: clamp(1.2rem, 2.5vw, 1.775rem); color: white; margin-top: -5px; white-space: nowrap; }
        .top-bar-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; flex-shrink: 0; margin-left: auto; }
        .search-container { display: flex; align-items: center; background: #595959; border-radius: 30px; padding: 6px 12px; gap: 6px; height: 36px; }
        .search-container svg { color: rgba(255, 255, 255, 0.7); flex-shrink: 0; }
        .search-container input { border: none; background: none; outline: none; font-family: 'Roboto Mono', sans-serif; font-size: clamp(0.7rem, 1vw, 0.9rem); width: clamp(120px, 15vw, 200px); color: white; min-width: 80px; }
        .search-container input::placeholder { color: rgba(255, 255, 255, 0.7); }
        .settings-button-desktop { background: #595959; border: none; border-radius: 50%; width: clamp(36px, 4vw, 44px); height: clamp(36px, 4vw, 44px); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s ease; color: white; padding: 0; flex-shrink: 0; }
        .settings-button-desktop svg { width: clamp(16px, 1.8vw, 20px); height: clamp(16px, 1.8vw, 20px); }
        .settings-button-desktop:hover { background: #4a4a4a; }
        .signout-button-desktop { background: #595959; border: none; border-radius: 50%; width: clamp(36px, 4vw, 44px); height: clamp(36px, 4vw, 44px); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s ease; color: white; padding: 0; flex-shrink: 0; }
        .signout-button-desktop svg { width: clamp(16px, 1.8vw, 20px); height: clamp(16px, 1.8vw, 20px); }
        .signout-button-desktop:hover { background: #cc0000; }
        .prompt-container-desktop { display: flex; background: #595959; border-radius: 30px; overflow: hidden; margin-right: 12px; height: 36px; }
        .prompt-input-desktop { background: transparent; border: none; outline: none; padding: 6px 14px; color: white; font-family: 'Roboto Mono', sans-serif; font-size: clamp(0.7rem, 1vw, 14px); width: clamp(140px, 18vw, 260px); min-width: 100px; }
        .prompt-input-desktop::placeholder { color: rgba(255, 255, 255, 0.7); }
        .prompt-button-desktop { background: #4a4a4a; border: none; padding: 8px 14px; cursor: pointer; color: white; transition: background 0.2s ease; flex-shrink: 0; }
        .prompt-button-desktop:hover { background: #404040; }
        .desktop-avatar { width: clamp(32px, 3.5vw, 40px); height: clamp(32px, 3.5vw, 40px); border-radius: 50%; border: 2px solid #4594F9; background: #595959; display: flex; align-items: center; justify-content: center; overflow: hidden; cursor: pointer; flex-shrink: 0; margin-right: 12px; }
        .desktop-avatar svg { color: white; }
        .desktop-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .nav-toggle-wrapper { position: relative; width: 100%; display: flex; justify-content: center; margin-top: -5px; z-index: 101; }
        .nav-toggle-button { background: #3d3d3dff; border: none; padding: 10px 24px; border-radius: 16px 16px 0 0; cursor: pointer; display: flex; align-items: center; gap: 8px; color: #ffffffff; font-family: 'Avenir', sans-serif; font-weight: 500; font-size: 0.8rem; transition: all 0.2s ease; border: 1.5px solid #626262ff; border-bottom: none; }
        .nav-toggle-button:hover { background: #3D3D3D; color: white; }
        .nav-toggle-button svg { width: 14px; height: 14px; }
        .nav-menu-container { background: #2D2D2D; border-bottom: 1px solid #444; overflow-x: auto; overflow-y: hidden; max-height: 100px; }
        .nav-menu-container::-webkit-scrollbar { height: 4px; }
        .nav-menu-container::-webkit-scrollbar-track { background: transparent; }
        .nav-menu-container::-webkit-scrollbar-thumb { background: #555; border-radius: 2px; }
        .nav-menu-content { padding: 12px 20px; display: flex; gap: 6px; flex-wrap: nowrap; width: max-content; min-width: 100%; }
        .nav-menu-item { padding: 8px 16px; border: none; background: #444; border-radius: 30px; cursor: pointer; transition: all 0.2s ease; color: rgba(255, 255, 255, 0.8); font-family: 'Avenir', sans-serif; font-size: clamp(0.7rem, 1vw, 0.95rem); font-weight: 500; white-space: nowrap; flex-shrink: 0; }
        .nav-menu-item:hover { background: #555; color: white; }
        .nav-menu-item.active { background: #88b7ff; color: white; }
        .nav-menu-item.disabled { color: #777; cursor: not-allowed; opacity: 0.6; }
        .nav-menu-item.disabled:hover { background: #444; }
        .scrollable-content-area { flex: 1; overflow-y: auto; overflow-x: hidden; position: relative; }
        .split-section { padding: 24px 48px 24px 32px; background: #fafafa; border-bottom: 1px solid #eaeaea; display: flex; gap: 24px; transition: max-height 0.25s ease, opacity 0.2s ease, padding 0.25s ease, margin 0.25s ease; position: relative; z-index: 1; overflow: hidden; }
        .split-section.catalog-active { margin-top: 0; opacity: 0; pointer-events: none; max-height: 0; padding-top: 0; padding-bottom: 0; border-bottom: none; margin-bottom: 0; transition: max-height 0.2s ease, opacity 0.15s ease, padding 0.2s ease, margin 0.2s ease; }
        .split-left { flex: 0 0 70%; overflow: hidden; }
        .split-right { flex: 0 0 30%; background: #f0f0f0; border-radius: 16px; overflow: hidden; display: flex; flex-direction: column; max-height: 320px; }
        .asset-library-header { display: flex; align-items: center; justify-content: space-between; padding: 16px; background: #f0f0f0; border-bottom: 1px solid #ddd; flex-shrink: 0; position: sticky; top: 0; z-index: 1; }
        .asset-library-header h3 { font-family: 'Avenir', sans-serif; font-weight: 600; font-size: 1rem; color: #333; }
        .asset-upload-btn { background: #88b7ff; border: none; border-radius: 20px; padding: 6px 12px; color: white; font-family: 'Avenir', sans-serif; font-size: 0.8rem; cursor: pointer; display: flex; align-items: center; gap: 4px; }
        .asset-grid-scrollable { flex: 1; overflow-y: auto; padding: 16px; }
        .asset-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
        .asset-item { aspect-ratio: 4 / 5; background: #ddd; border-radius: 12px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s ease; border: 1px solid #ccc; }
        .asset-item:hover { border-color: #88b7ff; background: #e8e8e8; }
        .asset-item svg { color: #666; }
        .section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
        .section-header h2 { font-family: 'Avenir', sans-serif; font-weight: 600; font-size: 1.2rem; color: #333; letter-spacing: -0.3px; }
        .horizontal-gallery { overflow-x: auto; overflow-y: hidden; padding-bottom: 8px; }
        .horizontal-gallery::-webkit-scrollbar { height: 6px; }
        .horizontal-gallery::-webkit-scrollbar-track { background: #e0e0e0; border-radius: 3px; }
        .horizontal-gallery::-webkit-scrollbar-thumb { background: #88b7ff; border-radius: 3px; }
        .horizontal-gallery-track { display: flex; gap: 16px; width: max-content; }
        .gallery-card-horizontal { flex-shrink: 0; width: 160px; background: white; border-radius: 16px; overflow: hidden; cursor: pointer; transition: all 0.2s ease; border: 1px solid #eaeaea; }
        .gallery-card-horizontal:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); border-color: #88b7ff; }
        .gallery-card-media-horizontal { width: 100%; height: 200px; background: #CECECE; position: relative; overflow: hidden; }
        .gallery-card-media-horizontal video, .gallery-card-media-horizontal img { width: 100%; height: 100%; object-fit: cover; }
        .gallery-card-media-horizontal.new-project-card { display: flex; align-items: center; justify-content: center; background: #e8e8e8; }
        .gallery-card-label-horizontal { padding: 12px; text-align: center; font-family: 'Avenir', sans-serif; font-weight: 500; color: #333; font-size: 0.85rem; }
        .card-badge { position: absolute; top: 12px; right: 12px; background: none; padding: 4px 10px; border-radius: 20px; color: #fff; font-size: 0.7rem; font-family: 'Avenir', sans-serif; font-weight: 500; line-height: 1; display: flex; align-items: center; gap: 4px; z-index: 5; }
        .feed-section { padding: 24px 32px 40px 32px; background: white; flex: 1; overflow-y: auto; transition: opacity 0.2s ease, transform 0.25s ease; position: relative; z-index: 2; display: flex; flex-direction: column; min-height: 0; opacity: 1; transform: translateY(0); }
        .feed-section.catalog-active { opacity: 1; transform: translateY(0); z-index: 10; overflow-y: auto; overflow-x: hidden; transition: opacity 0.2s ease, transform 0.25s ease; display: flex; flex-direction: column; }
        .feed-section.catalog-inactive { opacity: 0; pointer-events: none; position: absolute; transform: translateY(8px); transition: opacity 0.15s ease, transform 0.2s ease; }
        .feed-section.expanded { position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 50; padding: 24px 32px 40px 32px; border-radius: 0; transform: none; }
        .feed-section::-webkit-scrollbar { width: 6px; }
        .feed-section::-webkit-scrollbar-track { background: #e0e0e0; border-radius: 3px; }
        .feed-section::-webkit-scrollbar-thumb { background: #88b7ff; border-radius: 3px; }
        .asset-library-back-btn { background: rgba(217, 217, 217, 1); color: #ffffff; padding: 6px 20px; border-radius: 20px; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 0.8rem; display: flex; align-items: center; gap: 6px; border: none; transition: all 0.2s ease; }
        .asset-library-back-btn:hover { background: #c0c0c0; }
        .catalog-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0px; }
        .catalog-header h2 { font-family: 'Avenir', sans-serif; font-weight: 600; font-size: 1.6rem; color: #333; letter-spacing: -0.3px; }
        .catalog-header-actions { display: flex; gap: 8px; align-items: center; }
        .catalog-search-form { display: flex; gap: 8px; align-items: center; }
        .catalog-search-input { padding: 8px 12px; border: 1px solid #ddd; border-radius: 20px; font-family: 'Avenir', sans-serif; font-size: 0.85rem; outline: none; width: 180px; }
        .catalog-search-input:focus { border-color: #88b7ff; }
        .catalog-expand-btn { background: white; border: 1px solid #e0e0e0; border-radius: 50%; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s ease; color: #666; padding: 0; }
        .catalog-expand-btn svg { width: 22px; height: 22px; }
        .catalog-expand-btn:hover { background: #f0f0f0; border-color: #88b7ff; color: #88b7ff; }
        .feed-gallery { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; align-content: start; grid-auto-rows: auto; }
        .feed-gallery-item { border-radius: 12px; overflow: hidden; cursor: pointer; position: relative; background: #e0e0e0; aspect-ratio: 4/3; width: 100%; min-height: 0; }
        .feed-gallery-item img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s ease; display: block; }
        .feed-gallery-item:hover img { transform: scale(1.05); }
        .feed-gallery-item:hover { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); }
        .style-guide-hero { position: relative; border-radius: 16px; overflow: hidden; background: #f5f5f5; }
        .style-guide-card { border-radius: 12px; overflow: hidden; cursor: pointer; transition: all 0.2s ease; background: white; }
        .style-guide-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
        .style-guide-button { background: #88b7ff; color: white; border: none; padding: 8px 16px; border-radius: 20px; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 0.85rem; font-weight: 500; display: flex; align-items: center; gap: 6px; transition: background 0.2s ease; }
        .style-guide-button:hover { background: #5998ff; }
        .style-guide-carousel-btn { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(255,255,255,0.9); border: none; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #333; z-index: 5; transition: all 0.2s ease; }
        .style-guide-carousel-btn:hover { background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
        .style-guide-dot { width: 8px; height: 8px; border-radius: 4px; border: none; cursor: pointer; transition: all 0.3s ease; padding: 0; }
        .style-guide-dot.active { width: 24px; background: #88b7ff; }
        .style-guide-dot.inactive { background: rgba(255,255,255,0.5); }
        .style-guide-dot.inactive:hover { background: rgba(255,255,255,0.8); }
        .photo-details-container { display: flex; gap: 0; height: 100%; min-height: 500px; flex: 1; }
        .photo-details-left { flex: 1; display: flex; align-items: center; justify-content: center; background: #f5f5f5; padding: 20px; }
        .photo-details-left img { max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 8px; }
        .photo-details-right { width: 350px; border-left: 1px solid #e0e0e0; background: white; display: flex; flex-direction: column; overflow-y: auto; }
        .photo-details-sidebar-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid #eee; }
        .photo-details-sidebar-header h3 { font-family: 'Avenir', sans-serif; font-weight: 600; font-size: 1.1rem; color: #333; }
        .photo-details-close-btn { background: none; border: none; cursor: pointer; color: #666; padding: 4px; border-radius: 50%; transition: background 0.2s ease; }
        .photo-details-close-btn:hover { background: #f0f0f0; }
        .photo-details-sidebar-content { flex: 1; padding: 20px; display: flex; flex-direction: column; gap: 24px; }
        .sidebar-info-section { display: flex; flex-direction: column; gap: 12px; }
        .sidebar-info-section h4 { font-family: 'Avenir', sans-serif; font-weight: 600; font-size: 0.95rem; color: #333; margin-bottom: 4px; }
        .sidebar-info-item { display: flex; flex-direction: column; gap: 2px; }
        .sidebar-info-label { font-family: 'Avenir', sans-serif; font-size: 0.8rem; color: #888; font-weight: 500; }
        .sidebar-info-value { font-family: 'Avenir', sans-serif; font-size: 0.9rem; color: #333; }
        .sidebar-info-value a { color: #88b7ff; text-decoration: none; }
        .sidebar-info-value a:hover { text-decoration: underline; }
        .sidebar-action-buttons { display: flex; flex-direction: column; gap: 10px; margin-top: auto; padding-top: 20px; border-top: 1px solid #eee; }
        .sidebar-action-btn { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border: 1px solid #e0e0e0; border-radius: 12px; background: white; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 0.9rem; color: #333; transition: all 0.2s ease; width: 100%; }
        .sidebar-action-btn:hover { background: #f5f5f5; border-color: #88b7ff; }
        .sidebar-action-btn.primary { background: #88b7ff; color: white; border-color: #88b7ff; }
        .sidebar-action-btn.primary:hover { background: #5998ff; }
        .load-more-btn { display: block; margin: 20px auto 0; padding: 10px 24px; background: #88b7ff; color: white; border: none; border-radius: 20px; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 0.9rem; transition: background 0.2s ease; }
        .load-more-btn:hover { background: #5998ff; }
        .load-more-btn:disabled { background: #ccc; cursor: not-allowed; }
        .desktop-profile-view { padding: 24px 32px 40px 32px; background: #fafafa; flex: 1; overflow-y: auto; display: flex; flex-direction: column; }
        .profile-view-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
        .profile-view-header h2 { font-family: 'Avenir', sans-serif; font-weight: 600; font-size: 1.3rem; color: #333; }
        .profile-close-btn { background: none; border: none; color: #666; cursor: pointer; padding: 8px; border-radius: 50%; transition: background 0.2s ease; }
        .profile-close-btn:hover { background: #e0e0e0; }
        .profile-view-content { display: flex; flex-direction: column; align-items: center; gap: 24px; max-width: 400px; margin: 0 auto; width: 100%; }
        .profile-view-avatar-section { display: flex; flex-direction: column; align-items: center; gap: 16px; }
        .profile-view-avatar-large { width: 120px; height: 120px; border-radius: 50%; border: 4px solid #4594F9; background: #595959; display: flex; align-items: center; justify-content: center; overflow: hidden; cursor: pointer; }
        .profile-view-avatar-image { width: 100%; height: 100%; object-fit: cover; }
        .profile-view-change-avatar { background: #88b7ff; border: none; color: white; padding: 8px 20px; border-radius: 20px; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 0.9rem; }
        .profile-view-info { width: 100%; background: white; border-radius: 16px; padding: 20px; }
        .profile-view-info-item { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid #eee; }
        .profile-view-info-item:last-child { border-bottom: none; }
        .profile-view-label { font-family: 'Avenir', sans-serif; font-weight: 500; color: #666; }
        .profile-view-value { font-family: 'Avenir', sans-serif; color: #333; }
        .profile-view-actions { display: flex; flex-direction: column; gap: 12px; width: 100%; }
                .profile-view-action-btn { padding: 14px 20px; background: white; border: 1px solid #ddd; border-radius: 12px; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 1rem; color: #333; transition: all 0.2s ease; text-align: left; }
        .profile-view-action-btn:hover { background: #f5f5f5; border-color: #88b7ff; }
        .profile-view-signout-btn { padding: 14px 20px; background: transparent; border: 1px solid #ff6b6b; border-radius: 12px; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 1rem; color: #ff6b6b; transition: all 0.2s ease; text-align: center; margin-top: 20px; }
        .profile-view-signout-btn:hover { background: #ff6b6b; color: white; }
        .mobile-dashboard {
          width: 100%; height: 100vh; height: 100dvh; max-height: 100vh; max-height: 100dvh;
          display: flex; flex-direction: column; background: #2D2D2D; overflow-x: hidden; overflow-y: hidden;
          position: relative; transform: translateX(0) scale(1);
          transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), background-color 0.5s ease;
          overscroll-behavior: none; touch-action: none;
          padding-top: env(safe-area-inset-top, 47px); padding-bottom: env(safe-area-inset-bottom, 34px);
          padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right);
        }
        .mobile-dashboard.browser-mode { position: fixed; top: 0; left: 0; right: 0; bottom: 0; overflow: visible; }
        .mobile-dashboard.catalog-scrollable { overflow-y: auto; overflow-x: hidden; touch-action: auto; }
        .top-banner-dashboard-div {
          background-color: #2D2D2D; padding: 16px 16px 12px 16px; height: 170px; position: relative;
          width: 100%; overflow: hidden; flex-shrink: 0; padding-top: calc(env(safe-area-inset-top, 47px) + 6px);
          transition: background-color 0.5s ease;
        }
        .grid-overlay-container {
          position: absolute; top: 0; left: 0; right: 0; bottom: 0; width: 200%; height: 100%;
          mask-image: linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%);
          -webkit-mask-image: linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%);
          pointer-events: none; z-index: 1;
        }
        .banner-content { display: flex; justify-content: space-between; align-items: flex-start; width: 100%; position: relative; z-index: 2; }
        .left-column { flex: 1; }
        .dashboard-title { font-family: 'Avenir', sans-serif; font-weight: 500; font-size: 1.4rem; padding-top: 14px; color: white; padding-left: 10px; margin: 0 0 2px 0; }
        .greeting { font-family: 'Avenir', sans-serif; font-weight: 500; font-size: 1.775rem; padding-top: 0px; padding-left: 10px; color: white; margin-top: -2px; margin-bottom: 0; }
        .prompt-container { margin-top: 15px; margin-left: 10px; width: 100%; max-width: 260px; position: relative; }
        .prompt-input-wrapper { display: flex; background-color: #595959; border-radius: 30px; overflow: visible; position: relative; }
        .prompt-input {
          flex: 1; background: transparent; border: none; outline: none; padding: 8px 12px;
          color: white; font-family: 'Roboto Mono', sans-serif; font-size: 16px;
          transform: scale(0.75) !important; transform-origin: left;
          margin-top: -4px; margin-bottom: -3px !important; margin-left: 4px; line-height: 20px;
          text-align: center; transition: text-align 0.2s ease;
        }
        .prompt-input::placeholder { color: rgba(255, 255, 255, 0.7); text-align: center; }
        .prompt-input:focus::placeholder { color: transparent; text-align: center !important; }
        .prompt-input:not(:placeholder-shown) { text-align: center; }
        .prompt-input, .project-name-input { font-size: 16px; transform: scale(0.85); transform-origin: left; margin-top: -4px; margin-bottom: -4px; margin-left: 4px; line-height: 20px; position: relative; z-index: 1; }
        @media screen and (max-width: 767px) { .dashboard-container { height: 100vh; height: 100dvh; overflow: hidden; } }
        .prompt-button { background-color: #4a4a4a; border: none; border-radius: 0 30px 30px 0; padding: 8px 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; min-width: 48px; transition: background-color 0.2s ease; flex-shrink: 0; }
        .prompt-button:hover { background-color: #404040; }
        .prompt-button:active { background-color: #363636; }
        .arrow-icon { color: white; font-size: 0.8rem; }
        .right-column { display: flex; flex-direction: column; align-items: flex-end; gap: 15px; margin-right: 10px; margin-top: 17px; position: relative; }
        .logo-icon { color: white; }
        .logo-image { width: 35px; height: 35px; padding-right: 5px; object-fit: contain; }
        .avatar-circle { width: 40px; height: 40px; border-radius: 50%; border: 2px solid #4594F9; background: #595959; display: flex; align-items: center; justify-content: center; overflow: hidden; cursor: pointer; transition: all 0.2s ease; flex-shrink: 0; }
        .avatar-circle:hover { border-color: #5998ff; }
        .avatar-placeholder { width: 100%; height: 100%; border-radius: 50%; background: #777; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .avatar-image { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
        .avatar-dropdown { position: absolute; top: 50px; right: 0; background: white; border-radius: 12px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15); z-index: 1000; min-width: 150px; overflow: hidden; }
        .dropdown-item { padding: 12px 16px; border: none; background: none; width: 100%; text-align: left; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 0.9rem; color: #333; transition: background-color 0.2s ease; }
        .dropdown-item:hover { background-color: #f5f5f5; }
        .dropdown-item:first-child { border-bottom: 1px solid #f0f0f0; }
        .settings-button { position: absolute; top: 19px; left: 150px; background: #616263ff; border: 2px solid #5f5f5fff; padding: 0; border-radius: 50%; cursor: pointer; transition: background-color 0.2s; color: white; z-index: 3; display: flex; align-items: center; justify-content: center; width: 22px; height: 22px; line-height: 1; }
        .settings-button svg { width: 12px; height: 12px; }
        .settings-button:hover { background-color: #5998ff; }
        .profile-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100vh; height: calc(var(--vh, 1vh) * 100); background: #fafafa; z-index: 1002; display: flex; flex-direction: column; padding: 0; overflow-y: auto; }
.profile-content { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 20px; margin-top:-30px; }
        .profile-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
        .profile-back-button { background: none; border: none; color: white; font-size: 1.2rem; cursor: pointer; padding: 8px; }
        .profile-title { font-family: 'Avenir', sans-serif; font-size: 1.2rem; color: white; }
        .profile-save-button { background: #88b7ff; border: none; color: white; padding: 8px 16px; border-radius: 20px; cursor: pointer; font-family: 'Avenir', sans-serif; }
        .profile-content { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 20px; }
        .profile-avatar-section { display: flex; flex-direction: column; align-items: center; gap: 15px; }
        .profile-avatar-large { width: 100px; height: 100px; border-radius: 50%; border: 3px solid #88b7ff; background: #595959; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .profile-avatar-image { width: 100%; height: 100%; object-fit: cover; }
        .change-avatar-button { background: #88b7ff; border: none; color: white; padding: 10px 20px; border-radius: 20px; cursor: pointer; font-family: 'Avenir', sans-serif; }
        .profile-name-section { width: 100%; max-width: 300px; }
        .profile-name-label { color: white; font-family: 'Avenir', sans-serif; margin-bottom: 10px; font-size: 1rem; }
        .profile-name-input-wrapper { display: flex; background-color: #595959; border-radius: 30px; overflow: hidden; position: relative; }
        .profile-name-input { flex: 1; background: transparent; border: none; outline: none; padding: 12px 16px; color: white; font-family: 'Avenir', sans-serif; font-size: 1rem; }
        .profile-name-input::placeholder { color: rgba(255, 255, 255, 0.7); }
        .profile-edit-button { background: none; border: none; color: white; padding: 12px 16px; cursor: pointer; }
        .profile-email-display { width: 100%; max-width: 300px; background: #3d3d3d; padding: 12px 16px; border-radius: 30px; color: white; font-family: 'Avenir', sans-serif; font-size: 0.9rem; text-align: center; }
        .profile-action-buttons { width: 100%; max-width: 300px; display: flex; flex-direction: column; gap: 12px; margin-top: 10px; }
        .profile-action-btn { background: #444; border: none; color: white; padding: 14px 20px; border-radius: 12px; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 1rem; text-align: left; transition: background 0.2s ease; }
        @media (max-width: 767px) {
          .profile-action-btn { padding: 10px 16px; font-size: 0.9rem; }
        }
        .profile-action-btn:hover { background: #555; }
        .profile-signout-btn { background: transparent; border: 1px solid #ff6b6b; color: #ff6b6b; padding: 14px 20px; border-radius: 12px; cursor: pointer; font-family: 'Avenir', sans-serif; font-size: 1rem; text-align: center; margin-top: 20px; transition: all 0.2s ease; }
        .profile-signout-btn:hover { background: #ff6b6b; color: white; }
        .middle-content-gallery-div { background-color: #f7f7f7; padding: 20px 16px; overscroll-behavior: none; overflow-x: auto; overflow-y: hidden; height: 280px; width: 100%; position: relative; flex-shrink: 0; }
        .middle-content-gallery-div::-webkit-scrollbar { display: none; }
        .middle-content-gallery-div { -ms-overflow-style: none; scrollbar-width: none; }
        .gallery-scroll-container { display: flex; gap: 16px; padding: 0 4px; height: 179px; width: max-content; min-width: 100%; }
        .gallery-item { display: flex; flex-direction: column; align-items: center; gap: 12px; flex-shrink: 0; }
        .gallery-tab { background-color: #CECECE; border-radius: 16px; width: 120px; height: 179px; display: flex; flex-direction: column; align-items: center; justify-content: center; position: relative; cursor: pointer; transition: all 0.3s ease; flex-shrink: 0; overflow: hidden; }
        .gallery-tab::before { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(136, 183, 255, 0.8) 90%, rgba(136, 183, 255, 0.9) 95%, rgba(136, 183, 255, 1) 100%); pointer-events: none; opacity: 0; transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
        .gallery-tab.new-project-tab::before { display: none; }
        .gallery-tab.new-project-tab:hover { background-color: #E0E0E0; }
        .gallery-tab.new-project-tab.selected { background-color: #D8D8D8; }
        .gallery-tab.saved-project-tab::before { display: none; }
        .gallery-tab.saved-project-tab:hover { background-color: #E0E0E0; }
        .gallery-tab.saved-project-tab.selected { background-color: #D8D8D8; }
        .gallery-tab:not(.new-project-tab):hover::before { opacity: 0.9; transition: all 0.3s ease; }
        .gallery-tab:not(.new-project-tab):hover { border: 3px solid #88b7ff; }
        .gallery-tab:not(.new-project-tab):not(.saved-project-tab).selected { background-color: #88b7ff; border: 3px solid #88b7ff; }
        .gallery-tab:not(.new-project-tab):not(.saved-project-tab).selected::before { opacity: 1; transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
        .tab-video { width: 100%; height: 100%; object-fit: cover; border-radius: 12px; position: relative; z-index: 1; }
        .tab-image { width: 100%; height: 100%; object-fit: cover; border-radius: 12px; position: relative; z-index: 1; }
        .tab-icon { margin-bottom: 0; }
        .question-mark-overlay { position: absolute; top: 12px; right: 12px; z-index: 10 !important; pointer-events: none; background: transparent; -webkit-transform: translateZ(0); transform: translateZ(0); -webkit-backface-visibility: hidden; backface-visibility: hidden; }
        .file-icon-overlay { position: absolute; top: 12px; right: 12px; z-index: 100 !important; pointer-events: none; color: white !important; }
        .file-icon-overlay svg { color: white !important; stroke: white !important; fill: white !important; }
        .tab-label { font-family: 'Avenir', sans-serif; color: #88b7ff; font-size: 0.875rem; font-weight: 500; text-align: center; }
        .mobile-scrollbar-container { position: absolute; bottom: 15px; left: 16px; right: 16px; height: 4px; background-color: #e5e5e5; border-radius: 2px; z-index: 20; pointer-events: none; }
        .mobile-scrollbar-thumb { position: absolute; height: 100%; background-color: #88b7ff; border-radius: 2px; left: 0; top: 0; min-width: 40px; will-change: transform; transition: none; }
        .bottom-menu-nav-div { background-color: white; padding: 24px 16px 32px; position: relative; min-height: 120px; width: 100%; flex: 1; display: flex; flex-direction: column; }
        .bottom-menu-nav-div::after { content: ''; position: absolute; top: 100%; left: 0; width: 100%; height: 50px; background-color: white; z-index: -1; }
        .menu-nav { display: flex; flex-direction: column; align-items: flex-start; gap: 1px; width: 100%; margin-bottom: 24px; padding: 0 16px; }
        .menu-nav.browser-mode { --browser-col-gap: 16px; --browser-row-gap: 4px; display: grid; grid-template-columns: auto auto; gap: var(--browser-row-gap) var(--browser-col-gap); align-items: start; justify-content: start; padding: 0 16px 0 12px; position: relative; }
        .menu-nav.browser-mode::after { content: ''; position: absolute; left: 39%; top: 0px; height: calc(100% - 30px); width: 1px; background: #f0f0f0ff; pointer-events: none; }
        .menu-item { font-family: 'Avenir', sans-serif; font-weight: 500; color: #88b7ff; background: none; border: none !important; padding: 0px 8px; border-radius: 12px; cursor: pointer; transition: all 0.2s; font-size: 1.6rem; letter-spacing: -0.7px; width: auto; text-align: left; border: 2px solid transparent; white-space: nowrap; }
        .menu-item.browser-mode { font-size: 1.30rem; letter-spacing: -0.5px; padding: 0px 4px; }
        .menu-item:last-of-type { font-size: 1.55rem; letter-spacing: -0.5px; }
        .menu-item:last-of-type.browser-mode { font-size: 1.35rem; }
        .menu-item:hover { background-color: #f3f3f3; border: none !important; }
        .menu-item.active { background-color: #f3f3f3; border: none !important; }
        .menu-item.disabled { color: #dbdbdb; cursor: not-allowed; opacity: 0.6; }
        .menu-item.disabled:hover { background: none; border-color: transparent; }
        .camera-action-container { position: fixed; right: 20px; bottom: 9%; transform: translateY(-50%); display: flex; flex-direction: column; align-items: center; z-index: 1000; }
        .camera-action-container.browser-mode { bottom: -5%; }
        .camera-action-button { background-color: #88b7ff; color: white; border: none; border-radius: 15px; padding: 50px 7px; cursor: pointer; display: flex; align-items: center; justify-content: center; min-height: 80px; min-width: 10px; writing-mode: vertical-lr; text-orientation: mixed; }
        .camera-action-button i { transform: rotate(90deg); font-size: 10px !important; }
        .camera-action-button:hover { background-color: #5998ff; }
        .mobile-home-button { position: fixed; bottom: 30px; left: 50%; transform: translateX(-50%); width: 56px; height: 56px; border-radius: 50%; background-color: white; border: 2px solid #88b7ff; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.3s ease; z-index: 999; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
        .mobile-home-button.browser-mode { bottom: 5px; width: 48px; height: 48px; }
        .mobile-home-button.browser-mode svg { width: 22px; height: 22px; }
        .mobile-home-button:hover { background-color: #f8f8f8; border-color: #5998ff; transform: translateX(-50%) scale(1.05); }
        .mobile-home-button:active { transform: translateX(-50%) scale(0.95); }
        .mobile-home-button svg { color: #88b7ff; width: 26px; height: 26px; }
        .loading-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100vh; height: calc(var(--vh, 1vh) * 100); background-color: rgba(0, 0, 0, 0.7); display: flex; flex-direction: column; justify-content: center; align-items: center; z-index: 1000; backdrop-filter: blur(20px) opacity(100); opacity: 0; transition: opacity 0.3s ease; }
        .loading-overlay.show { opacity: 1; }
        .loading-text { color: white; font-family: 'Avenir', sans-serif; font-size: 1.2rem; margin-bottom: 20px; text-align: center; }
        .loading-bar-container { width: 80%; max-width: 300px; height: 6px; background-color: #333; border-radius: 3px; overflow: hidden; }
        .loading-bar { height: 100%; background-color: #4594F9; border-radius: 3px; transition: width 0.3s ease; width: 0%; }
        .loading-percentage { color: white; font-family: 'Avenir', sans-serif; font-size: 1rem; margin-top: 10px; text-align: center; }
        .project-name-container { background: #ffffff; padding: 26px 20px 20px 20px; border-radius: 22px; width: 85%; max-width: 400px; text-align: center; position: relative; margin-top: 0; }
        .project-name-title { font-family: 'Avenir', sans-serif; text-align: center; font-weight: 500; font-size: 1.3rem; color: #ffffff; margin: 0 0 15px 0; padding: 0; width: 100%; position: relative; z-index: 1001; }
        .project-details-display { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; font-family: 'Roboto Mono', sans-serif; font-size: 0.75rem; color: #666; text-align: left; }
        .project-details-label { font-family: 'Roboto Mono', sans-serif; font-weight: bold; margin-bottom: 5px; color: #333; }
        .project-name-display { background: #f5f5f5; padding: 12px 16px; border-radius: 8px; margin-bottom: 15px; font-family: 'Avenir', sans-serif; font-size: 1.1rem; color: #333; text-align: center; font-weight: 500; }
        .project-name-input-wrapper { display: flex; background-color: #f5f5f5; border-radius: 30px; overflow: hidden; margin-bottom: 20px; }
        .project-name-input { flex: 1; background: transparent; border: none; outline: none; padding: 12px 16px; color: #333; font-family: 'Roboto Mono', sans-serif; font-size: 16px; transform: scale(0.85); transform-origin: left; margin-top: -4px; margin-bottom: -4px; margin-left: 4px; max-width: 100%; }
        .project-name-input::placeholder { color: #999; font-family: 'Roboto Mono', sans-serif; }
        .project-name-buttons { display: flex; gap: 10px; justify-content: center; }
        .project-name-button { background-color: #88b7ff; border: none; border-radius: 30px; padding: 12px 24px; cursor: pointer; display: flex; align-items: center; justify-content: center; min-width: 100px; transition: background-color 0.2s ease; font-family: 'Avenir', sans-serif; font-size: 0.9rem; color: white; }
        .project-name-button:hover { background-color: #5998ff; }
        .project-name-button:disabled { background-color: #ccc; cursor: not-allowed; }
        .cancel-button { background-color: #999; }
        .cancel-button:hover { background-color: #888; }
        .photo-thumbnail-container { width: 100%; display: flex; justify-content: center; margin-bottom: 20px; }
        .photo-placeholder-grey { width: 200px; height: 350px; background: #e0e0e0; border-radius: 12px; border: 2px solid #88b7ff; }
        .photo-thumbnail { width: 200px; height: 350px; object-fit: cover; border-radius: 12px; border: 2px solid #88b7ff; }
        .photo-selection-area { width: 100%; margin-bottom: 20px; }
        .empty-photo-area { width: 100%; height: 250px; background: #f5f5f5; border-radius: 16px; border: 2px dashed #ccc; display: flex; align-items: center; justify-content: center; }
        .photo-icons { display: flex; gap: 30px; }
        .photo-icon-btn { background: none; border: none; display: flex; flex-direction: column; align-items: center; gap: 8px; cursor: pointer; padding: 12px; border-radius: 12px; transition: all 0.2s ease; color: #666; }
        .photo-icon-btn:hover { background: #e8e8e8; }
        .photo-icon-btn span { font-size: 12px; font-family: 'Avenir', sans-serif; }
        .camera-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100vh; height: calc(var(--vh, 1vh) * 100); background: #000; z-index: 1001; display: flex; flex-direction: column; padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom); }
        .camera-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; background: #000; color: white; z-index: 10; }
        .camera-close { background: none; border: none; color: white; font-size: 1.5rem; cursor: pointer; padding: 8px; transition: all 0.1s ease; border-radius: 50%; }
        .camera-close:active { background: transparent !important; color: #d2d2d2 !important; transform: scale(0.9); }
        .camera-title { font-family: 'Avenir', sans-serif; font-size: 1.2rem; }
        .camera-preview { flex: 1; background: #000; display: flex; justify-content: center; align-items: center; position: relative; overflow: hidden; }
        .camera-live-preview { width: 100%; height: 100%; object-fit: cover; background: #000; transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
        .camera-live-preview.ultra-wide { transform: scale(0.7); }
        .camera-live-preview.zooming { transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
        .camera-placeholder { width: 100%; height: 100%; background: #343434ff; background-size: 50px 50px; display: flex; justify-content: center; align-items: center; color: white; font-family: 'Avenir', sans-serif; font-size: 1.2rem; }
        .camera-controls { display: flex; justify-content: space-between; align-items: center; padding: 40px 30px 80px 30px; background: #000000ff; position: relative; }
        .camera-gallery-button, .camera-shutter, .flash-button, .flash-placeholder { flex: 0 0 auto; width: 50px; height: 50px; display: flex; align-items: center; justify-content: center; }
        .camera-shutter { width: 70px; height: 70px; border-radius: 50%; background: white; border: 4px solid #f0f0f0; cursor: pointer; display: flex; justify-content: center; align-items: center; transition: all 0.1s ease; position: absolute; left: 50%; transform: translateX(-50%); }
        .camera-shutter:active, .camera-shutter.clicked { background: #d2d2d2 !important; border: 4px solid white !important; transform: translateX(-50%) scale(0.95); }
        .camera-shutter:active .shutter-icon, .camera-shutter.clicked .shutter-icon { color: #333; }
        .camera-shutter.in-selection-mode { background: white !important; border: 4px solid #f0f0f0 !important; transform: translateX(-50%) scale(0.8); opacity: 0.7; }
        .camera-gallery-button { background: none; border: none; color: white; font-size: 2rem; cursor: pointer; padding: 10px; transition: all 0.1s ease; border-radius: 50%; }
        .camera-gallery-button:active { background: rgba(255, 255, 255, 0.1) !important; color: #d2d2d2 !important; transform: scale(0.9); }
        .flash-button { background: none !important; border: none; color: white; cursor: pointer; padding: 10px; transition: all 0.1s ease; border-radius: 50%; }
        .flash-button:active { background: none !important; color: #d2d2d2 !important; transform: scale(0.9); }
        .flash-button.active { background: none !important; color: #FFEB3B !important; }
        .shutter-icon { color: #333; transition: color 0.1s ease; }
        .shutter-effect { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: white; opacity: 0; pointer-events: none; z-index: 20; }
        .shutter-effect.active { animation: shutterFlash 0.3s ease; }
        .confirm-button { background: rgba(46, 46, 46, 0.75) !important; color: white !important; border: 2px solid #88b7ff !important; padding: 12px 24px !important; border-radius: 18px !important; font-size: 16px !important; cursor: pointer !important; font-family: 'Avenir', sans-serif !important; }
        .confirm-button:active { background: #88b7ff !important; transform: scale(0.95) !important; }
        .decline-button { background: #2c2c2cff !important; color: white !important; border: none !important; padding: 12px 24px !important; border-radius: 18px !important; font-size: 16px !important; cursor: pointer !important; font-family: 'Avenir', sans-serif !important; }
        .decline-button:active { background: #1a1a1aff !important; border: 2px solid #88b7ff !important; transform: scale(0.95) !important; }
        .camera-overlay button:focus { outline: none !important; box-shadow: none !important; }
        .desktop-camera-fab { position: fixed; bottom: 55px; right: 55px; width: 60px; height: 60px; border-radius: 50%; background: #88b7ff; border: none; box-shadow: 0 4px 16px rgba(136, 183, 255, 0.3); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s ease; z-index: 100; }
        .desktop-camera-fab:hover { transform: scale(1.05); background: #5998ff; }
        .desktop-camera-fab svg { color: white; width: 30px; height: 30px; }
        @keyframes shutterFlash { 0% { opacity: 0; } 50% { opacity: 0.8; } 100% { opacity: 0; } }
        @keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

                   /* Renders Panel Slide - PUSH CONTENT LIKE CAMERA */
          @media (max-width: 767px) {
            /* Mobile - Panel slides over and pushes content */
            .renders-panel-slide {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              width: 100%;
              height: 100%;
              z-index: 1001;
              background: #000;
              overflow: hidden;
              transform: translateX(100%);
              will-change: transform;
            }
            
            /* Ensure mobile dashboard has transition for push animation */
            .mobile-dashboard {
              position: relative;
              width: 100%;
              height: 100%;
              transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
              will-change: transform;
              background-color: #2D2D2D;
            }
            
            /* Remove any gaps between sliding elements */
            body, html {
              margin: 0;
              padding: 0;
              overflow-x: hidden;
              width: 100%;
              height: 100%;
            }
            
            .dashboard-container {
              overflow-x: hidden;
              width: 100%;
              height: 100%;
            }
          }

          @media (min-width: 768px) {
            /* Desktop/Tablet - Normal modal behavior */
            .renders-panel-slide {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              z-index: 100;
            }
          }

          /* Slide animations for camera overlay */
          @keyframes slideInFromRight {
            0% {
              transform: translateX(100%);
            }
            100% {
              transform: translateX(0);
            }
          }

          @keyframes slideOutToRight {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(100%);
            }
          }
          
          /* Ensure renders panel internal components are edge-to-edge */
          @media (max-width: 767px) {
            .renders-panel-slide .renders-overlay {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              width: 100% !important;
              height: 100% !important;
              padding: 0 !important;
              margin: 0 !important;
              background-color: #000 !important;
              backdrop-filter: none !important;
            }
            
            .renders-panel-slide .renders-panel-container {
              width: 100% !important;
              height: 100% !important;
              max-width: 100% !important;
              max-height: 100% !important;
              border-radius: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              background-color: #000 !important;
            }
            
            .renders-panel-slide .desktop-renders-view {
              width: 100% !important;
              height: 100% !important;
              background-color: #ffffffff !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            .renders-panel-slide .renders-header {
              margin: 0 !important;
              border-radius: 0 !important;
            }
            
            .renders-panel-slide .renders-content-scroll {
              padding: 16px !important;
            }
          }

                @media (min-width: 768px) { .desktop-tablet-layout { display: flex; } .mobile-dashboard { display: none; } .mobile-home-button { display: none; } }
        @media (max-width: 767px) { .desktop-tablet-layout { display: none; } .mobile-dashboard { display: flex; width: 100%; } .mobile-home-button { display: none; } }
        @media (max-width: 767px) { .desktop-tablet-layout { display: none; } .mobile-dashboard { display: flex; width: 100%; overflow-x: hidden; } .mobile-home-button { display: flex; } .dashboard-container { width: 100%; overflow-x: hidden; } .top-banner-dashboard-div { width: 100%; } .middle-content-gallery-div { width: 100%; } .bottom-menu-nav-div { width: 100%; } }
        @supports (-webkit-touch-callout: none) {
          .dashboard-container.browser-mode { position: fixed; top: 0; left: 0; right: 0; bottom: 0; height: 100dvh; overflow: hidden; }
          .mobile-dashboard.browser-mode { position: fixed; top: 0; left: 0; right: 0; bottom: 0; overflow: hidden; }
          .mobile-dashboard { padding-top: constant(safe-area-inset-top); padding-top: env(safe-area-inset-top); padding-bottom: constant(safe-area-inset-bottom); padding-bottom: env(safe-area-inset-bottom); padding-left: constant(safe-area-inset-left); padding-left: env(safe-area-inset-left); padding-right: constant(safe-area-inset-right); padding-right: env(safe-area-inset-right); width: 100%; }
          .top-banner-dashboard-div { padding-top: constant(safe-area-inset-top); padding-top: env(safe-area-inset-top, 47px); margin-top: 0px; height: 170px; overflow: visible; }
          body.browser-body { position: fixed; width: 100%; height: 100%; overflow: hidden; }
          input, textarea { font-size: 16px !important; transform: none !important; }
        }
        .top-banner-dashboard-div.browser-mode { margin-top: 10px; height: 175px; overflow: hidden; }
      `}</style>

      <div className={`dashboard-container ${catalogActive ? 'catalog-scrollable' : ''} ${!isPWA ? 'browser-mode' : ''}`}>
        
        {/* ============================================ */}
        {/* DESKTOP/TABLET LAYOUT */}
        {/* ============================================ */}
        <div className="desktop-tablet-layout">
          <div className="desktop-grid-overlay">
            {gridStyles.map((style, index) => (
              <div key={index} style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)`,
                backgroundSize: style.backgroundSize || '40px 40px',
                display: activeGrid === index ? 'block' : 'none', opacity: gridOpacity,
                transition: 'opacity 1s ease', willChange: 'transform',
              }} />
            ))}
          </div>

          <div className="desktop-main">
            {/* Home/Exit Button */}
            {!rendersActive && !catalogActive && !showDesktopProfileView && !assetLibraryActive && !showDesktopSettingsView && (
              <div className="nav-toggle-wrapper">
                <button className="nav-toggle-button" onClick={handleGoHome}>
                  <FiHome size={10} /> Exit
                </button>
              </div>
            )}

            <div className="desktop-dashboard-card">
              {/* Desktop Top Bar */}
              <div className="desktop-top-bar">
                <div className="top-bar-grid-overlay">
                  {gridStyles.map((style, index) => (
                    <div key={index} style={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      backgroundImage: `linear-gradient(to right, rgba(255, 255, 255, 0.3) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.3) 1px, transparent 1px)`,
                      backgroundSize: style.backgroundSize || '40px 40px',
                      display: activeGrid === index ? 'block' : 'none', opacity: gridOpacity,
                      transition: 'opacity 1s ease', willChange: 'transform',
                    }} />
                  ))}
                </div>

                <div className="top-bar-left">
                  <div className="greeting-section" style={{ flexShrink: 0, minWidth: 'fit-content' }}>
                    <h1>Dashboard</h1>
                    <p>Hi, {authUser ? getDisplayName() : 'Guest'}</p>
                    {authUser && (
                      <div style={{ display: 'flex', gap: '6px', marginTop: '2px', position: 'absolute', top: '100%', left: 0, whiteSpace: 'nowrap' }}>
                        <div style={{
                          background: '#595959', borderRadius: '20px', padding: '3px 10px', color: 'white',
                          fontSize: '0.65rem', fontFamily: 'Avenir, sans-serif', whiteSpace: 'nowrap',
                          display: 'flex', alignItems: 'center', gap: '6px', minWidth: '100px',
                          overflow: 'hidden'
                        }}>
                          <RiImageCircleAiLine size={12} style={{ flexShrink: 0 }} />
                          <div style={{ position: 'relative', display: 'grid', flex: 1 }}>
                            <span style={{
                              gridRow: 1, gridColumn: 1,
                              opacity: showDollarValue ? 0 : 1,
                              transition: 'opacity 0.5s ease'
                            }}>
                              {Math.round(imagesRemaining)} images
                            </span>
                            <span style={{
                              gridRow: 1, gridColumn: 1,
                              opacity: showDollarValue ? 1 : 0,
                              transition: 'opacity 0.5s ease'
                            }}>
                              ${(imagesRemaining * IMAGE_MARKET_RATE).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                        <div style={{
                          background: '#595959', borderRadius: '20px', padding: '3px 10px', color: 'white',
                          fontSize: '0.65rem', fontFamily: 'Avenir, sans-serif', whiteSpace: 'nowrap',
                          display: 'flex', alignItems: 'center', gap: '6px', minWidth: '100px',
                          overflow: 'hidden'
                        }}>
                          <RiVideoOnAiLine size={12} style={{ flexShrink: 0 }} />
                          <div style={{ position: 'relative', display: 'grid', flex: 1 }}>
                            <span style={{
                              gridRow: 1, gridColumn: 1,
                              opacity: showDollarValue ? 0 : 1,
                              transition: 'opacity 0.5s ease'
                            }}>
                              {Math.round(videosRemaining)} videos
                            </span>
                            <span style={{
                              gridRow: 1, gridColumn: 1,
                              opacity: showDollarValue ? 1 : 0,
                              transition: 'opacity 0.5s ease'
                            }}>
                              ${(videosRemaining * VIDEO_MARKET_RATE_HIGH).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="desktop-avatar" onClick={handleDesktopAvatarClick}>
                    {getAvatarUrl() ? <img src={getAvatarUrl()} alt="User" /> : <FiUser size={20} color="#FFFFFF" />}
                  </div>

                  <div className="prompt-container-desktop">
                    <input type="text" className="prompt-input-desktop" placeholder="Describe your project"
                      value={promptValue} onChange={(e) => setPromptValue(e.target.value)} onKeyPress={handleKeyPress} />
                    <button className="prompt-button-desktop" onClick={handlePromptSubmit}>
                      <MdKeyboardArrowRight size={24} />
                    </button>
                  </div>
                </div>

                <div className="top-bar-actions">
                  <div className="search-container">
                    <FiSearch size={16} />
                    <input type="text" placeholder="Search projects..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                  </div>
                  <button className="settings-button-desktop" onClick={() => setShowDesktopSettingsView(true)}>
                    <FiSettings size={22} />
                  </button>
                  <button className="signout-button-desktop" onClick={handleSignOut} title="Sign Out">
                    <GiPowerButton size={22} />
                  </button>
                  <div className="desktop-logo">
                    <img src={LogoWhite} alt="Logo" className="desktop-logo-img" />
                  </div>
                </div>
              </div>

              {/* Desktop Navigation Menu */}
              <div className="nav-menu-container">
                <div className="nav-menu-content">
                                {navItems.filter(item => item.id !== 'developer' || isDeveloper).map((item) => (
                <button key={item.id}
                  className={`nav-menu-item ${activeNavItem === item.id ? 'active' : ''} ${!isDashboardAvailable && item.id === 'renders' ? 'disabled' : ''}`}
                  onClick={() => {
                    console.log('Clicked nav item:', item.id, 'current activeNavItem:', activeNavItem);
                    if (!isDashboardAvailable && item.id === 'renders') { alert('Please log in to access this feature'); return; }
                    
                    // Toggle logic: if clicking the same active nav item, close it
                    if (activeNavItem === item.id) {
                      console.log('Closing panel, setting activeNavItem to null');
                      setActiveNavItem(null);
                      setRendersActive(false);
                      setCatalogActive(false);
                      setAssetLibraryActive(false);
                      setShowDesktopSettingsView(false);
                      setShowDesktopProfileView(false);
                      setShowDesktopDeveloperView(false);
                      setCatalogExpanded(false);
                      setShowLoadingOverlay(false);  // ADD THIS
                      setShowPhotoStep(false);       // ADD THIS
                      setProjectCreationStep('name'); // ADD THIS
                      setLoadingMode('');             // ADD THIS
                      setIsLoadingActive(false);      // ADD THIS
                      return;
                    }
                    
                    console.log('Opening panel, setting activeNavItem to:', item.id);
                    setActiveNavItem(item.id);
                    if (item.id === 'new-project') { 
                      handleNewProjectClick(); 
                      setRendersActive(false); 
                      setCatalogActive(false); 
                    }
                    else if (item.id === 'settings') { 
                      setShowDesktopSettingsView(true); 
                      setRendersActive(false);
                      setCatalogActive(false);
                      setAssetLibraryActive(false);
                    }
                    else if (item.id === 'creative-catalog') { 
                      setRendersActive(false); 
                      setCatalogActive(true); 
                      setCatalogExpanded(false);
                      setAssetLibraryActive(false);
                      setShowDesktopSettingsView(false);
                      setShowDesktopProfileView(false);
                      setShowDesktopDeveloperView(false);
                    }
                    else if (item.id === 'renders') { 
                      setCatalogActive(false); 
                      setRendersActive(true);
                      setAssetLibraryActive(false);
                      setShowDesktopSettingsView(false);
                      setShowDesktopProfileView(false);
                      setShowDesktopDeveloperView(false);
                    }
                    else if (item.id === 'asset-library') { 
                      setRendersActive(false); 
                      setCatalogActive(false); 
                      setAssetLibraryActive(true);
                      setShowDesktopSettingsView(false);
                      setShowDesktopProfileView(false);
                      setShowDesktopDeveloperView(false);
                    }
                    else if (item.id === 'developer') { 
                      setRendersActive(false); 
                      setCatalogActive(false); 
                      setAssetLibraryActive(false); 
                      setShowDesktopSettingsView(false); 
                      setShowDesktopProfileView(false); 
                      setShowDesktopDeveloperView(true);
                    }
                    else { 
                      setRendersActive(false); 
                      setCatalogActive(false); 
                      setShowDesktopSettingsView(false);
                      setAssetLibraryActive(false);
                      setShowDesktopProfileView(false);
                      setShowDesktopDeveloperView(false);
                    }
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {item.icon === 'file' && <BiFileBlank size={18} />}
                  {item.icon === 'film' && <RiFilmAiLine size={18} />}
                  {item.icon === 'cube' && <TbCube3dSphere size={18} />}
                  {item.icon === 'catalog' && <FiGrid size={18} />}
                  {item.icon === 'settings' && <FiSettings size={18} />}
                  {item.icon === 'developer' && <TbDeviceIpadMinus size={18} />}
                  {item.label}
                </button>
              ))}
                </div>
              </div>

              {/* Desktop Split Section - Recent Projects & Asset Library */}
              <div className={`split-section ${showDesktopProfileView ? 'catalog-active' : (catalogActive && !catalogExpanded) || rendersActive || assetLibraryActive ? 'catalog-active' : ''}`}>
                <div className="split-left">
                  <div className="section-header"><h2>Recent Projects & Tutorials</h2></div>
                  <div className="horizontal-gallery">
                    <div className="horizontal-gallery-track">
                      {projectsLoading ? <SkeletonHorizontalCards count={1} /> : tabs.map((tab, index) => (
                        <div key={tab.id} className="gallery-card-horizontal" onClick={() => handleSavedProjectClick(tab)} style={{ position: 'relative' }}>
                          <div className={`gallery-card-media-horizontal ${tab.type === 'new-project' ? 'new-project-card' : ''}`}>
                            {tab.video && <video ref={el => videoRefs.current[index] = el} src={tab.video} loop muted playsInline autoPlay />}
                            {tab.image && <LazyImage src={tab.image} alt={tab.label} />}
                            {tab.icon === 'plus' && !tab.video && !tab.image && <FiPlus size={32} color="#999" />}
                            {tab.type === 'tutorial' && <div className="card-badge">Tutorial<FiHelpCircle size={12} /></div>}
                            {tab.type === 'saved-project' && <div className="card-badge" style={{ left: '12px', right: 'auto' }}>Project<BiFileBlank size={12} /></div>}
                          </div>
                          <div className="gallery-card-label-horizontal">
                            {tab.label}
                            {tab.type === 'saved-project' && tab.updatedAt && (
                              <span style={{ display: 'block', fontSize: '0.65rem', color: '#999', marginTop: '2px' }}>
                                Saved {tab.updatedAt?.toDate ? new Date(tab.updatedAt.toDate()).toLocaleDateString() : ''}
                              </span>
                            )}
                          </div>
                          {tab.type === 'saved-project' && (
                            <button onClick={(e) => { e.stopPropagation(); setProjectSettingsPopup(tab); }}
                              style={{ position: 'absolute', top: '8px', right: '8px', background: '#88b7ff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', padding: 0, zIndex: 11 }}
                              title="Project Settings">
                              <LiaToolsSolid size={12} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="split-right" style={{ background: '#f0f0f0', borderRadius: '16px', display: 'flex', flexDirection: 'column', maxHeight: '320px', cursor: 'pointer' }} onClick={() => setAssetLibraryActive(true)}>
                  <div className="asset-library-header">
                    <h3>Asset Library</h3>
                    <button className="asset-upload-btn" onClick={(e) => { e.stopPropagation(); setAssetLibraryActive(true); }}>
                      <FiPlus size={14} /> Open
                    </button>
                  </div>
                  <div className="asset-grid-scrollable" style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
  {userAssets.length === 0 ? (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px' }}>
      <TbCube3dSphere size={32} color="#ccc" />
      <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.7rem', color: '#999', textAlign: 'center', margin: 0 }}>No assets yet</p>
    </div>
  ) : (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
  {userAssets.slice(0, 8).map((asset, idx) => (
    <div key={asset.id || idx} style={{
      width: '100%', aspectRatio: '1/1', background: '#ddd', borderRadius: '12px', display: 'flex',
      flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      border: '1px solid #ccc', position: 'relative', cursor: 'pointer', padding: '5px'
    }}>
          <div style={{ width: '100%', height: 'calc(100% - 24px)', borderRadius: '6px', overflow: 'hidden', background: '#ccc' }}>
            {asset.thumbnailURL ? (
              <LazyImage src={asset.thumbnailURL} alt={asset.name || 'Asset'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : asset.downloadURL && asset.type?.startsWith('image/') ? (
              <LazyImage src={asset.downloadURL} alt={asset.name || 'Asset'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TbCube3dSphere size={24} color="#999" />
              </div>
            )}
          </div>
          <div style={{
            width: '100%', fontSize: '0.7rem', fontFamily: 'Avenir, sans-serif',
            color: '#333', fontWeight: 500, textAlign: 'center', marginTop: '6px',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          }}>
            {asset.name || `Asset ${idx + 1}`}
          </div>
        </div>
      ))}
    </div>
  )}
</div>
                </div>
              </div>

              {/* Desktop Creative Catalog */}
              <div className={`feed-section ${catalogExpanded ? 'expanded' : ''} ${catalogActive ? 'catalog-active' : rendersActive ? 'catalog-inactive' : ''}`}
                onClick={handleCatalogContainerClick}>
                
                {catalogActive && !showPhotoDetails && !showStyleGuidesFull && !catalogExpanded && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', paddingBottom: '12px', flexShrink: 0 }}>
                    <button onClick={(e) => { e.stopPropagation(); setCatalogActive(false); setActiveNavItem(null); }}
                      style={{ background: 'rgba(217, 217, 217, 1)', color: '#ffffffff', padding: '6px 20px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s ease' }}>
                      <FiArrowLeft size={14} /> Back
                    </button>
                  </div>
                )}

                {showStyleGuidesFull ? (
                  /* Style Guides Full Blog View */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', height: '100%', overflowY: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                      <button onClick={handleCloseStyleGuidesFull} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: '4px', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem' }}>
                        <FiArrowLeft size={18} /> Back to Catalog
                      </button>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#88b7ff', color: 'white', padding: '6px 14px', borderRadius: '20px', fontFamily: 'Avenir, sans-serif', fontWeight: 500, fontSize: '0.85rem' }}>
                        <HiOutlineSwatch size={14} /> Style Guides
                      </div>
                    </div>

                    <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', height: '380px', flexShrink: 0, cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); handleStyleGuideClick(styleGuides[activeStyleGuide]); }}>
                      <LazyImage src={styleGuides[activeStyleGuide].image} alt={styleGuides[activeStyleGuide].title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <div style={{ position: 'absolute', top: '20px', left: '20px', display: 'inline-block', background: '#88b7ff', padding: '6px 14px', borderRadius: '20px', fontSize: '0.75rem', fontFamily: 'Avenir, sans-serif', color: 'white', fontWeight: 500, zIndex: 5 }}>
                        {styleGuides[activeStyleGuide].category}
                      </div>
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '40px 30px', background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', color: 'white' }}>
                        <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '2rem', margin: '0 0 8px 0' }}>{styleGuides[activeStyleGuide].title}</h3>
                        <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '1rem', opacity: 0.9, margin: '0 0 16px 0' }}>{styleGuides[activeStyleGuide].description}</p>
                        <button onClick={(e) => { e.stopPropagation(); handleStyleGuideClick(styleGuides[activeStyleGuide]); }}
                          style={{ background: 'white', color: '#333', border: 'none', padding: '10px 24px', borderRadius: '25px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontWeight: 500, fontSize: '0.9rem' }}>Read More</button>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setActiveStyleGuide(prev => (prev - 1 + styleGuides.length) % styleGuides.length); }}
                        className="style-guide-carousel-btn" style={{ left: '16px' }}><FiChevronLeft size={24} /></button>
                      <button onClick={(e) => { e.stopPropagation(); setActiveStyleGuide(prev => (prev + 1) % styleGuides.length); }}
                        className="style-guide-carousel-btn" style={{ right: '16px' }}><FiChevronRight size={24} /></button>
                      <div style={{ position: 'absolute', bottom: '16px', right: '30px', display: 'flex', gap: '8px' }}>
                        {styleGuides.map((_, index) => (
                          <button key={index} onClick={(e) => { e.stopPropagation(); setActiveStyleGuide(index); }}
                            className={`style-guide-dot ${activeStyleGuide === index ? 'active' : 'inactive'}`} />
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.1rem', color: '#333', marginBottom: '16px' }}>All Style Guides</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                        {styleGuides.map((guide, index) => (
                          <div key={guide.id} onClick={(e) => { e.stopPropagation(); setActiveStyleGuide(index); }}
                            style={{ borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', border: activeStyleGuide === index ? '2px solid #88b7ff' : '2px solid transparent', transition: 'all 0.2s ease', background: 'white' }}>
                            <div style={{ minHeight: '180px', overflow: 'hidden' }}>
                              <LazyImage src={guide.image} alt={guide.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease' }} />
                            </div>
                            <div style={{ padding: '14px' }}>
                              <div style={{ display: 'inline-block', background: '#f0f0f0', padding: '2px 8px', borderRadius: '10px', fontSize: '0.65rem', fontFamily: 'Avenir, sans-serif', color: '#666', marginBottom: '8px' }}>{guide.category}</div>
                              <div style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#333', marginBottom: '4px' }}>{guide.title}</div>
                              <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>{guide.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : showPhotoDetails && selectedPhoto ? (
                  /* Photo Detail View */
                  <div className="photo-details-container">
                    <div className="photo-details-left" style={{ position: 'relative' }}>
                      <img src={selectedPhoto.urls?.regular || selectedPhoto.urls?.full} alt={selectedPhoto.alt_description || 'Selected photo'} />
                      <button onClick={(e) => { e.stopPropagation(); const idx = unsplashPhotos.findIndex(p => p.id === selectedPhoto.id); if (idx > 0) { const prev = unsplashPhotos[idx - 1]; setSelectedPhoto(prev); triggerUnsplashDownload(prev); } }}
                        disabled={unsplashPhotos.findIndex(p => p.id === selectedPhoto.id) <= 0}
                        style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255, 255, 255, 0.9)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#333', zIndex: 10,  opacity: unsplashPhotos.findIndex(p => p.id === selectedPhoto.id) <= 0 ? 0.3 : 1, transition: 'all 0.2s ease' }}>
                        <FiChevronLeft size={24} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); const idx = unsplashPhotos.findIndex(p => p.id === selectedPhoto.id); if (idx < unsplashPhotos.length - 1) { const next = unsplashPhotos[idx + 1]; setSelectedPhoto(next); triggerUnsplashDownload(next); } }}
                        disabled={unsplashPhotos.findIndex(p => p.id === selectedPhoto.id) >= unsplashPhotos.length - 1}
                        style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255, 255, 255, 0.9)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#333', zIndex: 10, opacity: unsplashPhotos.findIndex(p => p.id === selectedPhoto.id) >= unsplashPhotos.length - 1 ? 0.3 : 1, transition: 'all 0.2s ease' }}>
                        <FiChevronRight size={24} />
                      </button>
                    </div>
                    <div className="photo-details-right">
                      <div className="photo-details-sidebar-header">
                        <h3>Photo Details</h3>
                        <button className="photo-details-close-btn" onClick={(e) => { e.stopPropagation(); handleBackToGallery(); }}><FiX size={20} /></button>
                      </div>
                      <div className="photo-details-sidebar-content">
                        <div className="sidebar-info-section">
                          <h4>Photographer</h4>
                          <div className="sidebar-info-item">
                            <span className="sidebar-info-label">Name</span>
                            <span className="sidebar-info-value"><a href={selectedPhoto.user?.links?.html || `https://unsplash.com/@${selectedPhoto.user?.username}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{selectedPhoto.user?.name || 'Unknown Photographer'}</a></span>
                          </div>
                          {selectedPhoto.user?.username && (
                            <div className="sidebar-info-item">
                              <span className="sidebar-info-label">Username</span>
                              <span className="sidebar-info-value"><a href={`https://unsplash.com/@${selectedPhoto.user.username}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>@{selectedPhoto.user.username}</a></span>
                            </div>
                          )}
                        </div>
                        <div className="sidebar-info-section">
                          <h4>Image Information</h4>
                          {selectedPhoto.description && <div className="sidebar-info-item"><span className="sidebar-info-label">Description</span><span className="sidebar-info-value">{selectedPhoto.description}</span></div>}
                          {selectedPhoto.alt_description && !selectedPhoto.description && <div className="sidebar-info-item"><span className="sidebar-info-label">Description</span><span className="sidebar-info-value">{selectedPhoto.alt_description}</span></div>}
                          <div className="sidebar-info-item"><span className="sidebar-info-label">Dimensions</span><span className="sidebar-info-value">{selectedPhoto.width} × {selectedPhoto.height}</span></div>
                          {selectedPhoto.created_at && <div className="sidebar-info-item"><span className="sidebar-info-label">Date</span><span className="sidebar-info-value">{new Date(selectedPhoto.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>}
                          <div className="sidebar-info-item"><span className="sidebar-info-label">Source</span><span className="sidebar-info-value"><a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Unsplash</a></span></div>
                        </div>
                        <div className="sidebar-action-buttons">
                          <button className="sidebar-action-btn primary" onClick={(e) => { e.stopPropagation(); handleAddToMoodBoard(selectedPhoto); }}><FiPlus size={18} /> Add to Mood Board</button>
                          <button className="sidebar-action-btn" onClick={(e) => { e.stopPropagation(); window.open(selectedPhoto.links?.html || `https://unsplash.com/photos/${selectedPhoto.id}`, '_blank'); triggerUnsplashDownload(selectedPhoto); }}><GrGallery size={18} /> View Source on Unsplash</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Catalog List View */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', flex: 1, minHeight: 0 }}>
                    <div className="catalog-header" style={{ flexShrink: 0 }}>
                      <h2>Creative Catalog</h2>
                      <div className="catalog-header-actions">
                        <form onSubmit={handleCatalogSearch} className="catalog-search-form" onClick={(e) => e.stopPropagation()}>
                          <input type="text" className="catalog-search-input" placeholder="Search Catalog ..." value={catalogSearchQuery} onChange={(e) => setCatalogSearchQuery(e.target.value)} />
                        </form>
                        <button className="catalog-expand-btn" onClick={(e) => { e.stopPropagation(); setCatalogExpanded(!catalogExpanded); }} title={catalogExpanded ? 'Shrink' : 'Expand'}>
                          {catalogExpanded ? <LuShrink size={20} /> : <IoExpand size={20} />}
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                      <div style={{ width: '45%', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', background: '#f8f8f8', borderRadius: '16px', padding: '16px' }}>
                        <div style={{ flexShrink: 0, paddingBottom: '4px' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#88b7ff', color: 'white', padding: '6px 14px', borderRadius: '20px', fontFamily: 'Avenir, sans-serif', fontWeight: 500, fontSize: '0.85rem' }}><HiOutlineSwatch size={14} /> Style Guides</div>
                          <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.7rem', color: '#888', margin: '8px 0 0 4px' }}>Click to view all style guides</p>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          {styleGuides.map((guide) => (
                            <div key={guide.id} style={{ borderRadius: '12px', overflow: 'hidden', background: 'white', border: '1px solid #eaeaea', cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); setActiveStyleGuide(styleGuides.indexOf(guide)); handleOpenStyleGuidesFull(e); }}>
                              <div style={{ minHeight: '160px', overflow: 'hidden' }}>
                                <LazyImage src={guide.image} alt={guide.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease' }} />
                              </div>
                              <div style={{ padding: '14px' }}>
                                <div style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#333', marginBottom: '4px' }}>{guide.title}</div>
                                <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888', marginBottom: '8px' }}>{guide.description}</div>
                                <div style={{ display: 'inline-block', background: '#f0f0f0', padding: '3px 10px', borderRadius: '12px', fontSize: '0.7rem', fontFamily: 'Avenir, sans-serif', color: '#666' }}>{guide.category}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                                   <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0, position: 'relative', height: '520px', overflow: 'hidden', padding: '0 40px' }}>
                <div style={{ flexShrink: 0 }}>
                  <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', margin: 0, paddingBottom: '2px' }}>Interior Design Inspiration</h3>
                  <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: '#888', margin: 0, paddingBottom: '8px' }}>{unsplashPhotos.length} photos</p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'flex-start', padding: '16px', overflowY: 'auto', flex: 1, alignContent: 'start' }}>
                  {unsplashLoading && unsplashPhotos.length === 0 && (
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', width: '100%' }}>
                      <SkeletonShimmer width="100%" height="150px" borderRadius="12px" style={{ flex: '0 0 calc(25% - 18px)', minWidth: '180px' }} />
                      <SkeletonShimmer width="100%" height="150px" borderRadius="12px" style={{ flex: '0 0 calc(25% - 18px)', minWidth: '180px', opacity: 0.6 }} />
                    </div>
                  )}
                  {unsplashPhotos.map((photo) => (
                    <div key={photo.id} onClick={(e) => { e.stopPropagation(); handlePhotoClick(photo); }} style={{ flex: '0 0 calc(25% - 18px)', borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #eaeaea', aspectRatio: '4/3', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}>
                      <LazyImage src={photo.urls?.small || photo.urls?.regular} alt={photo.alt_description || 'Unsplash photo'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
                {unsplashPhotos.length > 0 && (
                  <button className="load-more-btn" onClick={(e) => { e.stopPropagation(); handleLoadMorePhotos(); }} disabled={unsplashLoading} style={{ flexShrink: 0 }}>
                    {unsplashLoading ? 'Loading...' : 'Load More'}
                  </button>
                )}
                {showInteriorDesignComingSoon && (
                  <div style={{ padding: '0 48px', width: '100%' }}>
                    <ComingSoonOverlay section="interior-design" />
                  </div>
                )}
              </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Desktop Settings View */}
              {showDesktopSettingsView && (
                      <div className="feed-section" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, overflowY: 'auto', display: 'flex', flexDirection: 'column', background: '#fafafa', borderRadius: '0 0 24px 24px', padding: '0' }}>
                       <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 16px', borderBottom: '1px solid #eaeaea', flexShrink: 0, width: '100%' }}>
                    <button onClick={() => { setShowDesktopSettingsView(false); setActiveNavItem(null); }} style={{ background: 'rgba(217, 217, 217, 1)', color: '#ffffff', padding: '6px 16px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}>
                      <FiArrowLeft size={14} /> Back
                    </button>
                    <FiSettings size={24} style={{ color: '#333' }} />
                    <h2 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.3rem', color: '#333', letterSpacing: '-0.3px', margin: 0 }}>Settings</h2>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px 24px 16px', flex: 1, justifyContent: 'flex-start' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '500px', width: '100%', position: 'relative', paddingBottom: '20px' }}>
                      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #eaeaea' }}>
                        <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', marginBottom: '16px' }}>Auto-Save</h3>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem', color: '#666' }}>Enable Auto-Save</span>
                          <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                            <input type="checkbox" defaultChecked style={{ opacity: 0, width: 0, height: 0 }} />
                            <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#88B7FF', borderRadius: '26px', transition: '0.3s' }}>
                              <span style={{ position: 'absolute', height: '20px', width: '20px', left: '24px', bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.3s' }} />
                            </span>
                          </label>
                        </div>
                        <div>
                          <label style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '8px' }}>Save Interval: 5 minutes</label>
                          <input type="range" min="1" max="30" defaultValue="5" style={{ width: '100%', accentColor: '#ccc', background: '#e0e0e0', height: '4px', borderRadius: '2px', WebkitAppearance: 'none', appearance: 'none' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#999', fontFamily: 'Avenir, sans-serif' }}><span>1 min</span><span>30 min</span></div>
                        </div>
                      </div>

                      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #eaeaea' }}>
                        <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', marginBottom: '16px' }}>Default Export Quality</h3>
                        <div>
                          <label style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '8px' }}>Quality: 85%</label>
                          <input type="range" min="10" max="100" defaultValue="85" style={{ width: '100%', accentColor: '#ccc', background: '#e0e0e0', height: '4px', borderRadius: '2px', WebkitAppearance: 'none', appearance: 'none' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#999', fontFamily: 'Avenir, sans-serif' }}><span>Low (10%)</span><span>High (100%)</span></div>
                        </div>
                      </div>

                      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #eaeaea' }}>
                        <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', marginBottom: '16px' }}>Default Export Format</h3>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          {['JPG', 'PNG', 'WEBP', 'TIFF'].map((format) => (
                            <button key={format} style={{ padding: '10px 20px', borderRadius: '12px', border: format === 'JPG' ? '2px solid #88B7FF' : '1px solid #ddd', background: format === 'JPG' ? '#88B7FF' : 'white', color: format === 'JPG' ? 'white' : '#333', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem', fontWeight: 500, transition: 'all 0.2s ease' }}>{format}</button>
                          ))}
                        </div>
                      </div>

                      {/* Debug Panel & Developer Console */}
                      {isDeveloper && (
                        <>
                          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #eaeaea' }}>
                            <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', marginBottom: '16px' }}>Developer Debug</h3>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button onClick={() => { showFullScreenModal({ trigger: FULLSCREEN_TRIGGERS.TRIAL_ENDED, plan: 'starter', remaining: { iterations: 0, videos: 0 }, onUpgrade: () => navigate('/pricing') }); }} style={{ padding: '6px 12px', borderRadius: '20px', border: 'none', background: '#ff9800', color: 'white', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'Avenir, sans-serif' }}>Trial Ended</button>
                              <button onClick={() => { showFullScreenModal({ trigger: FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_ITERATIONS, plan: 'creator', remaining: { iterations: 0, videos: 5 }, onUpgrade: () => navigate('/pricing') }); }} style={{ padding: '6px 12px', borderRadius: '20px', border: 'none', background: '#88B7FF', color: 'white', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'Avenir, sans-serif' }}>Out of Iterations</button>
                              <button onClick={() => { showFullScreenModal({ trigger: FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_VIDEOS, plan: 'pro', remaining: { iterations: 10, videos: 0 }, onUpgrade: () => navigate('/pricing') }); }} style={{ padding: '6px 12px', borderRadius: '20px', border: 'none', background: '#88B7FF', color: 'white', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'Avenir, sans-serif' }}>Out of Videos</button>
                            </div>
                          </div>
                          <button onClick={() => { setShowDesktopSettingsView(false); setActiveNavItem('developer'); setShowDesktopDeveloperView(true); }} style={{ background: '#88B7FF', color: 'white', border: 'none', borderRadius: '30px', padding: '14px 20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '1rem', fontWeight: 500, width: '100%' }}>Developer Console</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

                                         {/* Desktop Developer View */}
              {showDesktopDeveloperView && isDeveloper && (
                <div className="feed-section" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, overflowY: 'auto', display: 'flex', flexDirection: 'column', background: '#fafafa', borderRadius: '0 0 24px 24px', padding: '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #eaeaea', background: '#fafafa', position: 'sticky', top: 0, zIndex: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button onClick={() => { setShowDesktopDeveloperView(false); setActiveNavItem(null); }}
                        style={{ background: 'rgba(217, 217, 217, 1)', color: '#ffffff', padding: '6px 16px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}>
                        <FiArrowLeft size={14} /> Back
                      </button>
                      <h2 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.3rem', color: '#333', letterSpacing: '-0.3px', margin: 0 }}>Developer Console</h2>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{ background: isDeveloper ? '#88b7ff' : '#ccc', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.7rem', fontFamily: 'Avenir, sans-serif' }}>
                        {isDeveloper ? 'Developer Access' : (isMarketer ? 'Marketer Access' : 'No Access')}
                      </span>
                    </div>
                  </div>

                  {/* Dashboard/Splash Screen with Buttons */}
                  {!selectedDeveloperModule ? (
                    <div style={{ padding: '48px 24px', maxWidth: '800px', margin: '0 auto', width: '100%', textAlign: 'center' }}>
                      <div style={{ marginBottom: '48px' }}>
                        <FaCog size={64} color="#88b7ff" style={{ marginBottom: '16px' }} />
                        <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.5rem', color: '#333', marginBottom: '8px' }}>Developer Dashboard</h3>
                        <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem', color: '#888' }}>Select a module to manage</p>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                        {/* Account Management Button */}
                        <button
                          onClick={() => setSelectedDeveloperModule('account')}
                          style={{
                            background: 'white',
                            border: '1px solid #eaeaea',
                            borderRadius: '20px',
                            padding: '32px 24px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#88b7ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#eaeaea'; }}
                        >
                          <FiUsers size={48} color="#88b7ff" style={{ marginBottom: '16px' }} />
                          <h4 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.2rem', color: '#333', marginBottom: '8px' }}>Account Management</h4>
                          <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Manage user subscriptions, roles, and view platform statistics</p>
                        </button>

                        {/* Marketing Studio Button */}
                        <button
                          onClick={() => {
                            setShowDesktopDeveloperView(false);
                            setShowDevStudio(true);
                          }}
                          style={{
                            background: 'white',
                            border: '1px solid #eaeaea',
                            borderRadius: '20px',
                            padding: '32px 24px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#88b7ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#eaeaea'; }}
                        >
                          <RiImageCircleAiLine size={48} color="#88b7ff" style={{ marginBottom: '16px' }} />
                          <h4 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.2rem', color: '#333', marginBottom: '8px' }}>Marketing Studio</h4>
                          <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Generate 4K 9:16 images for social media content</p>
                        </button>

                        {/* Welcome Splash Button */}
                        <button
                          onClick={() => navigate('/welcome')}
                          style={{
                            background: 'white',
                            border: '1px solid #eaeaea',
                            borderRadius: '20px',
                            padding: '32px 24px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#88b7ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#eaeaea'; }}
                        >
                          <FiHome size={48} color="#88b7ff" style={{ marginBottom: '16px' }} />
                          <h4 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.2rem', color: '#333', marginBottom: '8px' }}>Welcome Splash</h4>
                          <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>View the welcome splash screen</p>
                        </button>
                      </div>

                                            {/* Test notifications moved to Profile > Debug Panel */}
                    </div>
                  ) : (
                    /* Account Management Content (when module selected) */
                    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
                      <button
                        onClick={() => setSelectedDeveloperModule(null)}
                        style={{
                          background: 'rgba(217, 217, 217, 1)',
                          color: '#ffffff',
                          padding: '6px 16px',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          border: 'none',
                          marginBottom: '24px',
                        }}
                      >
                        <FiArrowLeft size={14} /> Back to Dashboard
                      </button>

                      {/* Stats Overview */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #eaeaea' }}>
                          <div style={{ fontSize: '0.7rem', color: '#888', fontFamily: 'Avenir, sans-serif' }}>Total Users</div>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#333' }}>{usersList.length}</div>
                        </div>
                        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #eaeaea' }}>
                          <div style={{ fontSize: '0.7rem', color: '#888', fontFamily: 'Avenir, sans-serif' }}>Pro Users</div>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#333' }}>{usersList.filter(u => u.subscriptionPlan === 'pro').length}</div>
                        </div>
                        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #eaeaea' }}>
                          <div style={{ fontSize: '0.7rem', color: '#888', fontFamily: 'Avenir, sans-serif' }}>Trial Users</div>
                          <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#333' }}>{usersList.filter(u => u.subscriptionPlan === 'trial' || !u.subscriptionPlan).length}</div>
                        </div>
                      </div>

                      {/* User Management Section */}
                      <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                          <div>
                            <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', marginBottom: '4px' }}>User Management</h3>
                            <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: '#888' }}>Manage user subscriptions and roles</p>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input type="text" placeholder="Search users..." value={searchUsersQuery} onChange={(e) => setSearchUsersQuery(e.target.value)}
                              style={{ padding: '8px 14px', border: '1px solid #ddd', borderRadius: '20px', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', outline: 'none', width: '200px' }} />
                            <button onClick={fetchAllUsers} style={{ background: '#88b7ff', border: 'none', padding: '8px 16px', borderRadius: '20px', color: 'white', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem' }}>
                              Refresh
                            </button>
                          </div>
                        </div>

                        {usersLoading ? (
                          <div style={{ textAlign: 'center', padding: '40px' }}><SkeletonShimmer width="100%" height="100px" borderRadius="8px" /></div>
                        ) : (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid #eaeaea', textAlign: 'left' }}>
                                  <th style={{ padding: '12px 8px' }}>User</th>
                                  <th style={{ padding: '12px 8px' }}>Email</th>
                                  <th style={{ padding: '12px 8px' }}>Plan</th>
                                  <th style={{ padding: '12px 8px' }}>Usage</th>
                                  <th style={{ padding: '12px 8px' }}>Role</th>
                                  <th style={{ padding: '12px 8px' }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {usersList.filter(u => u.email?.toLowerCase().includes(searchUsersQuery.toLowerCase())).map((user) => (
                                  <tr key={user.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '12px 8px' }}><strong>{user.displayName || user.firstName || 'User'}</strong></td>
                                    <td style={{ padding: '12px 8px', color: '#666' }}>{user.email}</td>
                                    <td style={{ padding: '12px 8px' }}>
                                      <span style={{ background: user.subscriptionPlan === 'pro' ? '#e8f5e9' : '#fff3e0', color: user.subscriptionPlan === 'pro' ? '#2e7d32' : '#e65100', padding: '4px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '500' }}>
                                        {user.subscriptionPlan || 'trial'}
                                      </span>
                                      {user.billingCycle && <span style={{ fontSize: '0.65rem', color: '#888', marginLeft: '6px' }}>({user.billingCycle})</span>}
                                    </td>
                                    <td style={{ padding: '12px 8px' }}>
                                      {user.usage?.imageEquivalents || 0} / {user.subscriptionPlan === 'pro' ? '5500' : '100'} images
                                    </td>
                                    <td style={{ padding: '12px 8px' }}>
                                      <span style={{ background: user.role === 'developer' ? '#e3f2fd' : (user.role === 'marketer' ? '#f3e5f5' : '#f5f5f5'), color: user.role === 'developer' ? '#1565c0' : (user.role === 'marketer' ? '#6a1b9a' : '#666'), padding: '4px 8px', borderRadius: '12px', fontSize: '0.7rem' }}>
                                        {user.role === 'developer' ? 'Developer' : (user.role === 'marketer' ? 'Marketer' : 'User')}
                                      </span>
                                    </td>
                                    <td style={{ padding: '12px 8px' }}>
                                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        <button onClick={() => setEditUserPlan({ show: true, userId: user.id, currentPlan: user.subscriptionPlan || 'trial', newPlan: '', billingCycle: '' })}
                                          style={{ background: '#88b7ff', border: 'none', padding: '4px 10px', borderRadius: '12px', color: 'white', cursor: 'pointer', fontSize: '0.7rem' }}>Change Plan</button>
                                        <button onClick={() => handleResetUserUsage(user.id, user.email)}
                                          style={{ background: '#ff9800', border: 'none', padding: '4px 10px', borderRadius: '12px', color: 'white', cursor: 'pointer', fontSize: '0.7rem' }}>Reset Usage</button>
                                        <button onClick={() => handleSyncUserAccount(user.id, user.email)}
                                          style={{ background: '#4caf50', border: 'none', padding: '4px 10px', borderRadius: '12px', color: 'white', cursor: 'pointer', fontSize: '0.7rem' }}>Sync Account</button>
                                        {user.role !== 'developer' && (
                                          <button onClick={() => handleGrantDeveloperRole(user.id, user.email)}
                                            style={{ background: '#2196f3', border: 'none', padding: '4px 10px', borderRadius: '12px', color: 'white', cursor: 'pointer', fontSize: '0.7rem' }}>Make Developer</button>
                                        )}
                                        {user.role !== 'marketer' && user.role !== 'developer' && (
                                          <button onClick={() => handleGrantMarketerRole(user.id, user.email)}
                                            style={{ background: '#9c27b0', border: 'none', padding: '4px 10px', borderRadius: '12px', color: 'white', cursor: 'pointer', fontSize: '0.7rem' }}>Make Marketer</button>
                                        )}
                                        {user.role !== 'user' && user.role !== undefined && (
                                          <button onClick={() => handleRevokeRole(user.id, user.email)}
                                            style={{ background: '#f44336', border: 'none', padding: '4px 10px', borderRadius: '12px', color: 'white', cursor: 'pointer', fontSize: '0.7rem' }}>Revoke Role</button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Change Plan Popup */}
              {editUserPlan.show && (
                <div style={popupOverlayStyle} onClick={() => setEditUserPlan({ show: false, userId: null, currentPlan: '', newPlan: '', billingCycle: '' })}>
                  <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
                    <h3 style={{ color: '#333', marginBottom: '16px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.1rem' }}>Change User Plan</h3>
                    <p style={{ color: '#666', marginBottom: '16px', fontSize: '0.85rem' }}>Current Plan: <strong>{editUserPlan.currentPlan || 'trial'}</strong></p>
                    <select value={editUserPlan.newPlan} onChange={(e) => setEditUserPlan({ ...editUserPlan, newPlan: e.target.value })}
                      style={{ width: '100%', padding: '12px 16px', marginBottom: '15px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif' }}>
                      <option value="">Select new plan</option>
                      <option value="starter">Starter</option>
                      <option value="creator">Creator</option>
                      <option value="pro">Pro</option>
                    </select>
                    <select value={editUserPlan.billingCycle} onChange={(e) => setEditUserPlan({ ...editUserPlan, billingCycle: e.target.value })}
                      style={{ width: '100%', padding: '12px 16px', marginBottom: '15px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif' }}>
                      <option value="">Select billing cycle (optional)</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <button onClick={() => setEditUserPlan({ show: false, userId: null, currentPlan: '', newPlan: '', billingCycle: '' })} style={popupCancelStyle}>Cancel</button>
                      <button onClick={handleUpdateUserPlan} disabled={!editUserPlan.newPlan} style={{ ...popupConfirmStyle, opacity: !editUserPlan.newPlan ? 0.5 : 1 }}>Update Plan</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Desktop Profile View */}
              {showDesktopProfileView && authUser && (
                <div className="feed-section" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, overflowY: 'auto', display: 'flex', flexDirection: 'column', background: '#fafafa', borderRadius: '0 0 24px 24px', padding: '0' }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 16px', borderBottom: '1px solid #eaeaea', flexShrink: 0 }}>
                    <button onClick={() => setShowDesktopProfileView(false)} style={{ background: 'rgba(217, 217, 217, 1)', color: '#ffffff', padding: '6px 16px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}>
                      <FiArrowLeft size={14} /> Back
                    </button>
                    <FiUser size={24} style={{ color: '#333' }} />
                    <h2 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.3rem', color: '#333', margin: 0 }}>Your Profile</h2>
                  </div>
                  <div className="profile-view-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', maxWidth: '400px', margin: '0 auto', width: '100%', flex: '1', justifyContent: 'center' }}>
                    <div className="profile-view-avatar-section">
                      <div className="profile-view-avatar-large" onClick={handleChooseProfileAvatar}>
                        {avatarPreview ? <img src={avatarPreview} alt="User Avatar" className="profile-view-avatar-image" /> : <img src={getAvatarUrl()} alt="User Avatar" className="profile-view-avatar-image" />}
                      </div>
                      <button className="profile-view-change-avatar" onClick={handleChooseProfileAvatar}>Change Avatar</button>
                      {avatarPreview && (
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button onClick={handleSaveAvatar} disabled={isSavingAvatar} style={{ padding: '6px 16px', backgroundColor: '#88B7FF', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', opacity: isSavingAvatar ? 0.6 : 1 }}>{isSavingAvatar ? 'Saving...' : 'Save Avatar'}</button>
                          <button onClick={handleCancelAvatar} style={{ padding: '6px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ccc', borderRadius: '20px', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      )}
                    </div>
                    <div className="profile-view-info">
                      <div className="profile-view-info-item">
                        <span className="profile-view-label">Display Name</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {isEditingName ? (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <input type="text" value={tempUserName} onChange={(e) => setTempUserName(e.target.value)} style={{ padding: '4px 8px', backgroundColor: '#f5f5f5', border: '1px solid #88b7ff', borderRadius: '6px', fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem', width: '150px' }} />
                              <button onClick={handleSaveProfileToFirebase} style={{ padding: '4px 12px', backgroundColor: '#88B7FF', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>Save</button>
                              <button onClick={() => { setIsEditingName(false); setTempUserName(userName); }} style={{ padding: '4px 12px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>Cancel</button>
                            </div>
                          ) : (
                            <>
                              <span className="profile-view-value">{getDisplayName()}</span>
                              <button onClick={() => { setIsEditingName(true); setTempUserName(userName); }} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}><LuPencilLine size={14} /></button>
                            </>
                          )}
                        </div>
                      </div>
                                            {/* Email Dropdown */}
                      <div className="profile-view-info-item" style={{ position: 'relative' }}>
                        <span className="profile-view-label">Email</span>
                        <div
                          onClick={() => setShowDesktopEmailDropdown(!showDesktopEmailDropdown)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            background: showDesktopEmailDropdown ? '#f0f0f0' : 'transparent',
                            transition: 'background 0.2s ease',
                          }}
                          onMouseEnter={(e) => { if (!showDesktopEmailDropdown) e.currentTarget.style.background = '#f5f5f5'; }}
                          onMouseLeave={(e) => { if (!showDesktopEmailDropdown) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span className="profile-view-value">{authUser?.email}</span>
                          <FaChevronDown
                            size={16}
                            style={{
                              color: '#999',
                              transition: 'transform 0.3s ease',
                              transform: showDesktopEmailDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          />
                        </div>
                        {/* Dropdown menu */}
                        {showDesktopEmailDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            right: 0,
                            minWidth: '200px',
                            background: 'white',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            zIndex: 10,
                            border: '1px solid #eaeaea',
                          }}>
                            <button
                              onClick={() => {
                                setShowDesktopEmailDropdown(false);
                                setShowEmailChangePopup(true);
                              }}
                              style={{
                                padding: '12px 16px',
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                background: 'white',
                                color: '#333',
                                cursor: 'pointer',
                                fontFamily: 'Avenir, sans-serif',
                                fontSize: '0.85rem',
                                transition: 'background 0.2s ease',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              Change Email
                            </button>
                            <button
                              onClick={() => {
                                setShowDesktopEmailDropdown(false);
                                setShowPasswordChangePopup(true);
                              }}
                              style={{
                                padding: '12px 16px',
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                borderTop: '1px solid #f0f0f0',
                                background: 'white',
                                color: '#333',
                                cursor: 'pointer',
                                fontFamily: 'Avenir, sans-serif',
                                fontSize: '0.85rem',
                                transition: 'background 0.2s ease',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              Change Password
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ width: '100%', background: '#ffffffff', padding: '12px 16px', borderRadius: '12px', color: 'white', fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem', textAlign: 'center', border: '1px solid #88b7ff' }}>
                      <span style={{ color: '#aaa', marginRight: '8px' }}>Current Plan:</span>
                      <span style={{ color: '#4594F9', fontWeight: '600' }}>{getCurrentPlanName().toUpperCase()}</span>
                      {getCurrentBillingCycle() && <span style={{ color: '#aaa', fontSize: '0.8rem', marginLeft: '6px' }}>({getCurrentBillingCycle()})</span>}
                    </div>

                    {/* Usage Pills & Add Balance */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                      <div style={{
                        background: '#f0f0f0', borderRadius: '20px', padding: '8px 16px',
                        display: 'flex', alignItems: 'center', gap: '8px',
                        fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#333',
                      }}>
                        <RiImageCircleAiLine size={16} color="#333" />
                        <span>{Math.round(imagesRemaining)} iterations</span>
                      </div>
                      <div style={{
                        background: '#f0f0f0', borderRadius: '20px', padding: '8px 16px',
                        display: 'flex', alignItems: 'center', gap: '8px',
                        fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#333',
                      }}>
                        <RiVideoOnAiLine size={16} color="#333" />
                        <span>{Math.round(videosRemaining)} videos</span>
                      </div>
                      <button
                        onClick={() => {
                          const planName = getCurrentPlanName().toLowerCase() || 'starter';
                          showFullScreenModal({
                            trigger: FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_ITERATIONS,
                            plan: planName,
                            remaining: { iterations: imagesRemaining, videos: videosRemaining },
                            onUpgrade: () => navigate('/pricing'),
                            onAddBalance: () => navigate('/pricing'),
                            initialView: 'add-balance',
                          });
                        }}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: '#88B7FF',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.3rem',
                          fontWeight: 'bold',
                          transition: 'background 0.2s ease',
                          flexShrink: 0,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#6a9fdf'}
                        onMouseLeave={e => e.currentTarget.style.background = '#88B7FF'}
                      >
                        +
                      </button>
                    </div>

                <button className="profile-view-signout-btn" onClick={handleSignOut}>Sign Out</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Desktop Camera FAB */}
          {!rendersActive && !catalogActive && !showDesktopProfileView && !assetLibraryActive && !showDesktopSettingsView && (
            <button className="desktop-camera-fab" onClick={handleCameraClick}><MdOutlinePhotoCamera size={30} /></button>
          )}
        </div>

        {/* ============================================ */}
        {/* MOBILE DASHBOARD */}
        {/* ============================================ */}

        {/* DevTools popup removed; moved to Profile > Debug Panel */}

        <div className={`mobile-dashboard ${catalogActive ? 'catalog-scrollable' : ''} ${!isPWA ? 'browser-mode' : ''}`} style={catalogActive ? { paddingBottom: 0 } : {}}>
          {/* Mobile Top Banner */}
          <div className={`top-banner-dashboard-div ${!isPWA ? 'browser-mode' : ''}`} onClick={() => setProjectDropdownOpen(null)}>
            <div className="grid-overlay-container" ref={gridContainerRef}>
              {gridStyles.map((style, index) => (
                <div key={index} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.3) 1px, transparent 1px)`, ...style }} />
              ))}
            </div>
            <div className="banner-content">
                            <div className="left-column">
                <div className="text-content">
                  <h1 className="dashboard-title">Dashboard</h1>
                  <p className="greeting" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Hi, {authUser ? getDisplayName() : 'Guest'}</p>
                  <div className="prompt-container">
                    <div className="prompt-input-wrapper">
                      <input type="text" className="prompt-input" placeholder="Describe your project" value={promptValue} onChange={(e) => setPromptValue(e.target.value)} onKeyPress={handleKeyPress} onFocus={() => setIsPromptFocused(true)} onBlur={() => setIsPromptFocused(false)} autoComplete="off" name="search" id="search" autoCorrect="off" autoCapitalize="sentences" spellCheck="false" />
                      <button className="prompt-button" onClick={handlePromptSubmit} disabled={isLoadingActive}><MdKeyboardArrowRight size={24} className="arrow-icon" /></button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="right-column" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '0px', marginRight: '10px', marginTop: '17px' }}>
                {authUser && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                    <div style={{
                      background: '#595959', borderRadius: '20px', padding: '3px 12px', color: 'white',
                      fontSize: '0.7rem', fontFamily: 'Avenir, sans-serif', whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: '4px', width: '110px', justifyContent: 'center',
                      position: 'relative', top: '4px',right: '45%', overflow: 'hidden'
                    }}>
                      <RiImageCircleAiLine size={13} style={{ flexShrink: 0 }} />
                      <div style={{
                        position: 'relative', display: 'inline-block', width: '100%', height: '1.2em',
                        lineHeight: '1.2em', verticalAlign: 'middle'
                      }}>
                        <span style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: showDollarValue ? 0 : 1, transition: 'opacity 0.5s ease'
                        }}>
                          {Math.round(imagesRemaining)}
                        </span>
                        <span style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: showDollarValue ? 1 : 0, transition: 'opacity 0.5s ease'
                        }}>
                              ${(imagesRemaining * IMAGE_MARKET_RATE).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                    <div style={{
                      background: '#595959', borderRadius: '20px', padding: '3px 12px', color: 'white',
                      fontSize: '0.7rem', fontFamily: 'Avenir, sans-serif', whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: '4px', width: '110px', justifyContent: 'center',
                      position: 'relative', top: '4px', right: '45%', overflow: 'hidden'
                    }}>
                      <RiVideoOnAiLine size={13} style={{ flexShrink: 0 }} />
                      <div style={{
                        position: 'relative', display: 'inline-block', width: '100%', height: '1.2em',
                        lineHeight: '1.2em', verticalAlign: 'middle'
                      }}>
                        <span style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: showDollarValue ? 0 : 1, transition: 'opacity 0.5s ease'
                        }}>
                          {Math.round(videosRemaining)}
                        </span>
                        <span style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: showDollarValue ? 1 : 0, transition: 'opacity 0.5s ease'
                        }}>
                              ${(videosRemaining * VIDEO_MARKET_RATE_HIGH).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '15px', marginLeft: '-20px' }}>
                  <div className="logo-icon"><img src={LogoWhite} alt="Logo" className="logo-image" /></div>
                  <div className="avatar-circle" onClick={handleAvatarClick} ref={avatarButtonRef}>
                    <div className="avatar-placeholder">
                      {getAvatarUrl() ? <img src={getAvatarUrl()} alt="User Avatar" className="avatar-image" /> : <FiUser size={20} color="#FFFFFF" />}
                    </div>
                  </div>
              
                  {showAvatarDropdown && (
                    <div className="avatar-dropdown" ref={avatarDropdownRef}>
                      <button className="dropdown-item" onClick={handleProfileClick}>Profile</button>
                      <button className="dropdown-item" onClick={handleSettingsClick}>Settings</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button className="settings-button" onClick={handleSignOut}><GiPowerButton size={12} /></button>
          </div>

          {/* Mobile Middle Gallery */}
          {!catalogActive && (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ position: 'absolute', top: 0, right: 0, width: '55%', height: '100%', background: 'linear-gradient(to right, transparent 5%, transparent 5%, #f7f7f7 90%, #f7f7f7 100%)', pointerEvents: 'none', zIndex: 10 }} />
              <div className="middle-content-gallery-div" ref={galleryRef}>
                <div className="gallery-scroll-container">
                  {tabs.map((tab, index) => (
                    <div key={`${tab.id}-${index}`} className="gallery-item">
                      <div className={`gallery-tab ${tab.type === 'new-project' ? 'new-project-tab' : ''} ${tab.type === 'saved-project' ? 'saved-project-tab' : ''} ${selectedTab === tab.id ? 'selected' : ''}`} onClick={() => {
  if (projectDropdownOpen) {
    // A dropdown is open – close it and do NOT open the project
    setProjectDropdownOpen(null);
  } else {
    // No dropdown open – open the project
    handleSavedProjectClick(tab);
  }
}}>
                        {tab.video && <video ref={el => videoRefs.current[index] = el} className="tab-video" src={tab.video} loop muted playsInline />}
                        {tab.image && <LazyImage src={tab.image} alt={tab.label} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', position: 'relative', zIndex: 1 }} />}
                        {tab.icon === 'plus' && !tab.video && !tab.image && <div className="tab-icon plus-icon"><FiPlus size={24} color="#FFFFFF" /></div>}
                        {tab.icon === 'question' && <div className="question-mark-overlay" style={{ right: '6px' }}><FiHelpCircle size={16} color="#FFFFFF" /></div>}
                                                {tab.type === 'saved-project' && (
                          <>
                            {/* Top-right corner click area (40% width, 70% height) – DEBUG RED BORDER */}
                            <div
                              className="gear-click-area"
                              onClick={(e) => {
                                e.stopPropagation();
                                setProjectDropdownOpen(projectDropdownOpen === tab.id ? null : tab.id);
                              }}
                              style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '35%',
                                height: '60%',
                                zIndex: 15,
                                cursor: 'pointer',
                                background: 'transparent',
                                WebkitTapHighlightColor: 'transparent',
                                touchAction: 'manipulation',
                                
                              }}
                              title="Project Settings"
                            />
                            {/* Gear icon (now non‑interactive, just visual) */}
                            <div className="file-icon-overlay" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', pointerEvents: 'none', right: '10px' }}>
                            <BiFileBlank size={16} color="#FFFFFF" />
                            <div style={{ background: '#afbacbad', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                              <LiaToolsSolid size={16} />
                            </div>
                          </div>
                            {/* Dropdown menu - portaled to body */}
                            {projectDropdownOpen === tab.id && createPortal(
                              <>
                                <div
                                  style={{
                                    position: 'fixed',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    zIndex: 99999,
                                    background: 'transparent',
                                    pointerEvents: 'none',
                                  }}
                                />
                                <div className="project-dropdown-menu" style={{
                                  position: 'fixed',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  background: 'white',
                                  borderRadius: '16px',
                                  
                                  boxShadow: '0 2px 12px rgba(4, 3, 3, 0.12)',
                                  zIndex: 100000,
                                  minWidth: '200px',
                                  overflow: 'hidden',
                                  border: '1px solid #eaeaea',
                                }}
                                onClick={(e) => e.stopPropagation()}
                                >
                                  {/* Open Project */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setProjectDropdownOpen(null);
                                      handleLoadSavedFile(tab.projectId, tab.fileName, tab.image);
                                    }}
                                    style={{
                                      padding: '14px 20px',
                                      width: '100%',
                                      textAlign: 'left',
                                      border: 'none',
                                      background: 'white',
                                      cursor: 'pointer',
                                      fontFamily: 'Avenir, sans-serif',
                                      fontSize: '0.95rem',
                                      color: '#333',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      borderBottom: '1px solid #f0f0f0',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                  >
                                    <FiArrowRight size={16} />
                                    Open Project
                                  </button>
                                  {/* Project Settings */}
                                        <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setProjectDropdownOpen(null);
                                          setProjectSettingsPopup(tab);
                                        }}
                                        style={{
                                          padding: '14px 20px',
                                          width: '100%',
                                          textAlign: 'left',
                                          border: 'none',
                                          background: 'white',
                                          cursor: 'pointer',
                                          fontFamily: 'Avenir, sans-serif',
                                          fontSize: '0.95rem',
                                          color: '#333',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '10px',
                                          borderBottom: '1px solid #f0f0f0',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                      >
                                        <GrDocumentConfig size={16} />
                                        Project Settings
                                      </button>
                                  {/* Delete Project */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setProjectDropdownOpen(null);
                                      setDeleteProjectPopup(tab);
                                    }}
                                    style={{
                                      padding: '14px 20px',
                                      width: '100%',
                                      textAlign: 'left',
                                      border: 'none',
                                      background: 'white',
                                      cursor: 'pointer',
                                      fontFamily: 'Avenir, sans-serif',
                                      fontSize: '0.95rem',
                                      color: '#e02f1d',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#fee'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                  >
                                    <FiTrash2 size={16} />
                                    Delete Project
                                  </button>
                                </div>
                              </>,
                              document.body
                            )}
                          </>
                        )}
                      </div>
                      <span className="tab-label">{tab.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mobile-scrollbar-container">
                <div className="mobile-scrollbar-thumb" ref={(el) => {
                  if (el && galleryRef.current) {
                    const gallery = galleryRef.current;
                    const updateThumb = () => {
                      const scrollLeft = gallery.scrollLeft;
                      const maxScroll = gallery.scrollWidth - gallery.clientWidth;
                      const containerWidth = gallery.clientWidth;
                      const thumbWidth = Math.max(40, (containerWidth / gallery.scrollWidth) * containerWidth);
                      const maxThumbTravel = containerWidth - thumbWidth;
                      const thumbPosition = maxScroll > 0 ? (scrollLeft / maxScroll) * maxThumbTravel : 0;
                      el.style.width = thumbWidth + 'px';
                      el.style.transform = `translateX(${thumbPosition}px)`;
                    };
                    gallery.addEventListener('scroll', updateThumb, { passive: true });
                    updateThumb();
                  }
                }} />
              </div>
            </div>
          )}

          {/* Mobile Bottom Menu */}
          {!catalogActive && (
            <div className="bottom-menu-nav-div" onClick={() => setProjectDropdownOpen(null)}>
              <nav className={`menu-nav ${!isPWA ? 'browser-mode' : ''}`}>
                <button className={`menu-item ${!isPWA ? 'browser-mode' : ''}`} onClick={handleNewProjectClick}>New Project</button>
                <button className={`menu-item ${!isDashboardAvailable ? 'disabled' : ''} ${!isPWA ? 'browser-mode' : ''}`} onClick={() => { 
                  if (!isDashboardAvailable) { alert('Please log in to access Renders'); return; } 
                  if (rendersActive) { 
                    closeRendersPanel(); 
                  } else { 
                    openRendersPanel(); 
                  } 
                }}>Renders</button>
                <button className={`menu-item ${!isPWA ? 'browser-mode' : ''}`} onClick={() => { 
                  if (assetLibraryActive) { 
                    setAssetLibraryActive(false); 
                  } else { 
                    setAssetLibraryActive(true); 
                    setCatalogActive(false);
                    setRendersActive(false);
                  } 
                }}>Asset Library</button>
                <button className={`menu-item ${!isPWA ? 'browser-mode' : ''}`} onClick={() => { 
                  if (catalogActive) { 
                    setCatalogActive(false); 
                  } else { 
                    setCatalogActive(true); 
                    setShowPhotoDetails(false); 
                    setShowStyleGuidesFull(false);
                    setAssetLibraryActive(false);
                    setRendersActive(false);
                  } 
                }}>Creative Catalog</button>
                <button className={`menu-item ${!isPWA ? 'browser-mode' : ''}`} onClick={() => { 
                  if (showMobileSettingsView) { 
                    setShowMobileSettingsView(false); 
                  } else { 
                    setShowMobileSettingsView(true);
                    setCatalogActive(false);
                    setAssetLibraryActive(false);
                    setRendersActive(false);
                  } 
                }}>Settings</button>


              </nav>
              <div className={`camera-action-container ${!isPWA ? 'browser-mode' : ''}`}>
                <button className="camera-action-button" onClick={handleCameraClick}><MdOutlinePhotoCamera size={24} /></button>
              </div>
            </div>
          )}

          {/* Mobile Settings View */}
          {showMobileSettingsView && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: 'calc(var(--vh, 1vh) * 100)', background: '#fafafa', zIndex: 2004, display: 'flex', flexDirection: 'column', overflowY: 'auto', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', flexShrink: 0, background: '#fafafa', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid #eaeaea' }}>
                <button onClick={() => setShowMobileSettingsView(false)} style={{ background: 'rgba(217, 217, 217, 1)', color: '#ffffff', padding: '8px 16px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}><FiArrowLeft size={14} /> Back</button>
                <h2 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.1rem', color: '#333', letterSpacing: '-0.3px', margin: 0 }}>Settings</h2>
              </div>
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #eaeaea' }}>
                  <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#333', marginBottom: '14px' }}>Auto-Save</h3>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#666' }}>Enable Auto-Save</span>
                    <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                      <input type="checkbox" defaultChecked style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#88B7FF', borderRadius: '24px', transition: '0.3s' }}><span style={{ position: 'absolute', height: '18px', width: '18px', left: '22px', bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.3s' }} /></span>
                    </label>
                  </div>
                  <div>
                    <label style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '6px' }}>Save Interval: 5 minutes</label>
                    <input type="range" min="1" max="30" defaultValue="5" style={{ width: '100%', accentColor: '#ccc', background: '#e0e0e0', height: '4px', borderRadius: '2px', WebkitAppearance: 'none', appearance: 'none' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#999', fontFamily: 'Avenir, sans-serif' }}><span>1 min</span><span>30 min</span></div>
                  </div>
                </div>
                <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #eaeaea' }}>
                  <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#333', marginBottom: '14px' }}>Default Export Quality</h3>
                  <div>
                    <label style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '6px' }}>Quality: 85%</label>
                    <input type="range" min="10" max="100" defaultValue="85" style={{ width: '100%', accentColor: '#ccc', background: '#e0e0e0', height: '4px', borderRadius: '2px', WebkitAppearance: 'none', appearance: 'none' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#999', fontFamily: 'Avenir, sans-serif' }}><span>Low (10%)</span><span>High (100%)</span></div>
                  </div>
                </div>
                <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #eaeaea' }}>
                  <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#333', marginBottom: '14px' }}>Default Export Format</h3>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {['JPG', 'PNG', 'WEBP', 'TIFF'].map((format) => (
                      <button key={format} style={{ padding: '8px 16px', borderRadius: '12px', border: format === 'JPG' ? '2px solid #88B7FF' : '1px solid #ddd', background: format === 'JPG' ? '#88B7FF' : 'white', color: format === 'JPG' ? 'white' : '#333', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', fontWeight: 500 }}>{format}</button>
                    ))}
                  </div>
                </div>
                {/* Debug Panel & Developer Console */}
                {isDeveloper && (
                  <>
                    <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #eaeaea' }}>
                      <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#333', marginBottom: '14px' }}>Developer Debug</h3>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button onClick={() => { showFullScreenModal({ trigger: FULLSCREEN_TRIGGERS.TRIAL_ENDED, plan: 'starter', remaining: { iterations: 0, videos: 0 }, onUpgrade: () => navigate('/pricing') }); }} style={{ padding: '6px 12px', borderRadius: '20px', border: 'none', background: '#ff9800', color: 'white', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'Avenir, sans-serif' }}>Trial Ended</button>
                        <button onClick={() => { showFullScreenModal({ trigger: FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_ITERATIONS, plan: 'creator', remaining: { iterations: 0, videos: 5 }, onUpgrade: () => navigate('/pricing') }); }} style={{ padding: '6px 12px', borderRadius: '20px', border: 'none', background: '#88B7FF', color: 'white', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'Avenir, sans-serif' }}>Out of Iterations</button>
                        <button onClick={() => { showFullScreenModal({ trigger: FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_VIDEOS, plan: 'pro', remaining: { iterations: 10, videos: 0 }, onUpgrade: () => navigate('/pricing') }); }} style={{ padding: '6px 12px', borderRadius: '20px', border: 'none', background: '#88B7FF', color: 'white', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'Avenir, sans-serif' }}>Out of Videos</button>
                      </div>
                    </div>
                    <button onClick={() => { setShowMobileSettingsView(false); setShowMobileDeveloperView(true); }} style={{ background: '#88B7FF', color: 'white', border: 'none', borderRadius: '30px', padding: '14px 20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '1rem', fontWeight: 500, width: '100%' }}>Developer Console</button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Mobile Creative Catalog */}
          {catalogActive && !showStyleGuidesFull && (
            <div data-mobile-catalog style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#FAFAFA', overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative', paddingBottom: 0, overscrollBehavior: 'none' }}>
              {showPhotoDetails && selectedPhoto ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', flexShrink: 0, borderBottom: '1px solid #eaeaea', background: '#FAFAFA', position: 'sticky', top: 0, zIndex: 10 }}>
                    <button onClick={() => { setShowPhotoDetails(false); setSelectedPhoto(null); }} style={{ background: 'rgba(217, 217, 217, 1)', color: '#ffffff', padding: '8px 16px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}><FiArrowLeft size={14} /> Back</button>
                    <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', margin: 0 }}>Photo Details</h3>
                  </div>
                  <div style={{ background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', flex: 1, minHeight: '250px', position: 'relative' }}>
                    <img src={selectedPhoto.urls?.regular || selectedPhoto.urls?.full} alt={selectedPhoto.alt_description || 'Selected photo'} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px', objectFit: 'contain' }} />
                    <button onClick={() => { const idx = unsplashPhotos.findIndex(p => p.id === selectedPhoto.id); if (idx > 0) { const prev = unsplashPhotos[idx - 1]; setSelectedPhoto(prev); triggerUnsplashDownload(prev); } }} disabled={unsplashPhotos.findIndex(p => p.id === selectedPhoto.id) <= 0} style={{ position: 'absolute', left: '24px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255, 255, 255, 0.9)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#333', zIndex: 10, opacity: unsplashPhotos.findIndex(p => p.id === selectedPhoto.id) <= 0 ? 0.3 : 1, padding: 0 }}><FiChevronLeft size={20} /></button>
                    <button onClick={() => { const idx = unsplashPhotos.findIndex(p => p.id === selectedPhoto.id); if (idx < unsplashPhotos.length - 1) { const next = unsplashPhotos[idx + 1]; setSelectedPhoto(next); triggerUnsplashDownload(next); } }} disabled={unsplashPhotos.findIndex(p => p.id === selectedPhoto.id) >= unsplashPhotos.length - 1} style={{ position: 'absolute', right: '24px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255, 255, 255, 0.9)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#333', zIndex: 10, opacity: unsplashPhotos.findIndex(p => p.id === selectedPhoto.id) >= unsplashPhotos.length - 1 ? 0.3 : 1, padding: 0 }}><FiChevronRight size={20} /></button>
                  </div>
                  <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', background: 'white', flexShrink: 0 }}>
                    <div><h4 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#333', marginBottom: '6px' }}>Photographer</h4><p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#88b7ff', margin: 0 }}>{selectedPhoto.user?.name || 'Unknown Photographer'}{selectedPhoto.user?.username && ` (@${selectedPhoto.user.username})`}</p></div>
                    {selectedPhoto.description && <div><h4 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#333', marginBottom: '6px' }}>Description</h4><p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#666', margin: 0 }}>{selectedPhoto.description}</p></div>}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleAddToMoodBoard(selectedPhoto)} style={{ flex: 1, padding: '10px', background: '#88b7ff', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', fontWeight: 500 }}>Add to Mood Board</button>
                      <button onClick={() => { window.open(selectedPhoto.links?.html || `https://unsplash.com/photos/${selectedPhoto.id}`, '_blank'); triggerUnsplashDownload(selectedPhoto); }} style={{ flex: 1, padding: '10px', background: 'white', color: '#333', border: '1px solid #e0e0e0', borderRadius: '10px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem' }}>View on Unsplash</button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', flexShrink: 0, borderBottom: '1px solid #eaeaea', background: '#FAFAFA', position: 'sticky', top: 0, zIndex: 10 }}>
                    <button onClick={() => { setCatalogActive(false); setActiveNavItem(null); }} style={{ background: 'rgba(217, 217, 217, 1)', color: '#ffffff', padding: '8px 16px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}><FiArrowLeft size={14} /> Back</button>
                    <h2 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.1rem', color: '#333', letterSpacing: '-0.3px', margin: 0 }}>Creative Catalog</h2>
                  </div>
                  <div style={{ padding: '12px 16px', flexShrink: 0 }}>
                    <form onSubmit={handleCatalogSearch} style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" placeholder="Search Catalog ..." value={catalogSearchQuery} onChange={(e) => setCatalogSearchQuery(e.target.value)} style={{ flex: 1, padding: '10px 14px', border: '1px solid #ddd', borderRadius: '20px', fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', outline: 'none' }} />
                      <button type="submit" style={{ background: '#88b7ff', color: 'white', border: 'none', borderRadius: '20px', padding: '10px 16px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem' }}>Search</button>
                    </form>
                  </div>
                  <div style={{ padding: '0 16px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#88b7ff', color: 'white', padding: '5px 12px', borderRadius: '16px', fontFamily: 'Avenir, sans-serif', fontWeight: 500, fontSize: '0.75rem' }}><HiOutlineSwatch size={12} /> Style Guides</div>
                      <button onClick={() => setShowStyleGuidesFull(true)} style={{ background: 'none', border: 'none', color: '#88b7ff', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', cursor: 'pointer' }}>View All</button>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '8px', WebkitOverflowScrolling: 'touch' }}>
                      {styleGuides.map((guide, idx) => (
                        <div key={guide.id} onClick={() => { setActiveStyleGuide(idx); handleStyleGuideClick(guide); }} style={{ flexShrink: 0, width: '140px', borderRadius: '10px', overflow: 'hidden', background: 'white', border: '1px solid #eaeaea', cursor: 'pointer' }}>
                          <div style={{ height: '100px', overflow: 'hidden' }}><LazyImage src={guide.image} alt={guide.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>
                          <div style={{ padding: '8px' }}><div style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.7rem', color: '#333' }}>{guide.title}</div><div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.65rem', color: '#888' }}>{guide.description}</div></div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ height: '1px', background: '#eaeaea', margin: '12px 16px', flexShrink: 0 }} />
                  <div style={{ padding: '16px 16px 0 16px', flex: 1, position: 'relative', minHeight: '200px', overflowY: 'auto' }}>
                    <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#333', margin: '0 0 10px 0' }}>Interior Design Inspiration</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                      {unsplashLoading && unsplashPhotos.length === 0 && <div style={{ display: 'flex', gap: '8px' }}><SkeletonShimmer width="48%" height="130px" borderRadius="10px" /><SkeletonShimmer width="48%" height="130px" borderRadius="10px" style={{ opacity: 0.6 }} /></div>}
                      {unsplashPhotos.slice(0, 6).map((photo) => (
                        <div key={photo.id} onClick={() => handlePhotoClick(photo)} style={{ borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', aspectRatio: '4/3' }}><LazyImage src={photo.urls?.small || photo.urls?.regular}                             alt={photo.alt_description || 'Unsplash photo'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      ))}
                    </div>
                    {unsplashPhotos.length > 0 && (
                      <button onClick={handleLoadMorePhotos} disabled={unsplashLoading} style={{ display: 'block', margin: '16px auto', padding: '10px 24px', background: '#88b7ff', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem' }}>
                        {unsplashLoading ? 'Loading...' : 'Load More'}
                      </button>
                    )}
                    {showInteriorDesignComingSoon && (
                      <ComingSoonOverlay
                        section="interior-design"
                        style={{ justifyContent: 'flex-start', paddingTop: '20px' }}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Home Button */}
        {!catalogActive && !rendersActive && !assetLibraryActive && !showMobileSettingsView && (
          <button className={`mobile-home-button ${!isPWA ? 'browser-mode' : ''}`} onClick={() => navigate('/')} aria-label="Home">
            <FiHome size={26} />
          </button>
        )}

        {/* Hidden File Inputs */}
        <input type="file" ref={fileInputRef} accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileChange(e, false)} />
        <input type="file" ref={profileFileInputRef} accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileChange(e, true)} />

        {/* ============================================ */}
        {/* PROFILE OVERLAY - MOBILE */}
        {/* ============================================ */}
        {showProfileOverlay && (
          <div className="profile-overlay" style={{ background: '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px',  flexShrink: 0, background: '#fafafa', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid #eaeaea' }}>
              <button onClick={handleCancelProfile} style={{ background: 'rgba(217, 217, 217, 1)', color: '#ffffff', padding: '8px 16px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}><FiArrowLeft size={14} /> Back</button>
              <div style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.1rem', color: '#333', letterSpacing: '-0.3px', margin: 0, flex: 1, textAlign: 'center' }}>Profile</div>
              <button onClick={handleSaveProfile} style={{ background: '#88B7FF', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', fontWeight: 500 }}>Save</button>
            </div>
            <div className="profile-content">
              {!authUser ? (
                <>
                  <div className="profile-avatar-section"><div className="profile-avatar-large" style={{ borderColor: '#88B7FF' }}><FiUser size={40} color="#666" /></div></div>
                  <p style={{ color: '#333', fontFamily: 'Avenir, sans-serif', textAlign: 'center' }}>Please log in to view and edit your profile</p>
                  <button className="profile-action-btn" onClick={() => { setShowProfileOverlay(false); navigate('/'); }} style={{ textAlign: 'center', background: '#88B7FF', color: 'white', border: 'none' }}>Go to Login</button>
                </>
              ) : (
                <>
                  <div className="profile-avatar-section">
                    <div className="profile-avatar-large" style={{ borderColor: '#88B7FF' }}>
                      {avatarPreview ? <img src={avatarPreview} alt="User Avatar" className="profile-avatar-image" /> : getAvatarUrl() ? <img src={getAvatarUrl()} alt="User Avatar" className="profile-avatar-image" /> : <FiUser size={40} color="#666" />}
                    </div>
                    <button className="change-avatar-button" onClick={handleChooseProfileAvatar} style={{ background: '#88B7FF', color: 'white' }}>Change Avatar</button>
                    {avatarPreview && (
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button onClick={handleSaveAvatar} disabled={isSavingAvatar} style={{ padding: '8px 16px', backgroundColor: '#88B7FF', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', opacity: isSavingAvatar ? 0.6 : 1 }}>{isSavingAvatar ? 'Saving...' : 'Save Avatar'}</button>
                        <button onClick={handleCancelAvatar} style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ccc', borderRadius: '20px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    )}
                  </div>
                  <div className="profile-name-section">
                    <div className="profile-name-label" style={{ color: '#333' }}>Display Name</div>
                    <div className="profile-name-input-wrapper" style={{ background: '#f0f0f0' }}>
                      <input type="text" className="profile-name-input" placeholder="Enter your name" value={tempUserName} onChange={(e) => setTempUserName(e.target.value)} readOnly={!isEditingName} autoComplete="off" name="profile-name" id="profile-name" autoCorrect="off" autoCapitalize="sentences" spellCheck="false" style={{ color: '#333' }} />
                      <button className="profile-edit-button" onClick={handleEditName} style={{ color: '#666' }}><FiEdit2 size={16} /></button>
                    </div>
                  </div>
                  <div style={{ width: '100%', maxWidth: '300px', position: 'relative' }}>
                    <div
                      onClick={() => setShowMobileEmailDropdown(!showMobileEmailDropdown)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        background: '#f0f0f0',
                        borderRadius: '30px',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease',
                        color: '#333',
                        fontFamily: 'Avenir, sans-serif',
                        fontSize: '0.9rem',
                      }}
                      onMouseEnter={(e) => { if (!showMobileEmailDropdown) e.currentTarget.style.background = '#e5e5e5'; }}
                      onMouseLeave={(e) => { if (!showMobileEmailDropdown) e.currentTarget.style.background = '#f0f0f0'; }}
                    >
                      <span>{authUser?.email}</span>
                      <FaChevronDown
                        size={18}
                        style={{
                          color: '#999',
                          transition: 'transform 0.3s ease',
                          transform: showMobileEmailDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    </div>
                    {showMobileEmailDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: 'white',
                        borderRadius: '12px',
                        
                        overflow: 'hidden',
                        zIndex: 10,
                        border: '1px solid #eaeaea',
                      }}>
                        <button
                          onClick={() => {
                            setShowMobileEmailDropdown(false);
                            setShowEmailChangePopup(true);
                          }}
                          style={{
                            padding: '12px 16px',
                            width: '100%',
                            textAlign: 'left',
                            border: 'none',
                            background: 'white',
                            color: '#333',
                            cursor: 'pointer',
                            fontFamily: 'Avenir, sans-serif',
                            fontSize: '0.85rem',
                            transition: 'background 0.2s ease',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          Change Email
                        </button>
                        <button
                          onClick={() => {
                            setShowMobileEmailDropdown(false);
                            setShowPasswordChangePopup(true);
                          }}
                          style={{
                            padding: '12px 16px',
                            width: '100%',
                            textAlign: 'left',
                            border: 'none',
                            borderTop: '1px solid #f0f0f0',
                            background: 'white',
                            color: '#333',
                            cursor: 'pointer',
                            fontFamily: 'Avenir, sans-serif',
                            fontSize: '0.85rem',
                            transition: 'background 0.2s ease',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          Change Password
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ width: '100%', maxWidth: '300px', background: 'white', padding: '12px 16px', borderRadius: '12px', color: '#333', fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem', textAlign: 'center', border: '1px solid #88B7FF' }}>
                    <span style={{ color: '#666', marginRight: '8px' }}>Current Plan:</span>
                    <span style={{ color: '#88B7FF', fontWeight: '600' }}>{getCurrentPlanName().toUpperCase()}</span>
                    {getCurrentBillingCycle() && <span style={{ color: '#666', fontSize: '0.8rem', marginLeft: '6px' }}>({getCurrentBillingCycle()})</span>}
                  </div>

                  {/* Usage Pills & Add Balance */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '300px' }}>
                    <div style={{
                      background: '#f0f0f0', borderRadius: '20px', padding: '6px 14px',
                      display: 'flex', alignItems: 'center', gap: '6px',
                      fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: '#333',
                    }}>
                      <RiImageCircleAiLine size={14} color="#333" />
                      <span>{Math.round(imagesRemaining)} iterations</span>
                    </div>
                    <div style={{
                      background: '#f0f0f0', borderRadius: '20px', padding: '6px 14px',
                      display: 'flex', alignItems: 'center', gap: '6px',
                      fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: '#333',
                    }}>
                      <RiVideoOnAiLine size={14} color="#333" />
                      <span>{Math.round(videosRemaining)} videos</span>
                    </div>
                    <button
                      onClick={() => {
                        const planName = getCurrentPlanName().toLowerCase() || 'starter';
                        showFullScreenModal({
                          trigger: FULLSCREEN_TRIGGERS.NOT_ENOUGH_BALANCE_ITERATIONS,
                          plan: planName,
                          remaining: { iterations: imagesRemaining, videos: videosRemaining },
                          onUpgrade: () => navigate('/pricing'),
                          onAddBalance: () => navigate('/pricing'),
                          initialView: 'add-balance',
                        });
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#88B7FF',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        transition: 'background 0.2s ease',
                        flexShrink: 0,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#6a9fdf'}
                      onMouseLeave={e => e.currentTarget.style.background = '#88B7FF'}
                    >
                      +
                    </button>
                  </div>
                                 <div className="profile-action-buttons" style={{ display: 'none' }}></div>
                <button className="profile-signout-btn" onClick={handleSignOut} style={{ borderColor: '#ff6b6b', color: '#ff6b6b', background: 'transparent' }}>Sign Out</button>
                </>
              )}
            </div>
          </div>
        )}

                {/* Mobile Developer View */}
        {showMobileDeveloperView && isDeveloper && (
                      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: 'calc(var(--vh, 1vh) * 100)', background: '#fafafa', zIndex: 2005, display: 'flex', flexDirection: 'column', overflowY: 'auto', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', flexShrink: 0, background: '#fafafa', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid #eaeaea' }}>
              <button onClick={() => setShowMobileDeveloperView(false)} style={{ background: 'rgba(217, 217, 217, 1)', color: '#ffffff', padding: '8px 16px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}>
                <FiArrowLeft size={14} /> Back
              </button>
              <h2 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.1rem', color: '#333', margin: 0 }}>Developer Console</h2>
            </div>
            
            {/* Mobile Splash Screen with Buttons */}
            {!selectedDeveloperModule ? (
              <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                <FaCog size={48} color="#88b7ff" style={{ marginBottom: '16px' }} />
                <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1.3rem', color: '#333', marginBottom: '8px' }}>Developer Dashboard</h3>
                <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888', marginBottom: '32px' }}>Select a module to manage</p>

                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Account Management Button */}
                        <button
                          onClick={() => setSelectedDeveloperModule('account')}
                          style={{
                            background: 'white',
                            border: '1px solid #eaeaea',
                            borderRadius: '16px',
                            padding: '24px 20px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                            width: '100%',
                          }}
                        >
                          <FiUsers size={32} color="#88b7ff" style={{ marginBottom: '12px' }} />
                          <h4 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', marginBottom: '4px' }}>Account Management</h4>
                          <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.7rem', color: '#888' }}>Manage subscriptions, roles & stats</p>
                        </button>

                        {/* Marketing Studio Button */}
                        <button
                          onClick={() => {
                            setShowMobileDeveloperView(false);
                            setShowDevStudio(true);
                          }}
                          style={{
                            background: 'white',
                            border: '1px solid #eaeaea',
                            borderRadius: '16px',
                            padding: '24px 20px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                            width: '100%',
                          }}
                        >
                          <RiImageCircleAiLine size={32} color="#88b7ff" style={{ marginBottom: '12px' }} />
                          <h4 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', marginBottom: '4px' }}>Marketing Studio</h4>
                          <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.7rem', color: '#888' }}>Generate 4K 9:16 images for social media</p>
                        </button>

                        {/* Welcome Splash Button */}
                        <button
                          onClick={() => navigate('/welcome')}
                          style={{
                            background: 'white',
                            border: '1px solid #eaeaea',
                            borderRadius: '16px',
                            padding: '24px 20px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                            width: '100%',
                          }}
                        >
                          <FiHome size={32} color="#88b7ff" style={{ marginBottom: '12px' }} />
                          <h4 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', marginBottom: '4px' }}>Welcome Splash</h4>
                          <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.7rem', color: '#888' }}>View the welcome splash screen</p>
                        </button>
                      </div>
              </div>
            ) : (
              /* Account Management Content (when module selected) */
              <div style={{ padding: '16px' }}>
                <button
                  onClick={() => setSelectedDeveloperModule(null)}
                  style={{
                    background: 'rgba(217, 217, 217, 1)',
                    color: '#ffffff',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: 'none',
                    marginBottom: '16px',
                  }}
                >
                  <FiArrowLeft size={14} /> Back to Dashboard
                </button>

                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ background: 'white', borderRadius: '12px', padding: '12px', border: '1px solid #eaeaea' }}>
                    <div style={{ fontSize: '0.65rem', color: '#888' }}>Total Users</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>{usersList.length}</div>
                  </div>
                  <div style={{ background: 'white', borderRadius: '12px', padding: '12px', border: '1px solid #eaeaea' }}>
                    <div style={{ fontSize: '0.65rem', color: '#888' }}>Pro Users</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>{usersList.filter(u => u.subscriptionPlan === 'pro').length}</div>
                  </div>
                </div>

                {/* Search & Refresh */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <input type="text" placeholder="Search users..." value={searchUsersQuery} onChange={(e) => setSearchUsersQuery(e.target.value)}
                    style={{ flex: 1, padding: '10px 14px', border: '1px solid #ddd', borderRadius: '20px', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', outline: 'none' }} />
                  <button onClick={fetchAllUsers} style={{ background: '#88b7ff', border: 'none', padding: '10px 20px', borderRadius: '20px', color: 'white', cursor: 'pointer', fontSize: '0.8rem' }}>Refresh</button>
                </div>

                {/* Users List */}
                {usersLoading ? (
                  <SkeletonShimmer width="100%" height="200px" borderRadius="12px" />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {usersList.filter(u => u.email?.toLowerCase().includes(searchUsersQuery.toLowerCase())).map((user) => (
                      <div key={user.id} style={{ background: 'white', borderRadius: '12px', padding: '14px', border: '1px solid #eaeaea' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div>
                            <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#333' }}>{user.displayName || user.firstName || 'User'}</div>
                            <div style={{ fontSize: '0.7rem', color: '#888' }}>{user.email}</div>
                          </div>
                          <span style={{ background: user.subscriptionPlan === 'pro' ? '#e8f5e9' : '#fff3e0', color: user.subscriptionPlan === 'pro' ? '#2e7d32' : '#e65100', padding: '2px 8px', borderRadius: '12px', fontSize: '0.65rem' }}>
                            {user.subscriptionPlan || 'trial'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '8px' }}>
                          Usage: {user.usage?.imageEquivalents || 0} images
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button onClick={() => setEditUserPlan({ show: true, userId: user.id, currentPlan: user.subscriptionPlan || 'trial', newPlan: '', billingCycle: '' })}
                            style={{ background: '#88b7ff', border: 'none', padding: '4px 10px', borderRadius: '12px', color: 'white', fontSize: '0.7rem', cursor: 'pointer' }}>Change Plan</button>
                          <button onClick={() => handleResetUserUsage(user.id, user.email)}
                            style={{ background: '#ff9800', border: 'none', padding: '4px 10px', borderRadius: '12px', color: 'white', fontSize: '0.7rem', cursor: 'pointer' }}>Reset Usage</button>
                          <button onClick={() => handleSyncUserAccount(user.id, user.email)}
                            style={{ background: '#4caf50', border: 'none', padding: '4px 10px', borderRadius: '12px', color: 'white', cursor: 'pointer', fontSize: '0.7rem' }}>Sync Account</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* LOADING OVERLAY */}
        {/* ============================================ */}
        {showLoadingOverlay && (
          <div className={`loading-overlay ${showLoadingOverlay ? 'show' : ''}`}>
            {!isLoadingActive ? (
              <>
                <h2 className="project-name-title">
                  {loadingMode === 'load-file' ? 'Loading Project' :
                   loadingMode === 'camera-mode' && cameraModeStep === 'photo' ? 'Review Your Photo' :
                   loadingMode === 'camera-mode' && cameraModeStep === 'name' ? 'Name Your Project' :
                   loadingMode === 'prompt' && projectCreationStep === 'name' ? 'Name Your Project' :
                   loadingMode === 'prompt' && projectCreationStep === 'photo' ? 'Add Your Photo' :
                   loadingMode === 'prompt' && projectCreationStep === 'confirm' ? 'Confirm Project' :
                   loadingMode === 'new-project' && projectCreationStep === 'name' ? 'Name Your Project' :
                   loadingMode === 'new-project' && projectCreationStep === 'photo' ? 'Add Your Photo' :
                   loadingMode === 'new-project' && projectCreationStep === 'confirm' ? 'Confirm Project' :
                   (showPhotoStep ? 'Add Your Photo' : 'Name Your Project')}
                </h2>
                <div className="project-name-container">
                  {/* Camera Mode - Photo Step */}
                  {loadingMode === 'camera-mode' && cameraModeStep === 'photo' && (
                    <>
                      <div className="photo-selection-area">
                        {(tempPhoto || projectPhoto) ? <img src={tempPhoto || projectPhoto} alt="Selected preview" className="photo-thumbnail" /> :
                          <div className="empty-photo-area">
                            <div className="photo-icons">
                              <button className="photo-icon-btn" onClick={() => fileInputRef.current?.click()}><FiImage size={32} /><span>Upload</span></button>
                              <button className="photo-icon-btn" onClick={handleCameraClickWithPhotoStep}><FiCamera size={32} /><span>Camera</span></button>
                            </div>
                          </div>
                        }
                      </div>
                      <div className="project-name-buttons">
                        <button className="project-name-button cancel-button" onClick={handleCameraModeBack}>Back</button>
                        <button className="project-name-button" onClick={handleCameraModeContinue} disabled={!(tempPhoto || projectPhoto)}>Continue</button>
                      </div>
                    </>
                  )}
                  {/* Camera Mode - Name Step */}
                  {loadingMode === 'camera-mode' && cameraModeStep === 'name' && (
                    <>
                      <div className="photo-thumbnail-container">
                        {(projectPhoto || tempPhoto) ? <img src={projectPhoto || tempPhoto} alt="Project preview" className="photo-thumbnail" /> : <div className="photo-placeholder-grey"></div>}
                      </div>
                      <div className="project-name-input-wrapper">
                        <input type="text" className="project-name-input" placeholder="Enter project name" value={projectName} onChange={(e) => setProjectName(e.target.value)} onKeyPress={handleProjectNameKeyPress} maxLength={24} autoFocus autoComplete="off" name="search" id="search" autoCorrect="off" autoCapitalize="sentences" spellCheck="false" />
                      </div>
                      <div className="project-name-buttons">
                        <button className="project-name-button cancel-button" onClick={() => { setCameraModeStep('photo'); setProjectName(''); }}>Back</button>
                        <button className="project-name-button" onClick={handleCreateProject} disabled={!projectName.trim() || !(projectPhoto || tempPhoto)}>Create</button>
                      </div>
                    </>
                  )}
                  {/* Prompt Mode - Name Step */}
                  {loadingMode === 'prompt' && projectCreationStep === 'name' && (
                    <>
                      {projectDetails && <div className="project-details-display"><div className="project-details-label">Project Details:</div>{projectDetails}</div>}
                      <div className="project-name-input-wrapper">
                        <input type="text" className="project-name-input" placeholder="Enter project name" value={projectName} onChange={(e) => setProjectName(e.target.value)} onKeyPress={handleProjectNameKeyPress} maxLength={24} autoFocus autoComplete="off" name="search" id="search" autoCorrect="off" autoCapitalize="sentences" spellCheck="false" />
                      </div>
                      <div className="project-name-buttons">
                        <button className="project-name-button cancel-button" onClick={handleCancelProject}>Cancel</button>
                        <button className="project-name-button" onClick={handleContinueToPhotoStep} disabled={!projectName.trim()}>Continue</button>
                      </div>
                    </>
                  )}
                  {/* Prompt Mode - Photo Step */}
                  {loadingMode === 'prompt' && projectCreationStep === 'photo' && (
                    <>
                      {projectDetails && <div className="project-details-display"><div className="project-details-label">Project Details:</div>{projectDetails}</div>}
                      <div className="project-name-display">{projectName}</div>
                      <div className="photo-selection-area">
                        {(tempPhoto || projectPhoto) ? <img src={tempPhoto || projectPhoto} alt="Selected preview" className="photo-thumbnail" /> :
                          <div className="empty-photo-area">
                            <div className="photo-icons">
                              <button className="photo-icon-btn" onClick={() => fileInputRef.current?.click()}><FiImage size={32} /><span>Upload</span></button>
                              <button className="photo-icon-btn" onClick={handleCameraClickWithPhotoStep}><FiCamera size={32} /><span>Camera</span></button>
                            </div>
                          </div>
                        }
                      </div>
                     {(tempPhoto || projectPhoto) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', width: '100%', justifyContent: 'center' }}>
                        <input
                          type="checkbox"
                          id="photoPermission"
                          checked={hasPhotoPermission}
                          onChange={(e) => setHasPhotoPermission(e.target.checked)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label htmlFor="photoPermission" style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#777', cursor: 'pointer' }}>
                          I have permission to use this photo.
                        </label>
                      </div>
                    )}
                      <div className="project-name-buttons">
                        <button className="project-name-button cancel-button" onClick={handleBackToNameStep}>Back</button>
                        <button className="project-name-button" onClick={handleContinueToConfirmStep} disabled={!(tempPhoto || projectPhoto) || !hasPhotoPermission}>Continue</button>
                      </div>
                    </>
                  )}
                  {/* Prompt Mode - Confirm Step */}
                  {loadingMode === 'prompt' && projectCreationStep === 'confirm' && (
                    <>
                      <div className="photo-thumbnail-container">{(projectPhoto || tempPhoto) && <img src={projectPhoto || tempPhoto} alt="Project preview" className="photo-thumbnail" />}</div>
                      <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '12px', marginBottom: '16px', width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                          <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Project Name:</span>
                          <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '1rem', fontWeight: 500, color: '#333' }}>{projectName}</span>
                        </div>
                        {projectDetails && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                            <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Project Description:</span>
                            <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem', color: '#555' }}>{projectDetails}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                          <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Project Type:</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: projectType === 'Personal' ? '#88B7FF' : '#666', fontWeight: projectType === 'Personal' ? '500' : '400' }}>Personal</span>
                            <label style={{ position: 'relative', display: 'inline-block', width: '40px', height: '20px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={projectType === 'Client'}
                                onChange={(e) => setProjectType(e.target.checked ? 'Client' : 'Personal')}
                                style={{ opacity: 0, width: 0, height: 0 }}
                              />
                              <span style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                background: projectType === 'Client' ? '#88B7FF' : '#ccc',
                                borderRadius: '20px',
                                transition: '0.3s',
                              }}>
                                <span style={{
                                  position: 'absolute', height: '16px', width: '16px',
                                  left: projectType === 'Client' ? '20px' : '2px',
                                  bottom: '2px',
                                  background: 'white',
                                  borderRadius: '50%',
                                  transition: '0.3s',
                                }} />
                              </span>
                            </label>
                            <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: projectType === 'Client' ? '#88B7FF' : '#666', fontWeight: projectType === 'Client' ? '500' : '400' }}>Client</span>
                          </div>
                        </div>
                      </div>
                      <div className="project-name-buttons">
                        <button className="project-name-button cancel-button" onClick={handleBackToPhotoStep}>Back</button>
                        <button className="project-name-button" onClick={handleCreateProject}>Create</button>
                      </div>
                    </>
                  )}
                  {/* New Project - Name Step */}
                  {loadingMode === 'new-project' && projectCreationStep === 'name' && (
                    <>
                      <div className="project-name-input-wrapper">
                        <input type="text" className="project-name-input" placeholder="Enter project name" value={projectName} onChange={(e) => setProjectName(e.target.value)} onKeyPress={handleProjectNameKeyPress} maxLength={24} autoFocus autoComplete="off" name="search" id="search" autoCorrect="off" autoCapitalize="sentences" spellCheck="false" />
                      </div>
                      <div className="project-name-buttons">
                        <button className="project-name-button cancel-button" onClick={handleCancelProject}>Cancel</button>
                        <button className="project-name-button" onClick={handleContinueToPhotoStep} disabled={!projectName.trim()}>Continue</button>
                      </div>
                    </>
                  )}
                  {/* New Project - Photo Step */}
                  {loadingMode === 'new-project' && projectCreationStep === 'photo' && (
                    <>
                      <div className="project-name-display">{projectName}</div>
                      <div className="photo-selection-area">
                        {(tempPhoto || projectPhoto) ? <img src={tempPhoto || projectPhoto} alt="Selected preview" className="photo-thumbnail" /> :
                          <div className="empty-photo-area">
                            <div className="photo-icons">
                              <button className="photo-icon-btn" onClick={() => fileInputRef.current?.click()}><FiImage size={32} /><span>Upload</span></button>
                              <button className="photo-icon-btn" onClick={handleCameraClickWithPhotoStep}><FiCamera size={32} /><span>Camera</span></button>
                            </div>
                          </div>
                        }
                      </div>
                     {(tempPhoto || projectPhoto) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', width: '100%', justifyContent: 'center' }}>
                        <input
                          type="checkbox"
                          id="photoPermissionNew"
                          checked={hasPhotoPermission}
                          onChange={(e) => setHasPhotoPermission(e.target.checked)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label htmlFor="photoPermissionNew" style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#777', cursor: 'pointer' }}>
                          I have permission to use this photo.
                        </label>
                      </div>
                    )}
                      <div className="project-name-buttons">
                        <button className="project-name-button cancel-button" onClick={handleBackToNameStep}>Back</button>
                        <button className="project-name-button" onClick={handleContinueToConfirmStep} disabled={!(tempPhoto || projectPhoto) || !hasPhotoPermission}>Continue</button>
                      </div>
                    </>
                  )}
                  {/* New Project - Confirm Step */}
                  {loadingMode === 'new-project' && projectCreationStep === 'confirm' && (
                    <>
                      <div className="photo-thumbnail-container">{(projectPhoto || tempPhoto) && <img src={projectPhoto || tempPhoto} alt="Project preview" className="photo-thumbnail" />}</div>
                      <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '12px', marginBottom: '16px', width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                          <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Project Name:</span>
                          <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '1rem', fontWeight: 500, color: '#333' }}>{projectName}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                          <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Project Type:</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: projectType === 'Personal' ? '#88B7FF' : '#666', fontWeight: projectType === 'Personal' ? '500' : '400' }}>Personal</span>
                            <label style={{ position: 'relative', display: 'inline-block', width: '40px', height: '20px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={projectType === 'Client'}
                                onChange={(e) => setProjectType(e.target.checked ? 'Client' : 'Personal')}
                                style={{ opacity: 0, width: 0, height: 0 }}
                              />
                              <span style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                background: projectType === 'Client' ? '#88B7FF' : '#ccc',
                                borderRadius: '20px',
                                transition: '0.3s',
                              }}>
                                <span style={{
                                  position: 'absolute', height: '16px', width: '16px',
                                  left: projectType === 'Client' ? '20px' : '2px',
                                  bottom: '2px',
                                  background: 'white',
                                  borderRadius: '50%',
                                  transition: '0.3s',
                                }} />
                              </span>
                            </label>
                            <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: projectType === 'Client' ? '#88B7FF' : '#666', fontWeight: projectType === 'Client' ? '500' : '400' }}>Client</span>
                          </div>
                        </div>
                      </div>
                      <div className="project-name-buttons">
                        <button className="project-name-button cancel-button" onClick={handleBackToPhotoStep}>Back</button>
                        <button className="project-name-button" onClick={handleCreateProject}>Create</button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="loading-text">{loadingMode === 'load-file' ? (loadStatusText || `Loading Project ${loadingDots}`) : `Creating Project ${loadingDots}`}</div>
                <div className="loading-bar-container"><div className="loading-bar" style={{ width: `${loadingProgress}%` }}></div></div>
                <div className="loading-percentage">{loadingProgress}%</div>
              </>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* CAMERA OVERLAY */}
        {/* ============================================ */}
        {showCamera && (
          <div className="camera-overlay" style={{ transform: 'translateX(100%)' }}>
            <div className="camera-header">
              <button className="camera-close" onClick={handleCameraClose}>←</button>
              <div style={{ width: '24px' }}></div>
            </div>
            <div className="camera-preview">
              {capturedPhoto ? (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img src={capturedPhoto} alt="Captured" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', bottom: '20px', left: '0', right: '0', display: 'flex', justifyContent: 'center', gap: '20px' }}>
                    <button onClick={handleDeclinePhoto} className="decline-button">{photoSource === 'camera' ? 'Retake' : 'Choose Different'}</button>
                    <button onClick={handleConfirmPhoto} className="confirm-button">Use Photo</button>
                  </div>
                </div>
              ) : (
                <>
                  <video ref={videoRef} autoPlay playsInline muted className={`camera-live-preview ${zoomLevel === 0.5 ? 'ultra-wide' : ''} ${isZooming ? 'zooming' : ''}`} />
                  <div className={`shutter-effect ${showShutterEffect ? 'active' : ''}`}></div>
                </>
              )}
            </div>
            <div className="camera-controls">
              <button className="camera-gallery-button" onClick={handleChooseFromGallery}><FiImage size={24} /></button>
              <button className={`camera-shutter ${showShutterEffect ? 'clicked' : ''} ${capturedPhoto ? 'in-selection-mode' : ''}`} onClick={capturedPhoto ? undefined : handleTakePicture}>
                <FiCamera size={24} className="shutter-icon" />
              </button>
              <button className={`flash-button ${flashMode !== 'off' ? 'active' : ''}`} onClick={toggleFlash}>
                {flashMode === 'off' && <MdFlashOff size={24} />}
                {flashMode === 'on' && <MdFlashOn size={24} />}
                {flashMode === 'auto' && <MdFlashAuto size={24} />}
              </button>
              {!capturedPhoto && (
                <button className={`floating-zoom-toggle ${zoomLevel === 0.5 ? 'active' : ''} ${isZooming ? 'zooming' : ''}`}
                  onClick={toggleZoomSmooth} onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); toggleZoomSmooth(); }} disabled={isZooming}
                  style={{ position: 'absolute', bottom: '180px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', background: 'transparent', border: 'none' }}>
                  <LuShrink size={20} color="white" />
                  {zoomLevel === 0.5 && <div className="zoom-badge">0.5</div>}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* POPUPS - EMAIL CHANGE */}
      {/* ============================================ */}
      {showEmailChangePopup && (
        <div style={popupOverlayStyle} onClick={() => setShowEmailChangePopup(false)}>
          <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '20px', fontFamily: 'avenir-lt-pro, sans-serif' }}>Change Email</h3>
            <form onSubmit={handleChangeEmail}>
              <input type="email" placeholder="New Email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required style={popupInputStyle} />
              <input type="password" placeholder="Current Password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} required style={popupInputStyle} />
              {emailError && <p style={{ color: '#E53935', fontSize: '0.85rem', margin: '-5px 0 10px 0' }}>{emailError}</p>}
              {emailSuccess && <p style={{ color: '#4CAF50', fontSize: '0.85rem', margin: '-5px 0 10px 0' }}>{emailSuccess}</p>}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowEmailChangePopup(false)} style={popupCancelStyle}>Cancel</button>
                <button type="submit" style={popupConfirmStyle}>Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* POPUPS - PASSWORD CHANGE */}
      {/* ============================================ */}
      {showPasswordChangePopup && (
        <div style={popupOverlayStyle} onClick={() => setShowPasswordChangePopup(false)}>
          <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '20px', fontFamily: 'avenir-lt-pro, sans-serif' }}>Change Password</h3>
            <form onSubmit={handleChangePassword}>
              <input type="password" placeholder="Current Password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} required style={popupInputStyle} />
              <input type="password" placeholder="New Password (min 6 characters)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required style={popupInputStyle} />
              <input type="password" placeholder="Confirm New Password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} required style={popupInputStyle} />
              {passwordError && <p style={{ color: '#E53935', fontSize: '0.85rem', margin: '-5px 0 10px 0' }}>{passwordError}</p>}
              {passwordSuccess && <p style={{ color: '#4CAF50', fontSize: '0.85rem', margin: '-5px 0 10px 0' }}>{passwordSuccess}</p>}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowPasswordChangePopup(false)} style={popupCancelStyle}>Cancel</button>
                <button type="submit" style={popupConfirmStyle}>Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* POPUPS - DELETE DISCLAIMER */}
      {/* ============================================ */}
      {showDeleteDisclaimer && (
        <div style={popupOverlayStyle} onClick={() => setShowDeleteDisclaimer(false)}>
          <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '20px', fontFamily: 'avenir-lt-pro, sans-serif' }}>Delete Account</h3>
            <p style={{ color: '#666', marginBottom: '25px', fontFamily: 'avenir-lt-pro, sans-serif' }}>Account deletion is not yet available. Please contact support for assistance.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button onClick={() => setShowDeleteDisclaimer(false)} style={popupConfirmStyle}>OK</button></div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* POPUPS - PROJECT SETTINGS */}
      {/* ============================================ */}
      {projectSettingsPopup && (
        <div style={popupOverlayStyle} onClick={() => setProjectSettingsPopup(null)}>
          <div style={{ ...popupContentStyle, maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '4px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.1rem' }}>Project Settings</h3>
            <p style={{ color: '#888', marginBottom: '20px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.85rem' }}>{projectSettingsPopup?.label || projectSettingsPopup?.fileName || 'Untitled Project'}</p>
            <button onClick={() => { setProjectToRename(projectSettingsPopup); setNewProjectName(projectSettingsPopup?.label || projectSettingsPopup?.fileName || ''); setShowRenameProjectPopup(true); setProjectSettingsPopup(null); }}
            style={{ width: '100%', padding: '12px', background: '#88B7FF', border: 'none', borderRadius: '30px', color: 'white', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <FiEdit2 size={14} /> Rename Project
          </button>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', background: '#f8f8f8', padding: '12px', borderRadius: '10px' }}>
              <span style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', color: '#333' }}>Auto-Save</span>
              <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                <input type="checkbox" defaultChecked style={{ opacity: 0, width: 0, height: 0 }} />
                <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#88b7ff', borderRadius: '24px', transition: '0.3s' }}>
                  <span style={{ position: 'absolute', height: '18px', width: '18px', left: '22px', bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.3s' }} />
                </span>
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', background: '#f8f8f8', padding: '12px', borderRadius: '10px' }}>
              <span style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', color: '#333' }}>Project Description</span>
              <input type="text" placeholder="Add description" defaultValue={projectSettingsPopup?.details || ''} style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '6px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.8rem', width: '60%' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', background: '#f8f8f8', padding: '12px', borderRadius: '10px' }}>
              <span style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', color: '#333' }}>Project Type</span>
              <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                <input type="checkbox" style={{ opacity: 0, width: 0, height: 0 }} />
                <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#ccc', borderRadius: '24px', transition: '0.3s' }}>
                  <span style={{ position: 'absolute', height: '18px', width: '18px', left: '3px', bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.3s' }} />
                </span>
              </label>
            </div>
            <div style={{ marginBottom: '16px', background: '#f8f8f8', padding: '12px', borderRadius: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', color: '#333' }}>Default Export Quality</span>
                <span style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.8rem', color: '#88b7ff' }}>85%</span>
              </div>
              <input type="range" min="10" max="100" defaultValue="85" style={{ width: '100%', accentColor: '#88b7ff' }} />
            </div>
            <div style={{ marginBottom: '20px', background: '#f8f8f8', padding: '12px', borderRadius: '10px' }}>
              <span style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', color: '#333', display: 'block', marginBottom: '8px' }}>Default Export Format</span>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {['JPG', 'PNG', 'WEBP', 'TIFF'].map((format) => (
                  <button key={format} style={{ padding: '6px 14px', borderRadius: '20px', border: format === 'PNG' ? '2px solid #88b7ff' : '1px solid #ddd', background: format === 'PNG' ? '#e8f0ff' : 'white', color: '#333', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.8rem' }}>{format}</button>
                ))}
              </div>
            </div>
            <button onClick={() => { setDeleteProjectPopup(projectSettingsPopup); setProjectSettingsPopup(null); }}
              style={{ width: '100%', padding: '12px', background: 'transparent', border: '1px solid #e02f1d', borderRadius: '30px', color: '#e02f1d', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', transition: 'all 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#e02f1d'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#e02f1d'; }}>
              <FiTrash2 size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Delete Project
            </button>
            <button onClick={() => setProjectSettingsPopup(null)} style={{ width: '100%', padding: '10px', marginTop: '8px', background: '#88B7FF', border: 'none', borderRadius: '30px', color: 'white', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem' }}>Done</button>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* POPUPS - DELETE PROJECT CONFIRMATION */}
      {/* ============================================ */}
      {deleteProjectPopup && (
        <div style={popupOverlayStyle} onClick={() => setDeleteProjectPopup(null)}>
          <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '16px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.1rem' }}>Delete Project</h3>
            <p style={{ color: '#666', marginBottom: '24px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.95rem', lineHeight: 1.5 }}>
              Are you sure you want to delete <strong>"{deleteProjectPopup?.label || deleteProjectPopup?.fileName || 'this project'}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteProjectPopup(null)} style={popupCancelStyle}>No, Cancel</button>
              <button onClick={handleDeleteProject} disabled={isDeletingProject} style={{ ...popupConfirmStyle, backgroundColor: '#e02f1d', opacity: isDeletingProject ? 0.6 : 1 }}>
                {isDeletingProject ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* POPUPS - RENAME PROJECT */}
      {/* ============================================ */}
      {showRenameProjectPopup && (
        <div style={popupOverlayStyle} onClick={() => setShowRenameProjectPopup(false)}>
          <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '16px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.1rem' }}>Rename Project</h3>
            <input type="text" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} placeholder="Enter new project name" autoFocus style={popupInputStyle}
              onKeyPress={(e) => { if (e.key === 'Enter') handleRenameProject(); }} />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => { setShowRenameProjectPopup(false); setProjectToRename(null); setNewProjectName(''); }} style={popupCancelStyle}>Cancel</button>
              <button onClick={handleRenameProject} disabled={!newProjectName.trim()} style={{ ...popupConfirmStyle, opacity: !newProjectName.trim() ? 0.5 : 1 }}>Rename</button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* RENDERS PANEL - Desktop and Mobile */}
      {/* ============================================ */}
      {rendersActive && (
        <div 
          className={`renders-panel-slide ${window.innerWidth > 767 ? 'desktop-mode' : ''}`}
          style={{
            position: 'fixed',
            top: 0,
            left: window.innerWidth > 767 ? 0 : '-2px',
            right: 0,
            bottom: 0,
            width: window.innerWidth > 767 ? '100%' : 'calc(100% + 4px)',
            height: '100dvh',
            zIndex: 1001,
            transform: window.innerWidth > 767 ? 'translateX(0)' : 'translateX(calc(100vw + 2px))',
            backgroundColor: window.innerWidth > 767 ? 'rgba(0,0,0,0.5)' : '#000',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: window.innerWidth > 767 ? 'blur(4px)' : 'none',
          }}
          onClick={(e) => {
            // Close when clicking overlay on desktop
            if (window.innerWidth > 767 && e.target === e.currentTarget) {
              closeRendersPanel();
            }
          }}
        >
          <div style={{
            width: window.innerWidth > 767 ? '90%' : '100%',
            maxWidth: window.innerWidth > 767 ? '1400px' : '100%',
            height: window.innerWidth > 767 ? '90vh' : '100%',
            maxHeight: window.innerWidth > 767 ? 'calc(100vh - 40px)' : '100%',
            borderRadius: window.innerWidth > 767 ? '24px' : '0',
            overflow: 'hidden',
            
          }}>
            <RendersPanel
              authUser={authUser}
              onClose={closeRendersPanel}
              userRenders={userRenders}
              setUserRenders={setUserRenders}
              imagesRemaining={imagesRemaining}
              videosRemaining={videosRemaining}
              updateRemainingCounts={updateRemainingCounts}
              isPWA={isPWA}
            />
          </div>
        </div>
      )}

           {/* ASSET LIBRARY PANEL */}
      {assetLibraryActive && (
        <AssetLibraryPanel
        authUser={authUser}
        onClose={() => { setAssetLibraryActive(false); setActiveNavItem(null); setCatalogActive(false); setRendersActive(false); setShowDesktopSettingsView(false); setShowDesktopProfileView(false); }}
        userAssets={userAssets}
        setUserAssets={setUserAssets}
        onAddToProject={(asset) => {
          const pendingAsset = { id: asset.id, name: asset.name, type: asset.type, downloadURL: asset.downloadURL, fileExtension: asset.fileExtension, thumbnailURL: asset.thumbnailURL };
          sessionStorage.setItem('pendingAsset', JSON.stringify(pendingAsset));
          navigate('/editor', { state: { assetToImport: pendingAsset, openAssetLibrary: true } });
        }}
        isPWA={isPWA}
        imagesRemaining={imagesRemaining}
        videosRemaining={videosRemaining}
        updateRemainingCounts={updateRemainingCounts}
        refetchAssets={fetchUserAssets}
      />
      )}

      {/* DEV STUDIO - MARKETING IMAGE GENERATION */}
      {showDevStudio && (
        <DevStudio
          authUser={authUser}
          onClose={() => { 
            setShowDevStudio(false);
            setActiveNavItem(null);
          }}
          imagesRemaining={imagesRemaining}
          videosRemaining={videosRemaining}
          updateRemainingCounts={updateRemainingCounts}
          isPWA={isPWA}
        />
      )}
    </>
  );
};

export default Dashboard;